// IntaStream Portal - TypeScript Migration in Progress
// Converting from JavaScript to TypeScript with full type safety

// Extend Window interface for custom properties
declare global {
  interface Window {
    mappingTimeout?: NodeJS.Timeout;
    notesTimeout?: NodeJS.Timeout;
  }
}

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  createContext,
  useContext,
  useMemo
} from 'react';

import {
  Modal,
  Select,
  Button,
  TextInput,
  Textarea,
  Group,
  Stack,
  Grid,
  MantineProvider,
  ActionIcon,
  Paper,
  Text,
  Title,
  Checkbox,
  Anchor,
  Container,
  useMantineTheme,
  Box,
  Badge,
  Progress,
  ScrollArea,
  Avatar,
  Indicator,
  Tooltip,
  Alert,
  Loader,
  UnstyledButton,
  Card,
  Timeline
} from '@mantine/core';

import { DatePicker, DateInput } from '@mantine/dates';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Notifications } from '@mantine/notifications';

import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  QuestionMarkCircleIcon,
  SunIcon,
  MoonIcon,
  TrashIcon,
  HomeIcon,
  ArrowRightIcon,
  AcademicCapIcon,
  UserPlusIcon,
  IdentificationIcon,
  DocumentCheckIcon,
  XMarkIcon,
  BookmarkIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  ArrowLeftEndOnRectangleIcon,
  UserGroupIcon,
  PhoneIcon,
  ComputerDesktopIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

import {
  IconSend,
  IconPlus,
  IconMessage,
  IconUser,
  IconAlertCircle,
  IconX,
  IconTrash
} from '@tabler/icons-react';

// Import TypeScript API service and type definitions
import { apiService } from './services/api';

// TypeScript utility functions will be imported as needed during incremental conversion

// PDF generation import
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

// Define constants locally (these were in the original App.js)
const STORAGE_KEYS = {
  AUTH: 'intastream_auth',
  CURRENT_STEP: 'intastream_step',
  THEME: 'intastream_theme',
  NURSE_AUTH: 'intastream_nurse_auth',
  NURSE_PROFILE: 'intastream_nurse_profile',
  REGISTRATION_SESSION: 'intastream_registration_session',
  REGISTRATION_AUTH: 'intastream_registration_auth',
  SELECTED_PROVIDER: 'intastream_selected_provider',
  TAB_CURRENT_STEP: 'intastream_tab_step',
  TAB_AUTH_TYPE: 'intastream_tab_auth_type'
} as const;

const APPOINTMENT_STATUS = {
  SEARCHING: 12,
  CONFIRMED: 13,
  HOLIDAY: 14,
  INVOICE_DOCUMENTS_ATTACHED: 15,
  TIMESHEET_VALIDATED: 16,
  INVOICE_PAID: 17,
  INVOICE_CANCELLED: 18
} as const;
// Helper function to get the correct API base URL for environment
const getApiBaseUrl = (): string => {
  return process.env.NODE_ENV === 'production'
    ? 'https://intastream.com/api'
    : 'http://localhost:8080/api';
};

// Encryption utilities for sensitive localStorage data
const ENCRYPTION_KEY = process.env.REACT_APP_STORAGE_ENCRYPTION_KEY || 'intastream-portal-key-2025';

const encryptData = (data: any): string => {
  const enableEncryption = process.env.REACT_APP_ENCRYPT_STORAGE === 'true';
  if (!enableEncryption) {
    return JSON.stringify(data);
  }

  try {
    const jsonString = JSON.stringify(data);
    // Simple XOR encryption for client-side storage
    let encrypted = '';
    for (let i = 0; i < jsonString.length; i++) {
      const keyChar = ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
      const dataChar = jsonString.charCodeAt(i);
      encrypted += String.fromCharCode(dataChar ^ keyChar);
    }
    return btoa(encrypted); // Base64 encode
  } catch (error) {
    console.warn('Encryption failed, storing as plain text:', error);
    return JSON.stringify(data);
  }
};

const decryptData = (encryptedData: string): any => {
  const enableEncryption = process.env.REACT_APP_ENCRYPT_STORAGE === 'true';
  if (!enableEncryption) {
    return JSON.parse(encryptedData);
  }

  try {
    const encrypted = atob(encryptedData); // Base64 decode
    let decrypted = '';
    for (let i = 0; i < encrypted.length; i++) {
      const keyChar = ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
      const encryptedChar = encrypted.charCodeAt(i);
      decrypted += String.fromCharCode(encryptedChar ^ keyChar);
    }
    return JSON.parse(decrypted);
  } catch (error) {
    console.warn('Decryption failed, attempting plain text parse:', error);
    try {
      return JSON.parse(encryptedData);
    } catch (parseError) {
      console.error('Failed to parse data:', parseError);
      return null;
    }
  }
};

// Check if a storage key should be encrypted (sensitive data)
const shouldEncrypt = (key: string): boolean => {
  const sensitiveKeys = [
    STORAGE_KEYS.AUTH,
    STORAGE_KEYS.NURSE_AUTH,
    STORAGE_KEYS.NURSE_PROFILE,
    STORAGE_KEYS.REGISTRATION_SESSION,
    STORAGE_KEYS.REGISTRATION_AUTH
  ] as const;
  return (sensitiveKeys as readonly string[]).includes(key);
};

// Utility functions with TypeScript typing and encryption
const loadFromStorage = (key: string, defaultValue: any, useSessionStorage: boolean = false): any => {
  try {
    const storage = useSessionStorage ? sessionStorage : localStorage;
    const item = storage.getItem(key);
    if (!item) return defaultValue;

    // Decrypt sensitive data
    if (shouldEncrypt(key)) {
      const parsed = decryptData(item);
      return parsed !== null ? parsed : defaultValue;
    } else {
      const parsed = JSON.parse(item);
      return parsed;
    }
  } catch (error) {
    console.error(`Error loading ${key} from storage:`, error);
    try {
      const storage = useSessionStorage ? sessionStorage : localStorage;
      storage.removeItem(key);
    } catch (e) {
      console.error('Failed to clear corrupted storage:', e);
    }
    return defaultValue;
  }
};

const saveToStorage = (key: string, value: unknown, useSessionStorage: boolean = false): void => {
  try {
    const storage = useSessionStorage ? sessionStorage : localStorage;

    // Encrypt sensitive data
    if (shouldEncrypt(key)) {
      const encryptedData = encryptData(value);
      storage.setItem(key, encryptedData);
    } else {
      storage.setItem(key, JSON.stringify(value));
    }
  } catch (error) {
    console.error('Error saving to storage:', error);
  }
};

// Storage utility functions now imported from ./types with TypeScript support

// Note: TrainingSystem component functionality integrated into main nurse dashboard

// Optimized CSV Mapping Interface Component
const OptimizedCSVMappingInterface = ({ onClose, adminEmail }: { onClose: () => void; adminEmail: string }) => {
  const [mappings, setMappings] = useState({});
  const [notes, setNotes] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

  // Database columns for mapping
  const dbColumns = useMemo(() => [
    'ProfessionalId', 'UID', 'Sex', 'DoB', 'Forename', 'Surname', 'NationalInsuranceNumber',
    'EmailAddress', 'MobileNumber', 'TelephoneNumber', 'AddressLine1', 'AddressLine2',
    'AddressLine3', 'AddressLine4', 'AddressLine5', 'Postcode', 'Title', 'PassportNumber',
    'DBSNumber', 'DBSUpdateReference', 'DBSRegistrationDate', 'DBSExpiryDate',
    'NMCPINHPCNumber', 'NMCPINHPCRegistrationDate', 'NMCPINHPCExpiryDate',
    'NextOfKinFullName', 'NextOfKinContactNumber', 'NextOfKinRelationship', 'NextOfKinAddress',
    'GPName', 'GPAddress', 'BankAccountHolderName', 'BankAccountNumber', 'BankSortCode',
    'VISAExpiryDate', 'IsEUCitizen', 'HasBritishEUPassport', 'WorkPreference'
  ], []);

  // CSV columns from Google Forms
  const csvColumns = useMemo(() => [
    'Timestamp', 'Title', 'Date of Birth', 'Forename(s)', 'Surname', 'National Insurance Number',
    'NMC/PIN/HPC Number', 'NMC/PIN/HPC Number Registration Date', 'NMC/PIN/HPC Number Expiry Date',
    'DBS Number', 'DBS Update Reference Number', 'DBS Registration Date', 'DBS Expiry Date',
    'Passport Number', 'Passport Style Photograph For Photo ID', 'Address Line 1', 'Address Line 2',
    'Address Line 3', 'Address Line 4', 'Address Line 5', 'Postcode', 'Email Address',
    'Mobile Phone Number', 'Land Line Number', 'Next of Kin (Full Name)', 'Next of Kin (Contact Telephone #)',
    'Next of Kin (Relationship Status)', 'Next of Kin (Address Inc. Country of Residence)',
    'Anaesthetics (ODP)', 'HCA (Health Care Assistant)', 'HDU/ITU', 'Paediatrics', 'Recovery',
    'Sterilisation Technician (SSD)', 'Ward (RN)', 'Scrub (General)', 'Scrub (Cardiac)', 'Scrub (ENT)',
    'Scrub (Gynaecology)', 'Scrub (Laparoscopic)', 'Scrub (Microvascular)', 'Scrub (Neuro)',
    'Scrub (Optha)', 'Scrub (Ortho)', 'Scrub (Plastics)', 'Scrub (Spine)', 'Scrub (Thoracic)',
    'Scrub (Urology)', 'Scrub (SFA/ASP)', 'Emergency Room Nurse (RN)', 'Upload your CV',
    'Full Name (As Shown On Bank Account)', 'Account Number', 'Sort Code',
    'VISA Expiry Date (Required if you have a VISA number)', 'Are you an EU citizen?',
    'Do you have a British or EU Passport?', 'What kind of work you are looking for?',
    'GP (General Practitioner) - Name & Address'
  ], []);

  // Memoized filtered database columns
  const filteredDbColumns = useMemo(() => {
    if (!searchTerm) return dbColumns;
    return dbColumns.filter(col =>
      col.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, dbColumns]);

  // Load existing mappings only once
  const loadExistingMappings = useCallback(async () => {
    if (hasLoadedInitial) return;

    try {
      setLoading(true);
      const existingMappings = await apiService.getFieldMappings();
      const mappingsObj = {};
      const notesObj = {};

      existingMappings.forEach(mapping => {
        mappingsObj[mapping.csvColumn] = mapping.databaseColumn;
        if (mapping.notes) {
          notesObj[mapping.csvColumn] = mapping.notes;
        }
      });

      setMappings(mappingsObj);
      setNotes(notesObj);
      setHasLoadedInitial(true);
      showNotification(`Loaded ${existingMappings.length} existing mappings`, 'success');
    } catch (error) {
      showNotification('No existing mappings found', 'warning');
    } finally {
      setLoading(false);
    }
  }, [hasLoadedInitial]);

  // Load mappings on mount
  useEffect(() => {
    loadExistingMappings();
  }, [loadExistingMappings]);

  // Handle mapping changes (local state only)
  const handleMappingChange = useCallback((csvColumn, dbColumn) => {
    setMappings(prev => ({
      ...prev,
      [csvColumn]: dbColumn
    }));
  }, []);

  // Handle notes changes (local state only)
  const handleNotesChange = useCallback((csvColumn, note) => {
    setNotes(prev => ({
      ...prev,
      [csvColumn]: note
    }));
  }, []);

  // Save all mappings at once
  const saveAllMappings = useCallback(async () => {
    try {
      setLoading(true);
      const savePromises = Object.entries(mappings)
        .filter(([_, dbColumn]) => dbColumn)
        .map(([csvColumn, dbColumn]) =>
          apiService.saveFieldMapping(csvColumn as string, dbColumn as string, notes[csvColumn] || '', adminEmail)
        );

      await Promise.all(savePromises);
      showNotification(`Successfully saved ${savePromises.length} mappings!`, 'success');
    } catch (error) {
      showNotification('Error saving mappings: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [mappings, notes, adminEmail]);

  const mappingCount = Object.keys(mappings).filter(key => mappings[key]).length;

  return (
    <Stack gap="lg">
      <Paper shadow="lg" p="xl" radius="md">
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <div>
              <Title order={2}>CSV to Database Field Mapping</Title>
              <Text c="dimmed">Map Google Forms CSV fields to ProfessionalSet database columns</Text>
            </div>
            <Group gap="md">
              <Button variant="light" onClick={onClose}>
                Back to Home
              </Button>
              <Button
                onClick={saveAllMappings}
                leftSection={<CheckCircleIcon style={{ width: '16px', height: '16px' }} />}
                variant="filled"
                color="green"
                loading={loading}
                disabled={mappingCount === 0}
              >
                Save All Mappings ({mappingCount})
              </Button>
            </Group>
          </Group>

          <Alert>
            <Text size="sm">
              <strong>Performance Optimized:</strong> All changes are cached locally.
              Click "Save All Mappings" to persist to database.
            </Text>
          </Alert>

          <Paper p="md" withBorder>
            <Group gap="md" align="center">
              <Text fw={600}>Search Database Columns:</Text>
              <TextInput
                placeholder="Filter database columns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ flex: 1 }}
              />
              <Text size="sm" c="dimmed">
                {filteredDbColumns.length} of {dbColumns.length} columns shown
              </Text>
            </Group>
          </Paper>
        </Stack>
      </Paper>

      <Paper shadow="md" p="lg" radius="md">
        <Title order={3} mb="md">Field Mappings ({csvColumns.length} CSV columns)</Title>
        {loading ? (
          <Group justify="center" p="xl">
            <Loader />
            <Text>Loading mappings...</Text>
          </Group>
        ) : (
          <Grid>
            {csvColumns.map((csvColumn) => (
              <Grid.Col span={{ base: 12, md: 6, lg: 4 }} key={csvColumn}>
                <Paper p="md" withBorder style={{
                  borderColor: mappings[csvColumn] ? '#51cf66' : '#ced4da',
                  borderWidth: mappings[csvColumn] ? '2px' : '1px'
                }}>
                  <Stack gap="sm">
                    <Text fw={600} size="sm" c={mappings[csvColumn] ? 'green' : 'blue'}>
                      {csvColumn}
                      {mappings[csvColumn] && ' ✓'}
                    </Text>
                    <Select
                      placeholder="Select database column"
                      data={[
                        { value: '', label: '-- No mapping --' },
                        ...filteredDbColumns.map(col => ({ value: col, label: col }))
                      ]}
                      value={mappings[csvColumn] || ''}
                      onChange={(value) => handleMappingChange(csvColumn, value)}
                      searchable
                      clearable
                    />
                    <Textarea
                      placeholder="Notes (transformation rules, etc.)"
                      value={notes[csvColumn] || ''}
                      onChange={(e) => handleNotesChange(csvColumn, e.target.value)}
                      autosize
                      minRows={2}
                      size="xs"
                    />
                  </Stack>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Paper>
    </Stack>
  );
};

// Notification helper functions
export const showNotification = (message, type = 'default', title = null) => {
  const config = {
    message,
    title,
    autoClose: 4000,
    withCloseButton: true,
  };

  switch (type) {
    case 'success':
      notifications.show({
        ...config,
        color: 'green',
        icon: <CheckCircleIcon style={{ width: '18px', height: '18px' }} />,
      });
      break;
    case 'error':
      notifications.show({
        ...config,
        color: 'red',
        icon: <XMarkIcon style={{ width: '18px', height: '18px' }} />,
      });
      break;
    case 'warning':
      notifications.show({
        ...config,
        color: 'yellow',
        icon: <QuestionMarkCircleIcon style={{ width: '18px', height: '18px' }} />,
      });
      break;
    default:
      notifications.show(config);
  }
};



// Server-Sent Events service for real-time messaging
class SSEService {
  eventSource: EventSource | null;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  reconnectInterval: number;
  listeners: Map<string, Function[]>;
  isConnected: boolean;
  userID: string | null;
  token: string | null;
  reconnectTimer: NodeJS.Timeout | null;
  connectionAttemptTimer: NodeJS.Timeout | null;
  connectionMonitor: NodeJS.Timeout | null;
  heartbeatTimer: NodeJS.Timeout | null;
  isManuallyDisconnected: boolean;

  constructor() {
    this.eventSource = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10; // Increased for better stability
    this.reconnectInterval = 2000; // Reduced for faster reconnection
    this.listeners = new Map();
    this.isConnected = false;
    this.reconnectTimer = null;
    this.connectionAttemptTimer = null;
    this.connectionMonitor = null;
    this.heartbeatTimer = null;
    this.isManuallyDisconnected = false;
  }

  connect() {
    console.log('SSE connect requested (using httpOnly cookies for auth):', { currentState: this.eventSource?.readyState, isConnected: this.isConnected });

    // Clear any pending timers
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.connectionAttemptTimer) {
      clearTimeout(this.connectionAttemptTimer);
      this.connectionAttemptTimer = null;
    }

    // Prevent multiple connections
    if (this.eventSource && (this.eventSource.readyState === EventSource.CONNECTING || this.eventSource.readyState === EventSource.OPEN)) {
      console.log('SSE already connected or connecting, skipping...', { readyState: this.eventSource.readyState });
      return;
    }

    // Close existing connection if any
    this.cleanup();

    this.isManuallyDisconnected = false;

    // Use environment-aware URL - authentication handled via httpOnly cookies  
    const sseUrl = `${getApiBaseUrl()}/messaging/events`;

    console.log('SSE connecting to:', sseUrl, '(using httpOnly cookies for auth)');

    try {
      // Create EventSource with proper withCredentials for cookie support
      // For localhost development, we need to ensure cookies are sent
      this.eventSource = new EventSource(sseUrl, {
        withCredentials: true  // This ensures cookies are sent with the request
      });

      // Set up connection timeout
      this.connectionAttemptTimer = setTimeout(() => {
        if (this.eventSource && this.eventSource.readyState !== EventSource.OPEN) {
          console.warn('SSE connection timeout - forcing reset and retry');
          this.forceReset();
          if (!this.isManuallyDisconnected && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect(2000);
          }
        }
      }, 10000); // 10 second timeout

      this.eventSource.onopen = () => {
        // Clear connection timeout on successful open
        if (this.connectionAttemptTimer) {
          clearTimeout(this.connectionAttemptTimer);
          this.connectionAttemptTimer = null;
        }
        console.log('SSE connected successfully', { userID: this.userID, reconnectAttempts: this.reconnectAttempts });
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.emit('connected', null);

        // Set up connection health monitoring
        this.startConnectionMonitoring();

        // Set up heartbeat to keep connection alive
        this.startHeartbeat();
      };

      this.eventSource.onmessage = (event) => {
        console.log('SSE message received:', event.data);
        try {
          const data = JSON.parse(event.data);
          this.emit('message', data);
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };

      this.eventSource.onerror = (error) => {
        // Only log errors if not manually disconnected and not during page unload
        if (!this.isManuallyDisconnected && !document.hidden) {
          console.error('SSE error occurred:', error, {
            readyState: this.eventSource?.readyState,
            reconnectAttempts: this.reconnectAttempts,
            isManuallyDisconnected: this.isManuallyDisconnected,
            userID: this.userID
          });
        }

        // Force complete cleanup on any error
        this.forceReset();

        // Only attempt reconnection if not manually disconnected and page is visible
        if (!this.isManuallyDisconnected && !document.hidden && this.reconnectAttempts < this.maxReconnectAttempts) {
          console.log('SSE error - forcing complete reconnection');
          this.scheduleReconnect(1000); // Give server time to clean up
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('SSE max reconnection attempts reached, giving up');
        }
      };

      // Handle specific event types
      this.eventSource.addEventListener('connected', (event) => {
        console.log('SSE connected event:', event.data);
        const data = JSON.parse(event.data);

        // Emit connected event
        this.emit('connected', data);
      });

      this.eventSource.addEventListener('new_message', (event) => {
        console.log('SSE new_message event:', event.data);
        try {
          const message = JSON.parse(event.data);
          console.log('SSE new_message parsed:', message);
          this.emit('new_message', message);
        } catch (error) {
          console.error('Error parsing SSE new_message:', error);
        }
      });

      this.eventSource.addEventListener('message_deleted', (event) => {
        console.log('🗑️ SSE message_deleted event received:', event.data);
        try {
          const deletionData = JSON.parse(event.data);
          console.log('🗑️ SSE message_deleted parsed:', deletionData);
          this.emit('message_deleted', deletionData);
        } catch (error) {
          console.error('❌ Error parsing SSE message_deleted:', error);
        }
      });

      this.eventSource.addEventListener('conversation_deleted', (event) => {
        console.log('🗑️ SSE conversation_deleted event received:', event.data);
        try {
          const deletionData = JSON.parse(event.data);
          console.log('🗑️ SSE conversation_deleted parsed:', deletionData);
          console.log('🗑️ Emitting conversation_deleted event to React context');
          this.emit('conversation_deleted', deletionData);
        } catch (error) {
          console.error('❌ Error parsing SSE conversation_deleted:', error);
        }
      });

      this.eventSource.addEventListener('conversation_created', (event) => {
        console.log('✨ SSE conversation_created event received:', event.data);
        try {
          const creationData = JSON.parse(event.data);
          console.log('✨ SSE conversation_created parsed:', creationData);
          console.log('✨ Emitting conversation_created event to React context');
          this.emit('conversation_created', creationData);
        } catch (error) {
          console.error('❌ Error parsing SSE conversation_created:', error);
        }
      });

      this.eventSource.addEventListener('heartbeat', (event) => {
        console.log('SSE heartbeat:', event.data);
        // Just keep the connection alive
      });

      // Appointment event listeners
      this.eventSource.addEventListener('appointment_created', (event) => {
        console.log('🆕 SSE appointment_created event received:', event.data);
        try {
          const appointmentData = JSON.parse(event.data);
          console.log('🆕 SSE appointment_created parsed:', appointmentData);
          this.emit('appointment_created', appointmentData);
        } catch (error) {
          console.error('❌ Error parsing SSE appointment_created:', error);
        }
      });

      this.eventSource.addEventListener('appointment_updated', (event) => {
        console.log('✏️ SSE appointment_updated event received:', event.data);
        try {
          const appointmentData = JSON.parse(event.data);
          console.log('✏️ SSE appointment_updated parsed:', appointmentData);
          this.emit('appointment_updated', appointmentData);
        } catch (error) {
          console.error('❌ Error parsing SSE appointment_updated:', error);
        }
      });

      this.eventSource.addEventListener('appointment_cancelled', (event) => {
        console.log('🗑️ SSE appointment_cancelled event received:', event.data);
        try {
          const appointmentData = JSON.parse(event.data);
          console.log('🗑️ SSE appointment_cancelled parsed:', appointmentData);
          this.emit('appointment_cancelled', appointmentData);
        } catch (error) {
          console.error('❌ Error parsing SSE appointment_cancelled:', error);
        }
      });

    } catch (error) {
      console.error('Error creating SSE connection:', error);
      this.emit('error', error);
    }
  }

  disconnect() {
    console.log('SSE manual disconnect requested');
    this.isManuallyDisconnected = true;
    this.cleanup();
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
  }

  cleanup() {
    console.log('SSE cleanup initiated');

    // Stop connection monitoring and heartbeat
    this.stopConnectionMonitoring();
    this.stopHeartbeat();

    // Clear timers
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.connectionAttemptTimer) {
      clearTimeout(this.connectionAttemptTimer);
      this.connectionAttemptTimer = null;
    }

    // Close EventSource
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.isConnected = false;
  }

  scheduleReconnect(customDelay = null) {
    if (this.isManuallyDisconnected) {
      console.log('SSE reconnect cancelled - manually disconnected');
      return;
    }

    this.reconnectAttempts++;
    const backoffDelay = customDelay || Math.min(this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1), 30000);
    console.log(`SSE reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${backoffDelay}ms`);

    this.reconnectTimer = setTimeout(() => {
      if (this.userID && this.token && !this.isManuallyDisconnected) {
        console.log('SSE executing reconnect attempt', this.reconnectAttempts);

        // Force complete reset before reconnecting to clear any stuck state
        this.forceReset();

        // Small delay to ensure cleanup is complete
        setTimeout(() => {
          if (!this.isManuallyDisconnected) {
            this.connect();
          }
        }, 100);
      }
    }, backoffDelay);
  }

  startConnectionMonitoring() {
    // Clear any existing monitoring
    this.stopConnectionMonitoring();

    // Monitor connection health every 5 seconds
    this.connectionMonitor = setInterval(() => {
      if (this.eventSource && this.eventSource.readyState === EventSource.CLOSED) {
        console.warn('SSE connection detected as closed during monitoring, reconnecting...');
        this.isConnected = false;
        if (!this.isManuallyDisconnected && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect(100); // Fast reconnect on detected close
        }
      }
    }, 5000);
  }

  stopConnectionMonitoring() {
    if (this.connectionMonitor) {
      clearInterval(this.connectionMonitor);
      this.connectionMonitor = null;
    }
  }

  startHeartbeat() {
    this.stopHeartbeat();

    // Send a heartbeat every 30 seconds to keep connection alive
    this.heartbeatTimer = setInterval(() => {
      if (this.eventSource && this.eventSource.readyState === EventSource.OPEN) {
        console.log('SSE heartbeat - connection alive');
        // Just log that we're alive - the connection itself acts as heartbeat
      } else if (this.eventSource) {
        console.warn('SSE heartbeat detected closed connection, readyState:', this.eventSource.readyState);
        if (!this.isManuallyDisconnected) {
          this.forceConnectionCheck();
        }
      }
    }, 30000);
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // Event listener management
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) {
      return;
    }
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  emit(event, data) {
    console.log('SSE emit called:', event, data);
    if (!this.listeners.has(event)) {
      console.log('No listeners found for SSE event:', event);
      return;
    }
    const callbacks = this.listeners.get(event);
    console.log(`Found ${callbacks.length} listeners for SSE event:`, event);
    callbacks.forEach((callback, index) => {
      try {
        console.log(`Calling SSE callback ${index} for event:`, event);
        callback(data);
        console.log(`SSE callback ${index} completed for event:`, event);
      } catch (error) {
        console.error(`Error in SSE ${event} callback ${index}:`, error);
      }
    });
  }

  // Test SSE connection
  testConnection() {
    console.log('SSE connection test:', {
      isConnected: this.isConnected,
      hasEventSource: !!this.eventSource,
      readyState: this.eventSource?.readyState,
      url: this.eventSource?.url,
      listenerCount: this.listeners.size,
      listeners: Array.from(this.listeners.keys())
    });
  }

  // Get connection status (legacy method)
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
    };
  }

  // Get detailed status for monitoring
  getStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      hasEventSource: !!this.eventSource,
      readyState: this.eventSource?.readyState,
      url: this.eventSource?.url,
      userID: this.userID,
      isManuallyDisconnected: this.isManuallyDisconnected
    };
  }

  // Force connection check and reconnect if needed
  forceConnectionCheck() {
    console.log('SSE forced connection check initiated');
    const status = this.getStatus();

    // If we think we're connected but EventSource is closed, force reconnect
    if (this.isConnected && this.eventSource && this.eventSource.readyState === EventSource.CLOSED) {
      console.warn('SSE connection state mismatch detected, forcing reconnect');
      this.forceReset();
      if (!this.isManuallyDisconnected) {
        this.connect();
      }
      return true; // Reconnection triggered
    }

    // If no connection exists but we should be connected
    if (!this.eventSource && !this.isManuallyDisconnected) {
      console.log('SSE no connection exists, establishing new connection');
      this.connect();
      return true; // Connection triggered
    }

    return false; // No action needed
  }

  // Force complete reset of connection state
  forceReset() {
    console.log('SSE force reset initiated - complete cleanup');

    // Stop all monitoring and timers
    this.stopConnectionMonitoring();
    this.stopHeartbeat();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.connectionAttemptTimer) {
      clearTimeout(this.connectionAttemptTimer);
      this.connectionAttemptTimer = null;
    }

    // Force close EventSource and clear reference
    if (this.eventSource) {
      try {
        this.eventSource.close();
      } catch (e) {
        console.warn('Error closing EventSource:', e);
      }
      this.eventSource = null;
    }

    // Reset connection state
    this.isConnected = false;

    // Emit disconnection event
    this.emit('disconnected', null);
  }
}

const sseService = new SSEService();

// Messaging Context
const MessagingContext = createContext(null);

const useMessaging = () => {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
};

const MessagingProvider = ({ children }) => {
  // State management
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState({});
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});

  // Current user info
  const [currentUser, setCurrentUser] = useState(null);

  // Initialize messaging for a user
  const initializeMessaging = useCallback(async (userInfo) => {
    try {
      setCurrentUser(userInfo);

      // Verify authentication before connecting SSE
      console.log('Verifying authentication before SSE connection...');
      const authStatus = await apiService.checkAuthenticationStatus();

      if (authStatus.authenticated) {
        console.log('Authentication verified, connecting SSE...');
        // Connect SSE - authentication handled via httpOnly cookies
        sseService.connect();

        // Test SSE connection after a brief delay
        setTimeout(() => {
          console.log('Testing SSE connection after initialization...');
          sseService.testConnection();
        }, 1000);

        // Load initial conversations with user info to ensure unread counts work
        await loadConversations(userInfo.id, userInfo);

        // Load online users
        await loadOnlineUsers();
      } else {
        console.warn('Authentication check failed, SSE will not connect');
        // Don't attempt to load conversations or connect SSE without authentication
        return;
      }

    } catch (error) {
      console.error('Error initializing messaging:', error);
      // Don't attempt messaging operations if there's an authentication error
    }
  }, []);

  // Load conversations
  const loadConversations = useCallback(async (userID, userInfo = null) => {
    if (!userID) return;

    // Check authentication before attempting to load conversations
    try {
      const authStatus = await apiService.checkAuthenticationStatus();
      if (!authStatus.authenticated) {
        console.log('User not authenticated, skipping conversation load');
        return;
      }
    } catch (error) {
      console.warn('Authentication check failed, skipping conversation load:', error);
      return;
    }

    setIsLoading(true);
    try {
      const conversationsData = await apiService.getConversations(userID);
      setConversations(conversationsData || []); // Ensure it's never null

      // Calculate unread counts - use userInfo if provided, otherwise fall back to currentUser
      const user = userInfo || currentUser;
      const counts = {};
      if (conversationsData && user) {
        conversationsData.forEach(conv => {
          if (conv && conv.ConversationId) {
            if (user.userType === 'ADMIN') {
              counts[conv.ConversationId] = conv.UnreadCount || 0;
            } else {
              counts[conv.ConversationId] = conv.UnreadCount || 0;
            }
          }
        });
      }
      setUnreadCounts(counts);

    } catch (error) {
      console.error('Error loading conversations:', error);
      setConversations([]); // Set empty array on error
      notifications.show({
        title: 'Loading Error',
        message: 'Failed to load conversations',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // Load messages for a conversation
  const loadMessages = useCallback(async (conversationId, pageSize = 50, pageNumber = 1) => {
    try {
      const messagesData = await apiService.getMessages(conversationId, pageSize, pageNumber);

      setMessages(prev => ({
        ...prev,
        [conversationId]: (messagesData || []).reverse(), // Reverse to show newest at bottom
      }));

      return messagesData || [];
    } catch (error) {
      console.error('Error loading messages:', error);
      notifications.show({
        title: 'Loading Error',
        message: 'Failed to load messages',
        color: 'red',
      });
      return [];
    }
  }, []);

  // Load online users
  const loadOnlineUsers = useCallback(async () => {
    // Check authentication before attempting to load online users
    try {
      const authStatus = await apiService.checkAuthenticationStatus();
      if (!authStatus.authenticated) {
        console.log('User not authenticated, skipping online users load');
        return;
      }
    } catch (error) {
      console.warn('Authentication check failed, skipping online users load:', error);
      return;
    }

    try {
      const users = await apiService.getOnlineUsers();
      setOnlineUsers(users);
    } catch (error) {
      console.error('Error loading online users:', error);
    }
  }, []);

  // Create or get conversation
  const createConversation = useCallback(async (hospitalProviderId, subject = '') => {
    if (!currentUser) {
      return null;
    }

    try {
      const conversation = await apiService.createConversation(currentUser.id, hospitalProviderId, subject);

      // Add to conversations if not already present
      setConversations(prev => {
        const exists = prev.find(c => c && c.ConversationId && c.ConversationId === conversation.ConversationId);
        if (exists) return prev;
        return [conversation, ...prev];
      });

      return conversation;
    } catch (error) {
      console.error('Error creating conversation:', error);
      notifications.show({
        title: 'Creation Error',
        message: `Failed to create conversation: ${error.message}`,
        color: 'red',
      });
      return null;
    }
  }, [currentUser]);

  // Send message
  const sendMessage = useCallback(async (conversationId, content, messageType = 'text') => {
    if (!currentUser || !content.trim()) {
      return;
    }

    try {
      // With SSE, we only send via REST API (SSE is receive-only)
      const newMessage = await apiService.sendMessage(currentUser.id, conversationId, content.trim(), messageType);

      // Don't add to local state - let SSE handle the real-time delivery

    } catch (error) {
      console.error('Error sending message:', error);
      notifications.show({
        title: 'Send Error',
        message: 'Failed to send message',
        color: 'red',
      });
    }
  }, [currentUser]);

  // Set typing status (REST API only with SSE)
  const setTypingStatus = useCallback((conversationId, isTyping) => {
    if (!currentUser) return;

    // Use REST API for typing status
    apiService.setTypingStatus(currentUser.id, conversationId, isTyping).catch(console.error);
  }, [currentUser]);

  // Mark messages as read (REST API only with SSE)
  const markAsRead = useCallback(async (conversationId) => {
    if (!currentUser) return;

    try {
      await apiService.markAsRead(currentUser.id, conversationId);

      // Update local unread count
      setUnreadCounts(prev => ({
        ...prev,
        [conversationId]: 0,
      }));

    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [currentUser]);

  // Select active conversation
  const selectConversation = useCallback(async (conversation) => {
    setActiveConversation(conversation);

    // Only proceed if conversation exists and has ID
    if (!conversation || !conversation.conversationId) {
      console.log('selectConversation: No valid conversation provided');
      return;
    }

    // Load messages if not already loaded
    if (!messages[conversation.conversationId]) {
      await loadMessages(conversation.conversationId);
    }

    // Mark as read
    await markAsRead(conversation.conversationId);
  }, [messages, loadMessages, markAsRead]);

  // WebSocket event handlers
  const handleConnected = useCallback(() => {
    setIsConnected(true);
    // Connection notifications removed for cleaner UX
  }, []);

  const handleDisconnected = useCallback(() => {
    setIsConnected(false);
    // Disconnection notifications removed for cleaner UX
  }, []);

  const handleNewMessage = useCallback((message) => {
    console.log('MessagingContext handleNewMessage called:', message);

    // Add message to the conversation
    setMessages(prev => {
      const newState = {
        ...prev,
        [message.conversationId]: [
          ...(prev[message.conversationId] || []),
          message,
        ],
      };
      return newState;
    });

    // Update conversation last message info
    setConversations(prev => prev.map(conv => {
      if (conv && conv.conversationId && conv.conversationId === message.conversationId) {
        return {
          ...conv,
          lastMessageAt: message.sentAt,
          lastMessageContent: message.content,
          lastMessageSenderId: message.senderId,
          lastMessageSenderType: message.senderType,
          lastMessageSenderName: message.senderName,
        };
      }
      return conv;
    }));

    // Update unread count if not the current user's message and not active conversation
    if (message.senderId !== currentUser?.id) {
      if (!activeConversation || activeConversation.conversationId !== message.conversationId) {
        setUnreadCounts(prev => ({
          ...prev,
          [message.conversationId]: (prev[message.conversationId] || 0) + 1,
        }));
      }

      // Show notification for new messages
      if (!activeConversation || activeConversation.conversationId !== message.conversationId) {
        notifications.show({
          title: `New message from ${message.senderName}`,
          message: message.content.substring(0, 100),
          color: 'blue',
          autoClose: 5000,
        });
      }
    }
  }, [currentUser, activeConversation]);

  const handleTypingStatus = useCallback((typingData) => {
    setTypingUsers(prev => {
      const conversationTyping = { ...(prev[typingData.conversationId] || {}) };

      if (typingData.isTyping) {
        conversationTyping[typingData.userId] = {
          userName: typingData.userName,
          startTime: Date.now(),
        };
      } else {
        delete conversationTyping[typingData.userId];
      }

      return {
        ...prev,
        [typingData.conversationId]: conversationTyping,
      };
    });
  }, []);

  const handleUserPresence = useCallback((presenceData) => {
    setOnlineUsers(prev => {
      if (presenceData.isOnline) {
        // Add or update user
        const existingIndex = prev.findIndex(u => u.userId === presenceData.userId);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = presenceData;
          return updated;
        } else {
          return [...prev, presenceData];
        }
      } else {
        // Remove user
        return prev.filter(u => u.userId !== presenceData.userId);
      }
    });
  }, []);

  const handleMessagesRead = useCallback((readData) => {
    // Handle read receipts if needed
    console.log('Messages read:', readData);
  }, []);

  const handleConversationDeleted = useCallback((deletionData) => {
    console.log('🗑️ CONVERSATION DELETED EVENT RECEIVED:', deletionData);

    // Validate deletion data
    if (!deletionData || !deletionData.conversationId) {
      console.error('🗑️ Invalid deletion data received:', deletionData);
      return;
    }

    const deletedConversationId = deletionData.conversationId.toString();

    // Use setTimeout to defer state update to avoid race conditions with user interactions
    setTimeout(() => {
      // Remove conversation from conversations list with robust filtering
      setConversations(prev => {
        if (!Array.isArray(prev)) {
          console.warn('🗑️ Conversations is not an array:', prev);
          return [];
        }

        console.log('🗑️ Current conversations before deletion:', prev.length);
        const filtered = prev.filter(conv => {
          // Robust null checking and ID validation
          if (!conv || typeof conv !== 'object') {
            console.warn('🗑️ Found null/invalid conversation:', conv);
            return false;
          }

          if (!conv.conversationId || typeof conv.conversationId === 'undefined') {
            console.warn('🗑️ Found conversation without ID:', conv);
            return false;
          }

          return conv.conversationId.toString() !== deletedConversationId;
        });

        console.log('🗑️ Conversations after filtering:', filtered.length, 'removed:', prev.length - filtered.length);
        return filtered;
      });

      // Remove messages for the deleted conversation
      setMessages(prev => {
        const newMessages = { ...prev };
        delete newMessages[deletionData.conversationId];
        return newMessages;
      });

      // Remove unread count for the deleted conversation
      setUnreadCounts(prev => {
        const newCounts = { ...prev };
        delete newCounts[deletionData.conversationId];
        return newCounts;
      });

      // Clear active conversation if it was the deleted one
      setActiveConversation(prev => {
        if (prev && prev.conversationId && prev.conversationId.toString() === deletedConversationId) {
          console.log('🗑️ Clearing active conversation as it was deleted');
          return null;
        }
        return prev;
      });

      // Show notification
      notifications.show({
        title: 'Conversation Deleted',
        message: 'A conversation has been deleted',
        color: 'yellow',
        autoClose: 3000,
      });
    }, 50); // Slightly longer delay to ensure UI updates complete
  }, []);

  const handleMessageDeleted = useCallback((deletionData) => {
    console.log('🗑️ MESSAGE DELETED EVENT RECEIVED:', deletionData);

    // Validate deletion data
    if (!deletionData || !deletionData.messageId || !deletionData.conversationId) {
      console.error('🗑️ Invalid message deletion data received:', deletionData);
      return;
    }

    const conversationId = deletionData.conversationId;
    const messageId = deletionData.messageId;

    // Remove the message from the messages state
    setMessages(prev => {
      if (!prev[conversationId]) {
        return prev;
      }

      const updatedMessages = prev[conversationId].filter(msg =>
        msg.messageId && msg.messageId.toString() !== messageId.toString()
      );

      return {
        ...prev,
        [conversationId]: updatedMessages
      };
    });

    console.log('🗑️ Message', messageId, 'removed from conversation', conversationId);
  }, []);

  const handleConversationCreated = useCallback((creationData) => {
    console.log('✨ CONVERSATION CREATED EVENT RECEIVED:', creationData);

    // Add new conversation to conversations list
    setTimeout(() => {
      setConversations(prev => {
        console.log('✨ Current conversations before addition:', prev.length);
        // Check if conversation already exists (prevent duplicates)
        const exists = prev.some(conv => conv && conv.conversationId && conv.conversationId === creationData.conversation.conversationId);
        if (exists) {
          console.log('✨ Conversation already exists, skipping addition');
          return prev;
        }

        const newConversations = [creationData.conversation, ...prev];
        console.log('✨ Conversations after addition:', newConversations.length, 'added: 1');
        return newConversations;
      });

      // Show notification
      notifications.show({
        title: 'New Conversation',
        message: 'A new conversation has been started',
        color: 'blue',
        autoClose: 3000,
      });
    }, 10); // Small delay to avoid race conditions
  }, []);

  // Register SSE event listeners (after all messaging handlers are defined)
  useEffect(() => {
    console.log('✅ Registering messaging SSE event listeners');

    // Register messaging SSE event listeners
    sseService.on('connected', handleConnected);
    sseService.on('disconnected', handleDisconnected);
    sseService.on('new_message', handleNewMessage);
    sseService.on('message_deleted', handleMessageDeleted);
    sseService.on('conversation_deleted', handleConversationDeleted);
    sseService.on('conversation_created', handleConversationCreated);
    sseService.on('typing_status', handleTypingStatus);
    sseService.on('user_presence', handleUserPresence);
    sseService.on('messages_read', handleMessagesRead);

    // Cleanup on unmount
    return () => {
      console.log('🧹 Cleaning up messaging SSE event listeners');
      sseService.off('connected', handleConnected);
      sseService.off('disconnected', handleDisconnected);
      sseService.off('new_message', handleNewMessage);
      sseService.off('message_deleted', handleMessageDeleted);
      sseService.off('conversation_deleted', handleConversationDeleted);
      sseService.off('conversation_created', handleConversationCreated);
      sseService.off('typing_status', handleTypingStatus);
      sseService.off('user_presence', handleUserPresence);
      sseService.off('messages_read', handleMessagesRead);
    };
  }, []); // Empty dependency array - run once on mount



  // Cleanup typing indicators (remove after 5 seconds of inactivity)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTypingUsers(prev => {
        const updated = {};
        Object.keys(prev).forEach(conversationId => {
          const conversationTyping = {};
          Object.keys(prev[conversationId]).forEach(userId => {
            const typing = prev[conversationId][userId];
            if (now - typing.startTime < 5000) {
              conversationTyping[userId] = typing;
            }
          });
          if (Object.keys(conversationTyping).length > 0) {
            updated[conversationId] = conversationTyping;
          }
        });
        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Context value
  const value = {
    // State
    conversations,
    activeConversation,
    messages,
    onlineUsers,
    typingUsers,
    isConnected: sseService.isConnected,
    isLoading,
    unreadCounts,
    currentUser,

    // Actions
    initializeMessaging,
    loadConversations,
    loadMessages,
    createConversation,
    sendMessage,
    setTypingStatus,
    markAsRead,
    selectConversation,

    // Utilities
    getTotalUnreadCount: () => {
      return Object.values(unreadCounts).reduce((sum, count) => (sum as number) + (count as number), 0);
    },
    getConversationTypingUsers: (conversationId) => {
      const typing = typingUsers[conversationId] || {};
      return Object.keys(typing)
        .filter(userId => userId !== currentUser?.id)
        .map(userId => typing[userId].userName);
    },
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};

// MessagingButton Component
const MessagingButton = ({ currentUser, availableProviders = [] }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { unreadCounts, isConnected, initializeMessaging } = useMessaging();
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Initialize messaging when user is available AND authenticated (only once)
  useEffect(() => {
    const initializeIfAuthenticated = async () => {
      // Only initialize once per user session, not on every currentUser change
      if (currentUser && initializeMessaging && !isInitialized && currentUser.userType) {
        try {
          // Verify authentication before initializing messaging
          const authStatus = await apiService.checkAuthenticationStatus();

          if (authStatus.authenticated) {
            console.log('User authenticated, initializing messaging for:', currentUser.email || currentUser.username);
            // Initialize messaging with a stable user object
            const stableUser = {
              id: currentUser.email || currentUser.username,
              userType: currentUser.userType,
              fullName: currentUser.fullName,
              email: currentUser.email || currentUser.username
            };
            initializeMessaging(stableUser);
            setIsInitialized(true);
          } else {
            console.log('User not authenticated, skipping messaging initialization');
          }
        } catch (error) {
          console.warn('Authentication check failed in messaging button:', error);
        }
      }
    };

    initializeIfAuthenticated();
  }, [currentUser?.userType, currentUser?.email, currentUser?.username, initializeMessaging, isInitialized]);

  // Calculate unread count with proper React dependency tracking
  const unreadCount = useMemo(() => {
    return Object.values(unreadCounts || {}).reduce((sum, count) => (sum as number) + (count as number), 0);
  }, [unreadCounts]);

  // Get tooltip text based on user type
  const getTooltipText = () => {
    if (currentUser?.userType === 'ADMIN') {
      return 'Messaging System';
    } else {
      return 'Chat With IntaStream';
    }
  };

  return (
    <>
      <Indicator
        color="red"
        label={(unreadCount as number) > 0 ? (unreadCount as number) : null}
        size={16}
        disabled={(unreadCount as number) === 0}
        style={{
          position: 'fixed',
          bottom: isMobile ? 10 : 20,
          right: isMobile ? 10 : 20,
          zIndex: 999
        }}
      >
        <Button
          onClick={() => {
            try {
              console.log('Messaging button clicked, current state:', { isPanelOpen, currentUser });
              setIsPanelOpen(!isPanelOpen);
            } catch (error) {
              console.error('Error clicking messaging button:', error);
            }
          }}
          variant="filled"
          color="blue"
          size={isMobile ? "sm" : "md"}
          style={{
            borderRadius: isMobile ? '20px' : '25px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            paddingLeft: isMobile ? '12px' : '16px',
            paddingRight: isMobile ? '8px' : '12px',
            height: isMobile ? '40px' : '50px',
            minWidth: isMobile ? '120px' : '160px'
          }}
          rightSection={
            <Indicator
              color={isConnected ? 'green' : 'red'}
              size={8}
              offset={4}
              processing={!isConnected}
            >
              <IconMessage size={20} />
            </Indicator>
          }
        >
          <Text size="sm" fw={500} style={{ whiteSpace: 'nowrap' }}>
            {getTooltipText()}
          </Text>
        </Button>
      </Indicator>

      <MessagingPanel
        currentUser={currentUser}
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        availableProviders={availableProviders}
      />
    </>
  );
};

// MessagingPanel Component  
const MessagingPanel = ({ currentUser, isOpen, onClose, availableProviders = [] }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [localProviders, setLocalProviders] = useState(availableProviders);

  const {
    conversations,
    activeConversation,
    setActiveConversation,
    messages,
    onlineUsers,
    typingUsers,
    isConnected,
    isLoading,
    unreadCounts,
    selectConversation,
    sendMessage,
    markAsRead,
    deleteConversation,
    deleteMessage,
    setTypingStatus,
    createConversation,
    getConversationTypingUsers,
    loadConversations,
    loadMessages
  } = useMessaging();

  // Load providers if not available and user is admin without selected provider
  useEffect(() => {
    const loadProviders = async () => {
      if (currentUser?.userType === 'ADMIN' && !currentUser?.providerId && localProviders.length === 0) {
        try {
          const providers = await apiService.getProviders();
          setLocalProviders(providers);
        } catch (error) {
          console.error('Error loading providers for messaging:', error);
        }
      }
    };

    const loadConversationsOnOpen = async () => {
      if (currentUser?.id && loadConversations) {
        try {
          console.log('Loading conversations when messaging panel opens');
          await loadConversations(currentUser.id, currentUser);
        } catch (error) {
          console.error('Error loading conversations when panel opens:', error);
        }
      }
    };

    if (isOpen) {
      loadProviders();
      loadConversationsOnOpen();
    }
  }, [isOpen, currentUser, localProviders.length, loadConversations]);
  // Helper function to format conversation display
  const formatConversationTitle = (conversation) => {
    if (!conversation) return 'Unknown';

    // Parse subject to extract user email and original subject
    const subject = conversation.subject || '';
    const userEmailMatch = subject.match(/Started by ([^:]+):/);

    if (userEmailMatch) {
      const userEmail = userEmailMatch[1];

      // Get the current user's full name if this conversation was created by them
      if (currentUser && currentUser.username === userEmail) {
        // This conversation was created by the current user, show their full name
        return `${conversation.hospitalName} - ${currentUser.fullName}`;
      }

      // For conversations created by other users, we need to look up their full name
      // For now, we'll show the email until we implement user lookup
      return `${conversation.hospitalName} - ${userEmail}`;
    }

    // Fallback for conversations without the new format - show hospital name only
    // (These are legacy conversations created before enhanced format)
    return conversation.hospitalName;
  };

  const formatConversationSubtitle = (conversation) => {
    if (!conversation) return 'No subject';

    const subject = conversation.subject || '';
    // Always show the conversation subject here. The latest message preview
    // is rendered separately below to avoid the appearance of messages
    // "accumulating" in the conversation list.
    const originalSubject = subject.replace(/Started by [^:]+:\s*/, '') || 'General Discussion';
    return originalSubject;
  };

  // Add CSS animations for typing indicator
  React.useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      @keyframes typingDot {
        0%, 60%, 100% {
          transform: scale(1);
          opacity: 0.4;
        }
        30% {
          transform: scale(1.2);
          opacity: 1;
        }
      }
      
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Calculate total unread count with proper React dependency tracking
  const getTotalUnreadCount = useMemo(() => {
    return Object.values(unreadCounts || {}).reduce((sum, count) => (sum as number) + (count as number), 0);
  }, [unreadCounts]);

  const [messageInput, setMessageInput] = useState('');
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);


  const [newConversationSubject, setNewConversationSubject] = useState('');
  const [selectedHospital, setSelectedHospital] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(null);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const deletingConversationRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeConversation]);

  // Handle typing indicators
  const handleTyping = () => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    if (activeConversation && activeConversation.conversationId) {
      setTypingStatus(activeConversation.conversationId, true);

      const timeout = setTimeout(() => {
        if (activeConversation && activeConversation.conversationId) {
          setTypingStatus(activeConversation.conversationId, false);
        }
      }, 3000);

      setTypingTimeout(timeout);
    }
  };

  const handleSendMessage = () => {
    if (!messageInput.trim() || !activeConversation || !activeConversation.conversationId) {
      return;
    }

    sendMessage(activeConversation.conversationId, messageInput.trim());
    setMessageInput('');

    if (typingTimeout) {
      clearTimeout(typingTimeout);
      if (activeConversation && activeConversation.conversationId) {
        setTypingStatus(activeConversation.conversationId, false);
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleDeleteMessage = async (messageId, event) => {
    event.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/messaging/messages/${messageId}`, {
        method: 'DELETE',
        credentials: 'include', // Include httpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        notifications.show({
          title: 'Success',
          message: 'Message deleted successfully',
          color: 'green'
        });

        // Refresh messages for the current conversation
        if (activeConversation && activeConversation.conversationId) {
          await loadMessages(activeConversation.conversationId);
        }
      } else {
        throw new Error('Failed to delete message');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete message',
        color: 'red'
      });
    }
  };

  const handleDeleteConversation = async (conversationId, event) => {
    event.stopPropagation(); // Prevent conversation selection

    // Check if conversationId is valid
    if (!conversationId) {
      console.error('Invalid conversationId:', conversationId);
      return;
    }

    // Check if we're already deleting this conversation
    if (deletingConversationRef.current === conversationId) {
      console.log('Deletion already in progress for conversation:', conversationId);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }

    // Mark this conversation as being deleted
    deletingConversationRef.current = conversationId;

    // If this is the active conversation, clear it immediately to prevent race conditions
    if (activeConversation && activeConversation.conversationId && activeConversation.conversationId.toString() === conversationId.toString()) {
      console.log('🗑️ Clearing active conversation before deletion to prevent race conditions');
      selectConversation(null);
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/messaging/conversations/${conversationId}`, {
        method: 'DELETE',
        credentials: 'include', // Include httpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Immediately update local state to prevent race conditions
        // Filter out the deleted conversation immediately
        const updatedConversations = conversations.filter(conv =>
          conv && conv.conversationId && conv.conversationId.toString() !== conversationId.toString()
        );

        // If the deleted conversation was active, clear it
        if (activeConversation && activeConversation.conversationId && activeConversation.conversationId === conversationId) {
          selectConversation(null);
        }

        notifications.show({
          title: 'Success',
          message: 'Conversation deleted successfully',
          color: 'green'
        });

        // Wait for SSE event to propagate, then reload to ensure consistency
        setTimeout(async () => {
          await loadConversations(currentUser.id);
          // Clear the deletion tracking
          deletingConversationRef.current = null;
        }, 200);
      } else {
        throw new Error('Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      // Clear the deletion tracking on error
      deletingConversationRef.current = null;
      notifications.show({
        title: 'Error',
        message: 'Failed to delete conversation',
        color: 'red'
      });
    }
  };

  const handleCreateConversation = async () => {
    if (currentUser?.userType === 'ADMIN') {
      // Admin creating conversation with selected hospital
      if (!currentUser?.providerId || !newConversationSubject.trim()) {
        notifications.show({
          title: 'Missing Information',
          message: 'Please ensure a hospital is selected and enter a subject',
          color: 'red'
        });
        return;
      }

      const conversation = await createConversation(currentUser.providerId, newConversationSubject.trim());
      if (conversation) {
        setShowNewConversationModal(false);
        setNewConversationSubject('');
        selectConversation(conversation);
        // Refresh conversations list to ensure it shows up immediately
        await loadConversations(currentUser.id, currentUser);
      }
    } else {
      // Hospital user creating conversation with IntaStream support
      if (!newConversationSubject.trim()) {
        notifications.show({
          title: 'Missing Information',
          message: 'Please enter a subject for your conversation',
          color: 'red'
        });
        return;
      }

      // Validate that we have a valid provider ID
      if (!currentUser.providerId) {
        notifications.show({
          title: 'No Hospital Selected',
          message: 'Please select a hospital from the provider dropdown before starting a conversation',
          color: 'red'
        });
        return;
      }

      // Create conversation with the selected provider ID
      const conversation = await createConversation(currentUser.providerId, newConversationSubject.trim());
      if (conversation) {
        setShowNewConversationModal(false);
        setNewConversationSubject('');
        selectConversation(conversation);
        // Refresh conversations list to ensure it shows up immediately
        await loadConversations(currentUser.id, currentUser);
      }
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    const messageDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (messageDate.toDateString() === today.toDateString()) {
      return formatTime(date);
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return messageDate.toLocaleDateString('en-GB');
    }
  };

  const activeMessages = React.useMemo(() => {
    if (!activeConversation || !activeConversation.conversationId) {
      return [];
    }
    return messages[activeConversation.conversationId] || [];
  }, [activeConversation, messages]);

  // Force re-render when messages change
  useEffect(() => {
    console.log('Messages updated for conversation:', activeConversation?.conversationId, activeMessages.length);
  }, [activeMessages, activeConversation?.conversationId]);

  const getTypingText = () => {
    if (!activeConversation || !activeConversation.conversationId) return '';
    const typing = getConversationTypingUsers(activeConversation.conversationId);
    if (typing.length === 0) return '';
    if (typing.length === 1) return `${typing[0]} is typing...`;
    if (typing.length === 2) return `${typing[0]} and ${typing[1]} are typing...`;
    return `${typing[0]} and ${typing.length - 1} others are typing...`;
  };

  // Mock hospital data for demo - replace with actual API call
  const hospitalOptions = [
    { value: '8', label: 'Burcot Hall Hospital' },
    { value: '2', label: 'General Hospital' },
    { value: '3', label: 'City Medical Center' }
  ];

  if (!isOpen) return null;

  return (
    <Paper
      radius={isMobile ? "md" : "xl"}
      shadow="xl"
      p={0}
      style={{
        position: 'fixed',
        bottom: isMobile ? 0 : 20,
        right: isMobile ? 0 : 20,
        left: isMobile ? 0 : 'auto',
        width: isMobile ? '100vw' : 560,
        height: isMobile ? '100vh' : 650,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--mantine-color-body)',
        border: '1px solid var(--mantine-color-default-border)',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Group
        justify="space-between"
        p="lg"
        style={{
          borderBottom: '1px solid var(--mantine-color-default-border)',
          backgroundColor: 'var(--mantine-color-blue-light)',
          backgroundImage: 'linear-gradient(135deg, var(--mantine-color-blue-light) 0%, var(--mantine-color-blue-light-hover) 100%)'
        }}
      >
        <Group gap="sm">
          <Box
            style={{
              padding: '8px',
              borderRadius: '12px',
              backgroundColor: 'var(--mantine-color-blue-6)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <IconMessage size={18} />
          </Box>
          <div>
            <Text fw={600} size="sm" style={{ color: 'var(--mantine-color-blue-filled)' }}>
              {currentUser?.userType === 'ADMIN' ? 'Messaging System' : 'Chat With IntaStream'}
            </Text>
            <Group gap="xs" mt={2} align="center">
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: isConnected ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-red-6)',
                  animation: !isConnected ? 'pulse 2s infinite' : 'none',
                  flexShrink: 0
                }}
              />
              <Text size="xs" c="dimmed">
                {isConnected ? 'Connected' : 'Connecting...'}
              </Text>
              {(getTotalUnreadCount as number) > 0 && (
                <Badge size="xs" variant="filled" color="red" style={{ fontSize: '10px' }}>
                  {getTotalUnreadCount as number}
                </Badge>
              )}
            </Group>
          </div>
        </Group>
        <Group gap="xs">
          <Tooltip label="New Conversation">
            <ActionIcon
              variant="light"
              color="blue"
              size="lg"
              radius="md"
              onClick={() => setShowNewConversationModal(true)}
            >
              <IconPlus size={18} />
            </ActionIcon>
          </Tooltip>
          <ActionIcon
            variant="light"
            color="gray"
            size="lg"
            radius="md"
            onClick={onClose}
          >
            <IconX size={18} />
          </ActionIcon>
        </Group>
      </Group>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Conversations List */}
        <div style={{
          width: isMobile ? (activeConversation ? '0%' : '100%') : (activeConversation ? '73%' : '100%'),
          display: isMobile && activeConversation ? 'none' : 'block',
          borderRight: activeConversation && !isMobile ? '1px solid var(--mantine-color-default-border)' : 'none',
          backgroundColor: 'var(--mantine-color-gray-light)'
        }}>
          <ScrollArea style={{ height: '100%' }}>
            {isLoading ? (
              <Box p="xl" style={{ textAlign: 'center' }}>
                <Loader size="sm" />
                <Text size="sm" c="dimmed" mt="xs">Loading conversations...</Text>
              </Box>
            ) : !conversations || conversations.length === 0 ? (
              <Box p="xl" style={{ textAlign: 'center' }}>
                <Box
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--mantine-color-gray-2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}
                >
                  <IconMessage size={24} color="var(--mantine-color-gray-5)" />
                </Box>
                <Text size="sm" c="dimmed" mb="md">No conversations yet</Text>
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => setShowNewConversationModal(true)}
                >
                  Start Conversation
                </Button>
              </Box>
            ) : (
              (conversations || [])
                .filter(conversation => {
                  if (!conversation || typeof conversation !== 'object') {
                    console.warn('🔍 Filtering out invalid conversation:', conversation);
                    return false;
                  }
                  if (!conversation.conversationId || typeof conversation.conversationId === 'undefined') {
                    console.warn('🔍 Filtering out conversation without ID:', conversation);
                    return false;
                  }
                  // Skip conversations that are currently being deleted
                  if (deletingConversationRef.current === conversation.conversationId) {
                    console.log('🗑️ Skipping conversation being deleted:', conversation.conversationId);
                    return false;
                  }
                  return true;
                })
                .map((conversation) => {
                  // Extra safety check within map
                  if (!conversation || !conversation.conversationId) {
                    console.error('🚨 CRITICAL: Null conversation passed filter:', conversation);
                    return null;
                  }

                  return (
                    <UnstyledButton
                      key={`conversation-${conversation.conversationId}-${conversation.createdAt || Date.now()}`}
                      onClick={() => {
                        if (conversation && conversation.conversationId) {
                          // Don't allow selection of conversations being deleted
                          if (deletingConversationRef.current === conversation.conversationId) {
                            console.log('🗑️ Blocked selection of conversation being deleted:', conversation.conversationId);
                            return;
                          }
                          selectConversation(conversation);
                        } else {
                          console.error('🚨 Attempted to select invalid conversation:', conversation);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '16px',
                        borderBottom: '1px solid var(--mantine-color-default-border)',
                        backgroundColor: (conversation && conversation.conversationId && activeConversation?.conversationId === conversation.conversationId)
                          ? 'var(--mantine-color-blue-light)'
                          : 'transparent',
                        borderLeft: (conversation && conversation.conversationId && activeConversation?.conversationId === conversation.conversationId)
                          ? '3px solid var(--mantine-color-blue-filled)'
                          : '3px solid transparent',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (conversation && conversation.conversationId && activeConversation?.conversationId !== conversation.conversationId) {
                          e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-light-hover)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (conversation && conversation.conversationId && activeConversation?.conversationId !== conversation.conversationId) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Group justify="space-between" align="flex-start">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Group gap="sm" mb={8}>
                            <div style={{ position: 'relative' }}>
                              <Avatar
                                size={40}
                                color="blue"
                                style={{
                                  backgroundColor: 'var(--mantine-color-blue-filled)',
                                  border: '2px solid var(--mantine-color-blue-light)'
                                }}
                              >
                                <IconUser size={18} />
                              </Avatar>
                              {/* Status indicator positioned better for wider windows */}
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: -2,
                                  right: -2,
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  backgroundColor: conversation.isHospitalUserOnline ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-red-6)',
                                  border: '2px solid var(--mantine-color-body)',
                                  zIndex: 1
                                }}
                              />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text size="sm" fw={600} style={{ color: 'var(--mantine-color-text)', marginBottom: '2px' }}>
                                {formatConversationTitle(conversation)}
                              </Text>
                              <Text size="xs" c="dimmed" style={{ marginBottom: '4px' }}>
                                {formatConversationSubtitle(conversation)}
                              </Text>
                            </div>
                          </Group>

                        </div>
                        <Stack gap="xs" align="flex-end">
                          <Group gap="xs" align="center">
                            <Text size="xs" c="dimmed">
                              {formatDate(conversation.lastMessageAt)}
                            </Text>
                            {currentUser?.userType === 'ADMIN' && (
                              <Tooltip label="Delete Conversation">
                                <div
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (conversation && conversation.conversationId && typeof conversation.conversationId !== 'undefined') {
                                      handleDeleteConversation(conversation.conversationId, e);
                                    } else {
                                      console.error('Cannot delete: conversation is null or missing conversationId', conversation);
                                    }
                                  }}
                                  style={{
                                    opacity: 0.7,
                                    padding: '6px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease',
                                    backgroundColor: 'transparent'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--mantine-color-red-light)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <IconTrash size={12} color="var(--mantine-color-red-6)" />
                                </div>
                              </Tooltip>
                            )}
                          </Group>
                          {conversation && conversation.conversationId && unreadCounts[conversation.conversationId] > 0 && (
                            <Badge size="xs" variant="filled" color="red">
                              {unreadCounts[conversation.conversationId]}
                            </Badge>
                          )}
                        </Stack>
                      </Group>
                    </UnstyledButton>
                  );
                })
                .filter(item => item !== null) // Remove any null items from map
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        {activeConversation && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Chat Header */}
            <Box
              p={isMobile ? "md" : "lg"}
              style={{
                borderBottom: '1px solid var(--mantine-color-default-border)',
                backgroundColor: 'var(--mantine-color-body)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <Group gap="sm">
                {isMobile && (
                  <ActionIcon
                    variant="light"
                    size="lg"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (selectConversation && typeof selectConversation === 'function') {
                        selectConversation(null);
                      }
                    }}
                    style={{ flexShrink: 0 }}
                  >
                    <ChevronLeftIcon style={{ width: '18px', height: '18px' }} />
                  </ActionIcon>
                )}
                {isMobile && (
                  <Tooltip label="New Conversation">
                    <ActionIcon
                      variant="light"
                      color="blue"
                      size="lg"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowNewConversationModal(true);
                      }}
                      style={{ flexShrink: 0 }}
                    >
                      <IconPlus style={{ width: '18px', height: '18px' }} />
                    </ActionIcon>
                  </Tooltip>
                )}
                <Avatar size={isMobile ? 32 : 36} color="green" style={{ backgroundColor: 'var(--mantine-color-green-filled)' }}>
                  <IconUser size={isMobile ? 16 : 18} />
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={600} size="sm" style={{ color: 'var(--mantine-color-text)' }} truncate>
                    {formatConversationTitle(activeConversation)}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {formatConversationSubtitle(activeConversation)}
                  </Text>
                </div>
              </Group>
            </Box>

            {/* Messages */}
            <ScrollArea style={{ flex: 1, backgroundColor: 'var(--mantine-color-gray-light)' }} p="md">
              <Stack gap="sm" key={`messages-${activeConversation?.conversationId}-${activeMessages.length}`}>
                {activeMessages.map((message, index) => {
                  const isOwn = message.senderId === currentUser?.id;
                  const showAvatar = index === 0 || activeMessages[index - 1].senderId !== message.senderId;

                  return (
                    <Group
                      key={message.messageId}
                      justify={isOwn ? 'flex-end' : 'flex-start'}
                      align="flex-end"
                      gap="sm"
                      style={{
                        paddingLeft: isOwn ? '60px' : '0px',
                        paddingRight: isOwn ? '0px' : '60px',
                      }}
                    >
                      {!isOwn && showAvatar && (
                        <Avatar size={32} color="blue" style={{ backgroundColor: 'var(--mantine-color-blue-filled)' }}>
                          <IconUser size={16} />
                        </Avatar>
                      )}
                      {!isOwn && !showAvatar && <div style={{ width: 32 }} />}

                      <Box
                        style={{
                          maxWidth: '75%',
                          wordBreak: 'break-word',
                          position: 'relative'
                        }}
                      >
                        {!isOwn && showAvatar && (
                          <Text size="xs" fw={500} mb={4} style={{ color: 'var(--mantine-color-blue-filled)' }}>
                            {message.senderName}
                          </Text>
                        )}
                        <Paper
                          p="sm"
                          radius="xl"
                          style={{
                            backgroundColor: isOwn
                              ? 'var(--mantine-color-blue-filled)'
                              : 'var(--mantine-color-body)',
                            color: isOwn
                              ? 'white'
                              : 'var(--mantine-color-text)',
                            border: !isOwn ? '1px solid var(--mantine-color-default-border)' : 'none',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            position: 'relative',
                          }}
                        >
                          <Group justify="space-between" align="flex-start" gap="sm">
                            <Text size="sm" style={{ lineHeight: 1.4, flex: 1 }}>
                              {message.content}
                            </Text>
                            {currentUser?.userType === 'ADMIN' && (
                              <ActionIcon
                                size="xs"
                                variant="light"
                                color="red"
                                onClick={(e) => handleDeleteMessage(message.messageId, e)}
                                title="Delete Message"
                                style={{
                                  opacity: 0.6,
                                  backgroundColor: isOwn ? 'rgba(255,255,255,0.2)' : 'rgba(255,0,0,0.1)'
                                }}
                              >
                                <IconTrash size={10} />
                              </ActionIcon>
                            )}
                          </Group>
                          <Text
                            size="xs"
                            style={{
                              opacity: 0.7,
                              textAlign: 'right',
                              marginTop: 6,
                              color: isOwn ? 'rgba(255,255,255,0.8)' : 'var(--mantine-color-dimmed)'
                            }}
                          >
                            {formatTime(message.sentAt)}
                          </Text>
                        </Paper>
                      </Box>
                    </Group>
                  );
                })}

                {/* Typing Indicator */}
                {getTypingText() && (
                  <Group justify="flex-start" gap="xs" style={{
                    animation: 'fadeIn 0.3s ease-in',
                    '@keyframes fadeIn': {
                      from: { opacity: 0, transform: 'translateY(10px)' },
                      to: { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}>
                    <Avatar size={24} color="gray">
                      <IconUser size={12} />
                    </Avatar>
                    <Paper p="xs" style={{ backgroundColor: 'var(--mantine-color-body)', border: '1px solid var(--mantine-color-default-border)' }}>
                      <Group gap="xs" align="center">
                        <div style={{
                          display: 'flex',
                          gap: '2px',
                          alignItems: 'center'
                        }}>
                          <div style={{
                            width: '4px',
                            height: '4px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--mantine-color-dimmed)',
                            animation: 'typingDot 1.4s ease-in-out infinite',
                            animationDelay: '0s'
                          }} />
                          <div style={{
                            width: '4px',
                            height: '4px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--mantine-color-dimmed)',
                            animation: 'typingDot 1.4s ease-in-out infinite',
                            animationDelay: '0.2s'
                          }} />
                          <div style={{
                            width: '4px',
                            height: '4px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--mantine-color-dimmed)',
                            animation: 'typingDot 1.4s ease-in-out infinite',
                            animationDelay: '0.4s'
                          }} />
                        </div>
                        <Text size="xs" c="dimmed" style={{ fontStyle: 'italic', marginLeft: '8px' }}>
                          {getTypingText()}
                        </Text>
                      </Group>
                    </Paper>
                  </Group>
                )}

                <div ref={messagesEndRef} />
              </Stack>
            </ScrollArea>

            {/* Message Input */}
            <Box
              p="lg"
              style={{
                borderTop: '1px solid var(--mantine-color-gray-2)',
                backgroundColor: 'var(--mantine-color-body)',
                boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <Group gap="sm">
                <TextInput
                  ref={messageInputRef}
                  placeholder="Type a message..."
                  value={messageInput}
                  onChange={(e) => {
                    setMessageInput(e.target.value);
                    handleTyping();
                  }}
                  onKeyPress={handleKeyPress}
                  radius="xl"
                  size="md"
                  style={{
                    flex: 1,
                  }}
                  styles={{
                    input: {
                      border: '1px solid var(--mantine-color-gray-3)',
                      backgroundColor: 'var(--mantine-color-gray-0)',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        borderColor: 'var(--mantine-color-blue-6)',
                        boxShadow: '0 0 0 1px var(--mantine-color-blue-6)'
                      }
                    }
                  }}
                  disabled={!isConnected}
                />
                <ActionIcon
                  onClick={handleSendMessage}
                  disabled={!messageInput.trim() || !isConnected}
                  variant="filled"
                  color="blue"
                  size="xl"
                  radius="xl"
                  style={{
                    background: !messageInput.trim() || !isConnected
                      ? 'var(--mantine-color-gray-4)'
                      : 'linear-gradient(135deg, var(--mantine-color-blue-6) 0%, var(--mantine-color-blue-7) 100%)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <IconSend size={18} />
                </ActionIcon>
              </Group>
              {!isConnected && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="yellow"
                  mt="xs"
                >
                  Reconnecting to messaging...
                </Alert>
              )}
            </Box>
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      <Modal
        opened={showNewConversationModal}
        onClose={() => {
          setShowNewConversationModal(false);
          setSelectedHospital('');
          setNewConversationSubject('');
        }}
        title="Start New Conversation"
        size="sm"
        centered
        styles={{
          root: { zIndex: 9999 },
          inner: { zIndex: 9999 },
          content: { zIndex: 9999 }
        }}
      >
        <Stack gap="md">
          {currentUser?.userType === 'ADMIN' ? (
            currentUser?.providerId ? (
              <TextInput
                label="To"
                value={currentUser?.selectedProviderName || "Selected Hospital"}
                disabled
                description="This conversation will be with the currently selected hospital"
              />
            ) : (
              <Select
                label="To (Select Hospital)"
                placeholder="Choose a hospital to start conversation with"
                value={selectedHospital}
                onChange={setSelectedHospital}
                data={localProviders?.map(provider => ({
                  value: provider.ProviderId.toString(),
                  label: provider.Description
                })) || []}
                description="Select which hospital you want to start a conversation with"
                searchable
              />
            )
          ) : (
            <TextInput
              label="To"
              value="IntaStream Support"
              disabled
              description="This conversation will be with IntaStream support team"
            />
          )}
          <TextInput
            label="Subject"
            placeholder="Enter conversation subject"
            value={newConversationSubject}
            onChange={(e) => setNewConversationSubject(e.target.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setShowNewConversationModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateConversation}
              disabled={!newConversationSubject.trim()}
            >
              Start Conversation
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
};

// Wrapper component to add Notifications to MantineProvider
const MantineWrapper = ({ children, colorScheme, currentUser, selectedProviderId, currentStep, availableProviders = [] }) => {
  // Only show messaging on authenticated pages, not on login/landing pages
  const shouldShowMessaging = currentStep && !['landing', 'initial-auth', 'nurse-auth', 'staff-registration'].some(step => currentStep.startsWith(step));

  return (
    <MantineProvider forceColorScheme={colorScheme}>
      <Notifications position="top-right" zIndex={9999} limit={5} style={{ position: 'fixed', top: 20, right: 20 }} />
      <MessagingProvider>
        {children}
        {currentUser && shouldShowMessaging && (
          // Show messaging button for hospital users always, for admin users in training/nurse contexts always, for admin users in scheduler only after provider selection
          (currentUser.userType !== 'ADMIN' || currentStep === 'training-module' || currentStep === 'nurse-dashboard' || currentStep?.startsWith('training-') || selectedProviderId || currentUser.email === 'maria@intastream.com') &&
          <MessagingButton currentUser={currentUser} availableProviders={availableProviders} />
        )}
      </MessagingProvider>
    </MantineProvider>
  );
};

// Database columns constant (moved outside component to prevent re-creation)
const DB_COLUMNS = [
  'ProfessionalId', 'UID', 'Sex', 'DoB', 'Forename', 'Surname', 'AddressLine1', 'AddressLine2',
  'AddressLine3', 'AddressLine4', 'AddressLine5', 'Postcode', 'EmailAddress', 'TelephoneNumber',
  'MobileNumber', 'CompanyName', 'CompanyNumber', 'BankAccountName', 'BankAcccountNumber',
  'BankSortCode', 'Taxcode', 'NoKName', 'NoKContactTelephoneNumber', 'NoKResidentOfCountry',
  'NoKRelationship', 'NationalInsuranceNumber', 'PassportNumber', 'DBSNumber', 'DBSUpdateNumber',
  'DBSIssueDate', 'DBSExpiryDate', 'PINNumber', 'PINRegistrationDate', 'PINExpiryDate',
  'IsAnesthetic', 'IsHCA', 'IsHDU', 'IsPediatric', 'IsSSD', 'IsWard', 'IsRecovery', 'IsScrub',
  'IsScrubCardiac', 'IsScrubENT', 'IsScrubGynae', 'IsScrubLap', 'IsScrubMicrovascular',
  'IsScrubNeuro', 'IsScrubOptha', 'IsScrubOrtho', 'IsScrubPlastic', 'IsScrubSpine',
  'IsScrubThorasic', 'IsScrubUrology', 'IsScrubASP', 'IsERNurse', 'MedicalRoleId',
  'PassportExpiryDate', 'WR_EUCitizen', 'WR_EUPassport', 'WR_VisaExpiryDate',
  'EmploymentDetail1NameAndAddress', 'EmploymentDetail1PositionGradeSpeciality',
  'EmploymentDetail1DateOfApplication', 'EmploymentDetail1ExpiryDate',
  'EmploymentDetail2NameAndAddress', 'EmploymentDetail2PositionGradeSpeciality',
  'EmploymentDetail2DateOfApplication', 'EmploymentDetail2ExpiryDate',
  'ProfReferee1EmployerName', 'ProfReferee1Name', 'ProfReferee1Title', 'ProfReferee1Address',
  'ProfReferee1Telephone', 'ProfReferee1Email', 'ProfReferee1Relationship',
  'ProfReferee1StartDate', 'ProfReferee1EndDate', 'ProfReferee2EmployerName',
  'ProfReferee2Name', 'ProfReferee2Title', 'ProfReferee2Address', 'ProfReferee2Telephone',
  'ProfReferee2Email', 'ProfReferee2Relationship', 'ProfReferee2StartDate', 'ProfReferee2EndDate'
];

const IntaStreamApp = () => {
  const theme = useMantineTheme();
  const [colorScheme, setColorScheme] = useState('light');
  const [themeLoaded, setThemeLoaded] = useState(false);

  // Data mapping state for CSV field mapping interface
  const [mappings, setMappings] = useState({});
  const [notes, setNotes] = useState({});
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [savingMapping, setSavingMapping] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');


  const filteredDbColumns = useMemo(() => {
    if (!searchTerm) return DB_COLUMNS;
    return DB_COLUMNS.filter(col =>
      col.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  // Helper function to get current user for messaging
  const getCurrentUser = () => {
    if (authForm.provider) {
      // Check if this is an admin user by userType, not isAdminContext flag
      const isAdmin = authForm.provider.userType === 'ADMIN' || authForm.provider.id === -1;

      if (isAdmin) {
        const selectedProvider = availableProviders.find(p => p.ProviderId.toString() === selectedProviderId);
        return {
          id: authForm.provider.username,
          userType: 'ADMIN',
          fullName: authForm.provider.fullName || authForm.provider.forename || 'Admin User',
          providerId: selectedProviderId ? parseInt(selectedProviderId) : null, // Use selected provider for admin
          selectedProviderName: selectedProvider ? selectedProvider.Description : null,
          email: authForm.provider.email,
          username: authForm.provider.username
        };
      } else {
        // Regular hospital user
        return {
          id: authForm.provider.username,
          userType: 'PRO_V',
          fullName: authForm.provider.fullName || authForm.provider.forename || authForm.provider.description,
          providerId: authForm.provider.id,
          email: authForm.provider.email,
          username: authForm.provider.username
        };
      }
    }
    if (nurseAuth.email && nurseAuth.authenticated) {
      // Check if this is an admin user (maria@intastream.com has admin privileges)
      const isAdminUser = nurseAuth.email === 'maria@intastream.com' || nurseAuth.userType === 'ADMIN';

      return {
        id: nurseAuth.username,  // Use Login table Username for SSE authentication
        userType: isAdminUser ? 'ADMIN' : 'NURSE',
        fullName: `${nurseProfile.firstName} ${nurseProfile.lastName}`.trim() || nurseAuth.email,
        providerId: isAdminUser ? (selectedProviderId ? parseInt(selectedProviderId) : null) : null,
        selectedProviderName: isAdminUser && selectedProviderId ?
          availableProviders.find(p => p.ProviderId.toString() === selectedProviderId)?.Description || 'Selected Hospital'
          : null
      };
    }
    return null;
  };

  // Confirmation modal functions
  const showConfirmation = (message, onConfirm, onCancel = null, title = 'Confirm Action', confirmText = 'OK', cancelText = 'Cancel') => {
    console.log('📋 showConfirmation called with message:', message);
    console.log('📋 Current step when showConfirmation called:', currentStep);
    setConfirmationModal({
      opened: true,
      title,
      message,
      onConfirm,
      onCancel,
      confirmText,
      cancelText
    });
    console.log('📋 Confirmation modal state set to opened');
    console.log('📋 Confirmation modal state after setting:', { opened: true, title, message });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(prev => ({
      ...prev,
      opened: false
    }));
  };

  const handleConfirmationConfirm = () => {
    console.log('✅ Confirmation dialog - Confirm button clicked');
    if (confirmationModal.onConfirm) {
      console.log('✅ Executing onConfirm callback');
      confirmationModal.onConfirm();
    }
    closeConfirmationModal();
  };

  const handleConfirmationCancel = () => {
    if (confirmationModal.onCancel) {
      confirmationModal.onCancel();
    }
    closeConfirmationModal();
  };

  const [currentStep, setCurrentStep] = useState('landing');

  // Initialize currentStep properly with useEffect instead of useState callback
  useEffect(() => {
    try {
      // Check URL first
      const urlParams = new URLSearchParams(window.location.search);
      const stepFromURL = urlParams.get('step');

      if (stepFromURL) {
        setCurrentStep(stepFromURL);
        return;
      }

      // First check for tab-specific session
      const tabStep = loadFromStorage(STORAGE_KEYS.TAB_CURRENT_STEP, null, true);
      const tabAuthType = loadFromStorage(STORAGE_KEYS.TAB_AUTH_TYPE, null, true);

      if (tabStep && tabAuthType) {
        // Verify the authentication is still valid
        if (tabAuthType === 'provider') {
          const savedAuth = loadFromStorage(STORAGE_KEYS.AUTH, { provider: null });
          if (savedAuth.provider) {
            setCurrentStep(tabStep);
            return;
          }
        } else if (tabAuthType === 'nurse') {
          const savedNurseAuth = loadFromStorage(STORAGE_KEYS.NURSE_AUTH, { authenticated: false });
          if (savedNurseAuth.authenticated) {
            setCurrentStep(tabStep);
            return;
          }
        } else if (tabAuthType === 'registration') {
          const savedRegAuth = loadFromStorage(STORAGE_KEYS.REGISTRATION_AUTH, { authenticated: false });
          if (savedRegAuth.authenticated) {
            setCurrentStep(tabStep);
            return;
          }
        }
      }

      // If no tab session, don't automatically redirect to any authenticated pages
      // Let users choose their intended destination instead of forcing redirects
      // This prevents cross-system authentication contamination

      // Don't auto-redirect to nurse-dashboard on page refresh - respect user navigation intent
      // The landing page should always be accessible even if authentication cookies exist
      // Users can explicitly click IntaLearn to re-enter the training system if needed

      // Default to landing page (already set)
    } catch (error) {
      console.error('Error initializing current step:', error);
      setCurrentStep('landing');
    }
  }, []);

  // No auto-loading - everything is local until "Save All Mappings" is clicked

  const [currentWeek, setCurrentWeek] = useState(() => {
    // Try to restore currentWeek from localStorage, default to today if not found
    const savedWeek = loadFromStorage('intastream_current_week', null);
    if (savedWeek) {
      const restoredDate = new Date(savedWeek);
      restoredDate.setHours(12, 0, 0, 0);
      return restoredDate;
    }
    // Default to today
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    return today;
  });
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [bookingOpened, { open: openBooking, close: closeBooking }] = useDisclosure(false);
  const [highlightSpecialization, setHighlightSpecialization] = useState(false);
  const [isFirstTime] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    return today;
  });
  const [editingBooking, setEditingBooking] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [datePickerOpened, { open: openDatePicker, close: closeDatePicker }] = useDisclosure(false);
  const [datePickerValue, setDatePickerValue] = useState(null);
  const [deleteConfirmOpened, { open: openDeleteConfirm, close: closeDeleteConfirm }] = useDisclosure(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    opened: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    confirmText: 'OK',
    cancelText: 'Cancel'
  });

  // Database-driven training state with session persistence for progress
  const [currentTraining, setCurrentTraining] = useState(() => {
    return loadFromStorage('intastream_current_training', null, true);
  });
  const [trainingProgress, setTrainingProgress] = useState({});
  const [currentSection, setCurrentSection] = useState(() => {
    return loadFromStorage('intastream_current_section', 0, true);
  });
  const [assessmentAnswers, setAssessmentAnswers] = useState(() => {
    return loadFromStorage('intastream_assessment_answers', {}, true);
  });
  const [assessmentComplete, setAssessmentComplete] = useState(false);
  const [assessmentScore, setAssessmentScore] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Database-driven training courses
  const [dbCourses, setDbCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesError, setCoursesError] = useState(null);
  const [currentCourseContent, setCurrentCourseContent] = useState(null);

  // Nurse authentication and profile state
  const [nurseAuth, setNurseAuth] = useState(loadFromStorage(STORAGE_KEYS.NURSE_AUTH, {
    email: '',
    provider: '',
    authenticated: false
  }));

  const [generatingCertificate, setGeneratingCertificate] = useState(false);
  const [nurseProfile, setNurseProfile] = useState(loadFromStorage(STORAGE_KEYS.NURSE_PROFILE, {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    address: {
      street: '',
      city: '',
      postcode: '',
      country: 'United Kingdom'
    },
    nursingRole: '',
    registrationNumber: '',
    qualifications: [],
    profileComplete: false
  }));

  // Live appointments state
  const [liveAppointments, setLiveAppointments] = useState([]);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);

  // Medical roles state
  const [medicalRoles, setMedicalRoles] = useState([]);
  const [isLoadingMedicalRoles, setIsLoadingMedicalRoles] = useState(false);

  // Professionals state (for admin staff assignment)
  const [professionals, setProfessionals] = useState([]);
  const [isLoadingProfessionals, setIsLoadingProfessionals] = useState(false);

  // Staff registration state
  const [registrationForm, setRegistrationForm] = useState(() => {
    // Check if user was previously authenticated for registration
    const savedRegAuth = loadFromStorage(STORAGE_KEYS.REGISTRATION_AUTH, { authenticated: false });

    if (savedRegAuth.authenticated && savedRegAuth.niNumber) {
      return {
        niNumber: savedRegAuth.niNumber,
        currentStep: 'personal-details', // Start from personal details if authenticated
        isValidating: false,
        professionalId: null,
        hasExistingSession: false,
        sessionChecked: false,
        userType: savedRegAuth.userType || 'USER'
      };
    }

    // Default state for new users
    return {
      niNumber: '',
      currentStep: 'welcome',
      isValidating: false,
      professionalId: null,
      hasExistingSession: false,
      sessionChecked: false,
      userType: 'USER'
    };
  });

  // Registration form data
  const [registrationData, setRegistrationData] = useState({
    // Personal Information
    title: '',
    dateOfBirth: '',
    forename: '',
    surname: '',
    fullName: '',

    // Professional Credentials
    nmcPinHpcNumber: '',
    nmcPinHpcRegistrationDate: '',
    nmcPinHpcExpiryDate: '',
    dbsNumber: '',
    dbsUpdateReference: '',
    dbsRegistrationDate: '',
    dbsExpiryDate: '',
    passportNumber: '',
    passportExpiryDate: '',

    // Address Information
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    addressLine4: '',
    addressLine5: '',
    postcode: '',

    // Contact Information
    emailAddress: '',
    mobilePhoneNumber: '',
    landLineNumber: '',

    // Next of Kin
    nextOfKinName: '',
    nextOfKinPhone: '',
    nextOfKinRelationship: '',
    nextOfKinAddress: '',

    // Clinical Specializations
    specializations: {
      anaestheticsODP: false,
      hcaHealthCareAssistant: false,
      hduItu: false,
      paediatrics: false,
      recovery: false,
      sterilisationTechnician: false,
      wardRN: false,
      scrubGeneral: false,
      scrubCardiac: false,
      scrubENT: false,
      scrubGynaecology: false,
      scrubLaparoscopic: false,
      scrubMicrovascular: false,
      scrubNeuro: false,
      scrubOptha: false,
      scrubOrtho: false,
      scrubPlastics: false,
      scrubSpine: false,
      scrubThoracic: false,
      scrubUrology: false,
      scrubSfaAsp: false,
      emergencyRoomNurse: false
    },

    // Competency Scores (0=Not Experienced, 1=Moderately Experienced, 2=Very Experienced)
    competencyScores: {
      anaestheticsODP: 0,
      hcaHealthCareAssistant: 0,
      hduItu: 0,
      paediatrics: 0,
      recovery: 0,
      sterilisationTechnician: 0,
      wardRN: 0,
      scrubGeneral: 0,
      scrubCardiac: 0,
      scrubENT: 0,
      scrubGynaecology: 0,
      scrubLaparoscopic: 0,
      scrubMicrovascular: 0,
      scrubNeuro: 0,
      scrubOptha: 0,
      scrubOrtho: 0,
      scrubPlastics: 0,
      scrubSpine: 0,
      scrubThoracic: 0,
      scrubUrology: 0,
      scrubSfaAsp: 0,
      emergencyRoomNurse: 0
    },

    // Work Preferences
    workType: '',
    clinicalAreas: '',
    startDate: '',
    canDrive: null,
    hasProfessionalIndemnity: null,
    professionalIndemnityNumber: '',
    professionalIndemnityEffectiveDate: '',

    // Financial Information
    taxStatement: '',
    hasStudentLoan: null,
    studentLoanBeforeApril6: null,
    studentLoanDirectDebit: null,
    studentLoanType: '',
    hasPostgraduateLoan: null,
    postgraduateLoanBeforeApril6: null,
    postgraduateLoanDirectDebit: null,
    preferredPaymentMethod: '',
    bankAccountName: '',
    bankAccountNumber: '',
    bankSortCode: '',
    gpNameAddress: '',

    // Health & Medical
    hasIllnessDisability: null,
    workRelatedIllness: null,
    currentTreatment: null,
    needsAdjustments: null,
    hasMRSA: null,
    hasCDiff: null,
    tuberculosisOutsideUK: null,
    tuberculosisBCGVaccination: null,
    tuberculosisCough: null,
    tuberculosisWeightLoss: null,
    tuberculosisFever: null,
    tuberculosisContact: null,
    chickenPoxShingles: null,
    bloodBorneVirusContact: null,
    tripleVaccination: null,
    vaccinationSummary: '',
    covid19VaccinationStatus: '',

    // Immigration Status
    visaExpiryDate: '',
    hasBritishEUPassport: null,
    isEUCitizen: null,
    otherWorkPermits: '',

    // Employment History (3 employers)
    employers: [
      { nameAddress: '', position: '', dateFrom: '', dateTo: '' },
      { nameAddress: '', position: '', dateFrom: '', dateTo: '' },
      { nameAddress: '', position: '', dateFrom: '', dateTo: '' }
    ],

    // Qualifications (3 qualifications)
    qualifications: [
      { name: '', institution: '', dateFrom: '', dateTo: '' },
      { name: '', institution: '', dateFrom: '', dateTo: '' },
      { name: '', institution: '', dateFrom: '', dateTo: '' }
    ],

    // Professional References (2 references)
    references: [
      {
        employerName: '', workplaceAddress: '', startDate: '', endDate: '',
        refereeName: '', refereeTitle: '', refereeRelationship: '',
        refereePhone: '', refereeEmail: ''
      },
      {
        employerName: '', workplaceAddress: '', startDate: '', endDate: '',
        refereeName: '', refereeTitle: '', refereeRelationship: '',
        refereePhone: '', refereeEmail: ''
      }
    ],

    // Organizations (3 sets)
    organizations: [
      { name: '', number: '', date: '', expiryDate: '' },
      { name: '', number: '', date: '', expiryDate: '' },
      { name: '', number: '', date: '', expiryDate: '' }
    ],

    // Background Checks
    criminalConvictions: null,
    policeInquiries: null,
    otherCountryPoliceCheck: null,
    backgroundCheckDetails: '',
    fitnessToPracticeInvestigation: null,
    disciplinaryAction: null,

    // Agreement & Signature
    dateSigned: '',
    termsAccepted: false,
    emailAddressSignature: '',

    // Medical Role Assignment
    medicalRoleId: null,
    assignedMedicalRole: '',

    // File uploads
    files: {
      cv: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
      photoId: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
      dbsCertificate: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
      nmcCertificate: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
      passportScans: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
      immunizationRecords: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
      references: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
      professionalQualifications: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
      professionalIndemnityInsurance: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
      companyFormationCertificate: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null }
    }
  });

  // Authorization form state - use sessionStorage for tab isolation
  const [authForm, setAuthForm] = useState(() => {
    // Check for tab-specific auth first
    const tabAuthType = loadFromStorage(STORAGE_KEYS.TAB_AUTH_TYPE, null, true);
    if (tabAuthType === 'provider') {
      return loadFromStorage(STORAGE_KEYS.AUTH, {
        username: '',
        password: '',
        termsAccepted: false,
        isAuthenticating: false,
        provider: null
      }, true); // Use sessionStorage for tab-specific state
    }
    return {
      username: '',
      password: '',
      termsAccepted: false,
      isAuthenticating: false,
      provider: null
    };
  });

  // Provider selection state for admin users
  const [availableProviders, setAvailableProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState(loadFromStorage(STORAGE_KEYS.SELECTED_PROVIDER, ''));
  const [isSelectingProvider, setIsSelectingProvider] = useState(false);
  const [adminUser, setAdminUser] = useState(null);

  // Hospital name based on authenticated provider
  const hospitalName = authForm.provider ? authForm.provider.description : 'Burcot Hall Hospital';

  // Booking form state
  const [bookingForm, setBookingForm] = useState({
    specialization: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    notes: '',
    assignedStaffId: ''
  });


  // Don't save currentStep to localStorage - always start from landing page

  useEffect(() => {
    // Save auth to sessionStorage for tab isolation when provider is authenticated
    if (authForm.provider) {
      saveToStorage(STORAGE_KEYS.AUTH, authForm, true); // Use sessionStorage
    }
  }, [authForm]);

  // Save selected provider ID for admin users
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.SELECTED_PROVIDER, selectedProviderId);
  }, [selectedProviderId]);

  // Save current week for persistence across page refreshes
  useEffect(() => {
    saveToStorage('intastream_current_week', currentWeek.toISOString());
  }, [currentWeek]);

  // Initialize theme from storage
  useEffect(() => {
    const savedTheme = loadFromStorage(STORAGE_KEYS.THEME, 'light');
    setColorScheme(savedTheme);
    setThemeLoaded(true);
  }, []);

  // Check authentication status on app startup for authenticated pages only
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Only check authentication if user is on a page that requires it
      const authRequiredPages = ['scheduler', 'booking-editor', 'nurse-dashboard', 'training-module'];
      if (!authRequiredPages.includes(currentStep)) {
        console.log('Skipping auth check for page:', currentStep);
        return;
      }


      // Prevent race condition: if user is already authenticated, don't overwrite the state immediately after login
      if (authForm.provider && authForm.provider.id && authForm.provider.id !== -1) {
        console.log('User already authenticated, skipping redundant auth check:', authForm.provider);
        return;
      }

      try {
        console.log('Checking authentication status on page refresh for:', currentStep);

        // Use the dedicated auth status endpoint to check if cookies are valid
        const authResult = await apiService.checkAuthenticationStatus();

        if (authResult.authenticated && authResult.userID && authResult.email) {
          // Set authentication state
          apiService.setTokens('', ''); // Just sets the authenticated state

          // Properly populate user information based on userType
          const userInfo = {
            id: (authResult as any).providerId || (authResult.userType === 'ADMIN' ? -1 : parseInt(authResult.userID)),
            username: authResult.email,
            email: authResult.email,
            userType: authResult.userType,
            description: authResult.userType === 'ADMIN' ? 'Admin User' : 'Hospital User',
            fullName: authResult.userType === 'ADMIN' ? 'Admin User' : 'Hospital User'
          };

          // Set the auth form state to populate user information throughout the app
          setAuthForm(prev => ({
            ...prev,
            provider: userInfo
          }));

          // Auto-select provider for admin users based on stored procedure providerId
          if (authResult.userType === 'ADMIN') {
            // Load providers first for admin users to avoid race condition
            try {
              await loadProviders();

              // Only auto-select provider if no provider is currently selected (fresh login scenario)
              // On page refresh, preserve the user's current provider selection
              if (!selectedProviderId && (authResult as any).providerId && (authResult as any).providerId !== -1) {
                console.log('Auto-selecting provider for fresh admin login:', (authResult as any).providerId);
                setSelectedProviderId((authResult as any).providerId.toString());
              } else if (selectedProviderId) {
                console.log('Preserving admin user\'s selected provider on page refresh:', selectedProviderId);
                // selectedProviderId is already set from localStorage, preserve it
              } else {
                console.log('No provider auto-selection - admin will choose manually');
              }
            } catch (error) {
              console.error('Error loading providers during auth restoration:', error);
            }
          }

          console.log('Authentication verified via httpOnly cookies:', userInfo);
        }
      } catch (error) {
        console.log('No valid authentication found');
        // Clear any old localStorage tokens for security
        localStorage.removeItem('intastream_access_token');
        localStorage.removeItem('intastream_refresh_token');
      }
    };

    checkAuthStatus();
  }, [currentStep]); // Add currentStep as dependency

  // Save theme changes to storage
  useEffect(() => {
    if (themeLoaded) {
      saveToStorage(STORAGE_KEYS.THEME, colorScheme);
    }
  }, [colorScheme, themeLoaded]);

  // Track tab-specific session for isolation
  useEffect(() => {
    // Determine auth type based on current step
    let authType = null;
    if (currentStep === 'scheduler' || currentStep === 'initial-auth') {
      authType = 'provider';
    } else if (currentStep === 'nurse-dashboard' || currentStep === 'nurse-login' || currentStep.startsWith('training-')) {
      authType = 'nurse';
    } else if (currentStep.startsWith('registration-') || currentStep === 'staff-registration') {
      authType = 'registration';
    }

    // Save tab session
    saveToStorage(STORAGE_KEYS.TAB_CURRENT_STEP, currentStep, true);
    saveToStorage(STORAGE_KEYS.TAB_AUTH_TYPE, authType, true);
  }, [currentStep]);

  // Update browser tab title based on current app
  useEffect(() => {
    const getTabTitle = () => {
      switch (currentStep) {
        case 'landing':
          return 'IntaStream Hub';
        case 'initial-auth':
          return 'IntaSchedule - Login';
        case 'scheduler':
        case 'booking-editor':
          return 'IntaSchedule - Booking System';
        case 'nurse-auth':
          return 'IntaLearn - Login';
        case 'nurse-dashboard':
        case 'training-module':
          return 'IntaLearn - Mandatory Training';
        case 'staff-registration':
          return 'IntaProfile - Staff Profile';
        default:
          return 'IntaStream Hub';
      }
    };

    document.title = getTabTitle();
  }, [currentStep]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.step) {
        setCurrentStep(event.state.step);
      } else {
        // If no state, check URL params
        const urlParams = new URLSearchParams(window.location.search);
        const stepFromURL = urlParams.get('step');
        setCurrentStep(stepFromURL || 'landing');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.NURSE_AUTH, nurseAuth);
  }, [nurseAuth]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.NURSE_PROFILE, nurseProfile);
  }, [nurseProfile]);


  // Scroll progress tracking for training modules
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setScrollProgress(Math.min(100, Math.max(0, scrollPercent)));
    };

    if (currentStep === 'training-module') {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [currentStep]);

  const toggleColorScheme = () => {
    setColorScheme(prevScheme => prevScheme === 'dark' ? 'light' : 'dark');
  };

  // Navigation function that updates both state and URL
  const navigateToStep = (step) => {
    setCurrentStep(step);
    // Update URL without triggering a page reload
    const url = new URL(window.location.href);
    if (step === 'landing') {
      url.searchParams.delete('step');
    } else {
      url.searchParams.set('step', step);
    }
    window.history.pushState({ step }, '', url);
  };

  // Unified intelligent appointment loading with mobile/desktop optimization
  const loadAppointments = async (providerId = null, options = {}) => {
    setIsLoadingAppointments(true);

    try {
      // Determine provider ID intelligently
      const targetProviderId = providerId ||
        (authForm.provider?.userType === 'ADMIN' && selectedProviderId ? parseInt(selectedProviderId) : authForm.provider?.id) ||
        8; // Fallback to Burcot Hall Hospital

      // Smart date range calculation based on mobile/desktop and options
      let startDate = (options as any).startDate;
      let endDate = (options as any).endDate;

      // Auto-calculate date ranges if not provided
      if (!startDate || !endDate) {
        const baseDate = (options as any).currentWeek || currentWeek || new Date();

        if (isMobile) {
          // Mobile: Load current week for performance
          const startOfWeek = new Date(baseDate);
          startOfWeek.setDate(baseDate.getDate() - baseDate.getDay());
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);

          startDate = startOfWeek.toISOString().split('T')[0];
          endDate = endOfWeek.toISOString().split('T')[0];
        } else {
          // Desktop: Load broader range (2 months) for better navigation
          const startRange = new Date(baseDate);
          startRange.setMonth(baseDate.getMonth() - 1);
          const endRange = new Date(baseDate);
          endRange.setMonth(baseDate.getMonth() + 1);

          startDate = startRange.toISOString().split('T')[0];
          endDate = endRange.toISOString().split('T')[0];
        }
      }

      console.log(`📅 Loading appointments: Provider ${targetProviderId}, ${startDate} to ${endDate}, Mobile: ${isMobile}`);

      const appointments = await apiService.getProviderAppointments(targetProviderId, startDate, endDate);
      setLiveAppointments(appointments || []);

    } catch (error) {
      console.error('Error loading appointments:', error);
      showNotification('Failed to load appointments. Please try again.', 'error');
      setLiveAppointments([]);
    } finally {
      setIsLoadingAppointments(false);
    }
  };

  // Load appointments when component mounts with intelligent date ranges (only if authenticated)
  useEffect(() => {
    // Only load appointments if user is authenticated
    if (authForm.provider || (nurseAuth.email && nurseAuth.authenticated)) {
      loadAppointments(null, { currentWeek });
    }
  }, [authForm.provider, nurseAuth.authenticated]); // Dependencies to trigger on auth changes

  // Load medical roles when component mounts (only if authenticated)
  useEffect(() => {
    // Only load medical roles if user is authenticated
    if (authForm.provider || (nurseAuth.email && nurseAuth.authenticated)) {
      loadMedicalRoles();
    }
  }, [authForm.provider, nurseAuth.authenticated]); // Dependencies to trigger on auth changes

  // Load professionals when booking modal opens for admin users
  useEffect(() => {
    const isAdmin = authForm.provider && (authForm.provider.userType === 'ADMIN' || authForm.provider.id === -1);
    if (bookingOpened && isAdmin) {
      loadProfessionals();
    }
  }, [bookingOpened, authForm.provider]);

  // Appointment SSE event handlers (defined after authForm and selectedProviderId)
  const handleAppointmentCreated = useCallback((appointmentData) => {
    console.log('🆕 New appointment created:', appointmentData);

    // Refresh appointments to show the new one
    const isAdmin = authForm.provider && (authForm.provider.userType === 'ADMIN' || authForm.provider.id === -1);
    const shouldReload = !isAdmin || (isAdmin && selectedProviderId);

    if (shouldReload) {
      loadAppointments(null, { currentWeek });
    }

    // Show notification
    showNotification(
      `New shift created: ${appointmentData.data?.location || 'New appointment'}`,
      'success',
      'Appointment Created'
    );
  }, [authForm.provider, selectedProviderId, currentWeek]);

  const handleAppointmentUpdated = useCallback((appointmentData) => {
    console.log('✏️ Appointment updated:', appointmentData);

    // Refresh appointments to show the updates
    const isAdmin = authForm.provider && (authForm.provider.userType === 'ADMIN' || authForm.provider.id === -1);
    const shouldReload = !isAdmin || (isAdmin && selectedProviderId);

    if (shouldReload) {
      loadAppointments(null, { currentWeek });
    }

    // Show notification
    showNotification(
      `Shift updated: ${appointmentData.data?.location || 'Appointment'}`,
      'warning',
      'Appointment Updated'
    );
  }, [authForm.provider, selectedProviderId, currentWeek]);

  const handleAppointmentCancelled = useCallback((appointmentData) => {
    console.log('🗑️ Appointment cancelled:', appointmentData);

    // Refresh appointments to remove the cancelled one
    const isAdmin = authForm.provider && (authForm.provider.userType === 'ADMIN' || authForm.provider.id === -1);
    const shouldReload = !isAdmin || (isAdmin && selectedProviderId);

    if (shouldReload) {
      loadAppointments(null, { currentWeek });
    }

    // Show notification
    showNotification(
      'A shift has been cancelled',
      'error',
      'Appointment Cancelled'
    );
  }, [authForm.provider, selectedProviderId, currentWeek]);

  // Register appointment SSE event listeners (after appointment handlers are defined)
  useEffect(() => {
    console.log('✅ Registering appointment SSE event listeners');

    // Register appointment SSE event listeners
    sseService.on('appointment_created', handleAppointmentCreated);
    sseService.on('appointment_updated', handleAppointmentUpdated);
    sseService.on('appointment_cancelled', handleAppointmentCancelled);

    // Cleanup on unmount
    return () => {
      console.log('🧹 Cleaning up appointment SSE event listeners');
      sseService.off('appointment_created', handleAppointmentCreated);
      sseService.off('appointment_updated', handleAppointmentUpdated);
      sseService.off('appointment_cancelled', handleAppointmentCancelled);
    };
  }, []); // Empty dependency array - run once on mount

  // Initialize messaging when user is authenticated
  useEffect(() => {
    const currentUser = getCurrentUser();
    if (currentUser) {
      console.log('User authenticated for messaging:', currentUser);
      // The messaging initialization will be handled by the MessagingProvider
      // via the initializeMessaging function when the user is available
    }
  }, [authForm.provider, nurseAuth.email]);

  // Load database courses when nurse is authenticated
  useEffect(() => {
    if (nurseAuth.authenticated && nurseAuth.professionalId) {
      loadDatabaseCourses(nurseAuth.professionalId);
    }
  }, [nurseAuth.authenticated, nurseAuth.professionalId]);

  // Load course content when training is restored from session storage (page refresh)
  useEffect(() => {
    if (currentStep === 'training-module' && currentTraining && !currentCourseContent && nurseAuth.authenticated) {
      // Load course content if we have a training ID but no content (likely from page refresh)
      loadCourseContent(currentTraining).catch(console.error);
    }
  }, [currentStep, currentTraining, currentCourseContent, nurseAuth.authenticated]);

  // Load providers list when admin user is authenticated (including on page refresh)
  useEffect(() => {
    const isAdmin = authForm.provider && (authForm.provider.userType === 'ADMIN' || authForm.provider.id === -1);
    if (isAdmin && availableProviders.length === 0) {
      loadProviders();
    }
  }, [authForm.provider, availableProviders.length]);

  // Load appointments when selected provider is restored from localStorage
  useEffect(() => {
    const isAdmin = authForm.provider && (authForm.provider.userType === 'ADMIN' || authForm.provider.id === -1);
    if (isAdmin && selectedProviderId && availableProviders.length > 0) {
      loadAppointments(parseInt(selectedProviderId), { currentWeek });
    }
  }, [authForm.provider, selectedProviderId, availableProviders.length, currentWeek]);

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only handle keyboard navigation if no modals are open
      if (!bookingOpened && !datePickerOpened && !deleteConfirmOpened) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          navigateWeek(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          navigateWeek(1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [bookingOpened, datePickerOpened, deleteConfirmOpened]);

  // Status helper functions
  const getStatusText = (backgroundId) => {
    const statusMap = {
      12: "Searching",
      13: "Confirmed",
      14: "Holiday",
      15: "Invoice Documents Attached",
      16: "Timesheet Validated",
      17: "Invoice Paid",
      18: "Invoice Cancelled"
    };
    return statusMap[backgroundId] || "Unknown";
  };


  // Helper function to determine day/night crossover icons
  const getCrossoverIcon = (booking, currentDate) => {
    const startDateTime = new Date(`${booking.startDate}T${booking.startTime}`);
    const endDateTime = new Date(`${booking.endDate}T${booking.endTime}`);
    const currentDateStr = currentDate.toISOString().split('T')[0];

    const startDateStr = booking.startDate;
    const endDateStr = booking.endDate;
    const startHour = startDateTime.getHours();
    const endHour = endDateTime.getHours();


    // Only show icons for shifts that cross day/night boundaries

    // Case 1: Shift crosses midnight (different dates)
    if (startDateStr !== endDateStr) {
      if (currentDateStr === startDateStr) {
        // Starting day of night shift - show moon
        return <MoonIcon style={{ width: '12px', height: '12px', color: 'var(--mantine-color-blue-6)' }} />;
      }
      if (currentDateStr === endDateStr) {
        // Ending day of night shift - show sun  
        return <SunIcon style={{ width: '12px', height: '12px', color: 'var(--mantine-color-yellow-6)' }} />;
      }
    }

    // Case 2: Same day but long shift crossing day/night hours (e.g., 8AM to 8PM or later)
    if (startDateStr === endDateStr && startDateStr === currentDateStr) {
      const isDayStart = startHour >= 6 && startHour < 18; // 6AM - 6PM
      const isNightEnd = endHour >= 18 || endHour < 6;    // 6PM - 6AM

      // If shift starts in day hours but ends in night hours (or vice versa)
      if (isDayStart && isNightEnd) {
        // Show sun icon to indicate day portion of shift
        return <SunIcon style={{ width: '12px', height: '12px', color: 'var(--mantine-color-yellow-6)' }} />;
      }

      const isNightStart = startHour >= 18 || startHour < 6; // Night start
      const isDayEnd = endHour >= 6 && endHour < 18;        // Day end

      if (isNightStart && isDayEnd) {
        // Show moon icon to indicate night portion of shift
        return <MoonIcon style={{ width: '12px', height: '12px', color: 'var(--mantine-color-blue-6)' }} />;
      }
    }

    return null; // No crossover icon needed
  };

  // Comprehensive booking editing business rules
  const shouldBookingBeReadOnly = (booking, user, currentTime = new Date()) => {
    // Admin users can override most restrictions
    if (user && user.userType === 'ADMIN') {
      return false; // Admin can edit everything
    }

    // Check for non-editable conditions based on business rules:
    // 1. BackgroundID 15-18 (Invoice Documents Attached, Timesheet Validated, Invoice Paid, Invoice Cancelled)
    if (booking.backgroundId >= 15 && booking.backgroundId <= 18) {
      return true; // Cannot edit when invoice/timesheet processing is involved
    }

    // 2. Has invoice or timesheet attached
    if ((booking.invoiceId && booking.invoiceId > 0) || (booking.timesheetId && booking.timesheetId > 0)) {
      return true; // Cannot edit when financial documents are attached
    }

    // 3. Past shifts (end time is before current time)
    const endDateTime = new Date(`${booking.endDate}T${booking.endTime}`);
    if (endDateTime < currentTime) {
      // Exception: Past searching bookings CAN be edited (unfilled shifts)
      if (booking.backgroundId === APPOINTMENT_STATUS.SEARCHING && !booking.officeId) {
        return false; // Can edit past unfilled shifts
      }
      return true; // Cannot edit past assigned bookings
    }

    // 4. Within 4 hours of start time
    if (isWithin4Hours(booking.startDate, booking.startTime, currentTime)) {
      return true; // Cannot edit within 4 hours of start time
    }

    // 5. Hospital portal users cannot edit assigned bookings (staff assignments)
    if (user && user.userType === 'PRO_V') {
      const isAssigned = booking.officeId || booking.backgroundId !== APPOINTMENT_STATUS.SEARCHING;
      if (isAssigned) {
        return true; // Hospital users cannot edit assigned bookings
      }
    }

    return false; // Default to editable
  };

  const getReadOnlyReason = (booking, user, currentTime = new Date()) => {
    if (!shouldBookingBeReadOnly(booking, user, currentTime)) {
      return null; // Not read-only
    }

    // Admin users can override most restrictions
    if (user && user.userType === 'ADMIN') {
      return null;
    }

    const isAssigned = booking.officeId || booking.backgroundId !== APPOINTMENT_STATUS.SEARCHING;

    if (isAssigned) {
      // Check end time
      const endDateTime = new Date(`${booking.endDate}T${booking.endTime}`);
      if (endDateTime < currentTime) {
        return 'End time is in the past';
      }

      // Check start time within 4 hours
      if (isWithin4Hours(booking.startDate, booking.startTime, currentTime)) {
        return 'Start time is within 4 hours';
      }

      // Check if assigned for portal users
      if (user && user.userType === 'PRO_V') {
        return 'Staff is assigned - portal cannot modify assignments';
      }
    }

    // For searching bookings
    if (booking.backgroundId === APPOINTMENT_STATUS.SEARCHING) {
      if (isWithin4Hours(booking.startDate, booking.startTime, currentTime)) {
        return 'Start time is within 4 hours';
      }
    }

    return 'Booking cannot be edited';
  };

  const canUserAssignStaff = (user) => {
    return user && user.userType === 'ADMIN';
  };

  const canUserEditAssignedBooking = (booking, user) => {
    return user && user.userType === 'ADMIN';
  };

  const canUserEditPastUnfilledShift = (booking, user, currentTime = new Date()) => {
    if (booking.backgroundId !== APPOINTMENT_STATUS.SEARCHING || booking.officeId) {
      return false; // Not an unfilled shift
    }

    const startDateTime = new Date(`${booking.startDate}T${booking.startTime}`);
    return startDateTime < currentTime; // Can edit if it's in the past
  };

  const getUserRole = (user) => {
    return user && user.userType === 'ADMIN' ? 'master' : 'slave';
  };

  const getReadOnlyConfiguration = (booking, user, currentTime = new Date()) => {
    const isReadOnly = shouldBookingBeReadOnly(booking, user, currentTime);
    const isPortalUser = user && user.userType === 'PRO_V';

    return {
      summaryDisabled: isReadOnly,
      locationDisabled: isReadOnly,
      startDateDisabled: isReadOnly,
      startTimeDisabled: isReadOnly,
      endDateDisabled: isReadOnly,
      endTimeDisabled: isReadOnly,
      staffDropdownDisabled: isPortalUser, // Always disabled for portal users
      notesDisabled: false, // Notes always editable for change requests
      showDeleteButton: !isReadOnly,
      showSaveButton: !isReadOnly
    };
  };

  // Enhanced isWithin4Hours function to accept currentTime parameter
  const isWithin4Hours = (dateStr, timeStr, currentTime = new Date()) => {
    const shiftDateTime = new Date(`${dateStr}T${timeStr}`);
    const timeDifference = shiftDateTime.getTime() - currentTime.getTime();
    const hoursDifference = timeDifference / (1000 * 60 * 60);
    return hoursDifference <= 4 && hoursDifference >= 0;
  };

  // Legacy function for backward compatibility
  const canEditShift = (booking) => {
    const user = authForm.provider || { userType: 'PRO_V' };
    return !shouldBookingBeReadOnly(booking, user);
  };

  const canDeleteShift = (booking) => {
    const user = authForm.provider || { userType: 'PRO_V' };
    // Admin users can always delete regardless of other rules
    if (user && user.userType === 'ADMIN') {
      return true;
    }
    // For non-admin users, use the same rules as editing
    return canEditShift(booking);
  };

  // Get bookings for a specific date (live appointments only)
  const getBookingsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];

    console.log('getBookingsForDate called for:', dateStr);
    console.log('liveAppointments count:', liveAppointments?.length);

    // For admin users, don't show live appointments until they select a provider
    const isAdmin = authForm.provider && (authForm.provider.userType === 'ADMIN' || authForm.provider.id === -1);
    const shouldShowLiveAppointments = !isAdmin || (isAdmin && selectedProviderId);

    console.log('Admin check:', { isAdmin, selectedProviderId, shouldShowLiveAppointments });

    // Get live appointments for this date (only if allowed)
    const liveAppointmentsForDate = shouldShowLiveAppointments ? (liveAppointments || []).filter(appointment => {
      // Handle the actual API structure with "start" and "end" fields
      const startDateStr = appointment.start || appointment.date || appointment.appointmentDate || appointment.startDate;
      const endDateStr = appointment.end;
      if (!startDateStr || !endDateStr) {
        console.log('Missing date fields:', appointment);
        return false;
      }

      try {
        const appointmentStartDate = new Date(startDateStr).toISOString().split('T')[0];
        const appointmentEndDate = new Date(endDateStr).toISOString().split('T')[0];

        const isMatch = appointmentStartDate <= dateStr && dateStr <= appointmentEndDate;
        console.log('Date filter check:', {
          appointmentId: appointment.id,
          appointmentStartDate,
          appointmentEndDate,
          requestedDate: dateStr,
          isMatch
        });

        // Include appointment if current date is between start and end date (inclusive)
        return isMatch;
      } catch (error) {
        console.warn('Invalid date format:', startDateStr, endDateStr);
        return false;
      }
    }).map(appointment => {
      const startDate = new Date(appointment.start);
      const endDate = new Date(appointment.end);

      // Map BackgroundId to status (ensure number comparison)
      const backgroundId = Number(appointment.backgroundId);
      let status = 'Unknown';
      if (backgroundId === APPOINTMENT_STATUS.SEARCHING) {
        status = 'Searching...';
      } else if (backgroundId === APPOINTMENT_STATUS.CONFIRMED) {
        status = 'Confirmed';
      } else if (backgroundId === APPOINTMENT_STATUS.HOLIDAY) {
        status = 'Holiday';
      } else if (backgroundId === APPOINTMENT_STATUS.INVOICE_DOCUMENTS_ATTACHED) {
        status = 'Invoice Documents Attached';
      } else if (backgroundId === APPOINTMENT_STATUS.TIMESHEET_VALIDATED) {
        status = 'Timesheet Validated';
      } else if (backgroundId === APPOINTMENT_STATUS.INVOICE_PAID) {
        status = 'Invoice Paid';
      } else if (backgroundId === APPOINTMENT_STATUS.INVOICE_CANCELLED) {
        status = 'Invoice Cancelled';
      }

      return {
        id: `live-${appointment.id}`,
        appointmentId: appointment.id,  // Database ID for deletion
        title: appointment.summary || status,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        startTime: startDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        endTime: endDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        specialization: appointment.location || 'General',
        status: status,
        isLive: true,
        invoiceId: appointment.invoiceId,
        timesheetId: appointment.timesheetId,
        officeId: appointment.officeId,
        assignedStaffId: appointment.officeId ? appointment.officeId.toString() : '',
        backgroundId: backgroundId,
        notes: appointment.notes || ''
      };
    }) : [];

    // Sort by start time (earliest first)
    return liveAppointmentsForDate.sort((a, b) => {
      const timeA = a.startTime; // Format: "HH:MM"
      const timeB = b.startTime;

      // Convert time strings to comparable numbers (HHMM format)
      const getTimeNumber = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 100 + minutes;
      };

      return getTimeNumber(timeA) - getTimeNumber(timeB);
    });


    return liveAppointmentsForDate;
  };

  // Certificate Generation Functions
  const generateCertificateForViewing = async (courseId, courseTitle, completionData) => {
    // Generate certificate PDF without downloading (for viewing)
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Certificate dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Professional color palette
    const colors = {
      primary: [0, 94, 184] as [number, number, number],      // NHS Blue
      gold: [255, 193, 7] as [number, number, number],        // Gold accent
      darkGold: [255, 160, 0] as [number, number, number],    // Darker gold
      lightBlue: [232, 244, 253] as [number, number, number], // Light blue
      darkText: [33, 37, 41] as [number, number, number],     // Dark gray
      mediumText: [108, 117, 125] as [number, number, number], // Medium gray
      lightGray: [248, 249, 250] as [number, number, number], // Light gray
      white: [255, 255, 255] as [number, number, number],     // White
      success: [40, 167, 69] as [number, number, number]      // Success green
    };

    // Elegant background with subtle gradient effect
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');

    // Decorative header with gradient-like effect
    pdf.setFillColor(0, 94, 184);
    pdf.rect(0, 0, pageWidth, 25, 'F');
    pdf.setFillColor(25, 118, 210);
    pdf.rect(0, 20, pageWidth, 5, 'F');

    // Professional border frame
    pdf.setDrawColor(0, 94, 184);
    pdf.setLineWidth(3);
    pdf.rect(8, 8, pageWidth - 16, pageHeight - 16);

    // Inner elegant border
    pdf.setDrawColor(255, 193, 7);
    pdf.setLineWidth(1.5);
    pdf.rect(12, 12, pageWidth - 24, pageHeight - 24);

    // Decorative corner elements
    const cornerSize = 15;
    pdf.setFillColor(255, 193, 7);
    // Top left corner
    pdf.rect(12, 12, cornerSize, 3, 'F');
    pdf.rect(12, 12, 3, cornerSize, 'F');
    // Top right corner
    pdf.rect(pageWidth - 12 - cornerSize, 12, cornerSize, 3, 'F');
    pdf.rect(pageWidth - 15, 12, 3, cornerSize, 'F');
    // Bottom left corner
    pdf.rect(12, pageHeight - 15, cornerSize, 3, 'F');
    pdf.rect(12, pageHeight - 12 - cornerSize, 3, cornerSize, 'F');
    // Bottom right corner
    pdf.rect(pageWidth - 12 - cornerSize, pageHeight - 15, cornerSize, 3, 'F');
    pdf.rect(pageWidth - 15, pageHeight - 12 - cornerSize, 3, cornerSize, 'F');

    // Elegant title with enhanced typography
    pdf.setTextColor(0, 94, 184);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(28);
    pdf.text('CERTIFICATE OF COMPLETION', pageWidth / 2, 45, { align: 'center' });

    // Professional subtitle with spacing
    pdf.setTextColor(108, 117, 125);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(16);
    pdf.text('Professional Healthcare Training Programme', pageWidth / 2, 55, { align: 'center' });

    // Decorative ornamental line
    pdf.setFillColor(255, 193, 7);
    const lineY = 62;
    pdf.rect(pageWidth / 2 - 50, lineY, 100, 2, 'F');
    pdf.setFillColor(255, 160, 0);
    pdf.rect(pageWidth / 2 - 30, lineY - 1, 60, 4, 'F');

    // Elegant certification text
    pdf.setTextColor(33, 37, 41);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(14);
    pdf.text('This is to certify that', pageWidth / 2, 80, { align: 'center' });

    // Prominent name with decorative styling
    pdf.setFont('times', 'bold');
    pdf.setFontSize(26);
    pdf.setTextColor(0, 94, 184);
    const nurseName = `${nurseProfile.firstName || 'Professional'} ${nurseProfile.lastName || 'User'}`.trim();
    pdf.text(nurseName, pageWidth / 2, 95, { align: 'center' });

    // Underline for name
    const nameWidth = pdf.getTextWidth(nurseName);
    pdf.setDrawColor(255, 193, 7);
    pdf.setLineWidth(2);
    pdf.line(pageWidth / 2 - nameWidth / 2 - 10, 98, pageWidth / 2 + nameWidth / 2 + 10, 98);

    // Course completion description
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(14);
    pdf.setTextColor(33, 37, 41);
    pdf.text('has successfully completed the training course', pageWidth / 2, 110, { align: 'center' });

    // Course title with emphasis
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(20);
    pdf.setTextColor(0, 94, 184);
    pdf.text(courseTitle || 'Professional Training Course', pageWidth / 2, 125, { align: 'center' });

    // Achievement details with visual emphasis
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(14);
    pdf.setTextColor(40, 167, 69);
    pdf.text(`with a score of ${completionData.score || 'N/A'}%`, pageWidth / 2, 138, { align: 'center' });

    pdf.setTextColor(33, 37, 41);
    pdf.text('meeting all regulatory compliance requirements', pageWidth / 2, 148, { align: 'center' });

    // Elegant certificate details box
    const detailsY = 160;
    const boxHeight = 35;

    // Elegant details box with gradient-like effect
    pdf.setFillColor(248, 249, 250);
    pdf.rect(25, detailsY, pageWidth - 50, boxHeight, 'F');

    // Decorative border for details box
    pdf.setDrawColor(255, 193, 7);
    pdf.setLineWidth(1.5);
    pdf.rect(25, detailsY, pageWidth - 50, boxHeight);

    // Inner shadow effect
    pdf.setDrawColor(0, 94, 184);
    pdf.setLineWidth(0.5);
    pdf.rect(26, detailsY + 1, pageWidth - 52, boxHeight - 2);

    // Certificate details with improved typography
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(108, 117, 125);

    const leftColumn = 35;
    const rightColumn = pageWidth / 2 + 25;

    // Left column labels
    pdf.text('Certificate ID:', leftColumn, detailsY + 10);
    pdf.text('Completion Date:', leftColumn, detailsY + 20);
    pdf.text('Valid Until:', leftColumn, detailsY + 30);

    // Right column labels
    pdf.text('Training Role:', rightColumn, detailsY + 10);
    pdf.text('Registration Number:', rightColumn, detailsY + 20);
    pdf.text('IntaLearn ID:', rightColumn, detailsY + 30);

    // Values with enhanced styling
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(33, 37, 41);

    pdf.text(completionData.certificateId || 'N/A', leftColumn + 38, detailsY + 10);
    pdf.text(new Date(completionData.completionDate).toLocaleDateString('en-GB'), leftColumn + 38, detailsY + 20);
    pdf.text(new Date(completionData.expiryDate).toLocaleDateString('en-GB'), leftColumn + 38, detailsY + 30);

    pdf.text(nurseProfile.nursingRole || 'Healthcare Professional', rightColumn + 43, detailsY + 10);
    pdf.text(nurseProfile.registrationNumber || 'N/A', rightColumn + 43, detailsY + 20);
    pdf.text(nurseAuth.email || 'N/A', rightColumn + 43, detailsY + 30);

    // Generate elegant QR code for verification
    const verificationUrl = `https://intastream.com/verify/${completionData.certificateId}`;
    const qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
      width: 300,
      margin: 3,
      color: {
        dark: '#005EB8',
        light: '#FFFFFF'
      }
    });

    // QR code with elegant frame
    const qrSize = 30;
    const qrX = pageWidth - 70;
    const qrY = detailsY + 5;

    // QR code background frame
    pdf.setFillColor(255, 255, 255);
    pdf.rect(qrX - 2, qrY - 2, qrSize + 4, qrSize + 4, 'F');
    pdf.setDrawColor(255, 193, 7);
    pdf.setLineWidth(1);
    pdf.rect(qrX - 2, qrY - 2, qrSize + 4, qrSize + 4);

    // Add QR code
    pdf.addImage(qrCodeDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

    // QR code label with enhanced styling
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(0, 94, 184);
    pdf.text('Scan to verify', qrX + qrSize / 2, qrY + qrSize + 8, { align: 'center' });

    // Add signature lines for authenticity
    const sigY = pageHeight - 50;
    pdf.setDrawColor(33, 37, 41);
    pdf.setLineWidth(0.5);

    // Director signature line
    pdf.line(50, sigY, 120, sigY);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(108, 117, 125);
    pdf.text('Training Director', 85, sigY + 5, { align: 'center' });

    // Date line
    pdf.line(pageWidth - 120, sigY, pageWidth - 50, sigY);
    pdf.text('Date of Issue', pageWidth - 85, sigY + 5, { align: 'center' });

    // Elegant footer with enhanced design
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(0, 94, 184);
    pdf.text('IntaLearn', pageWidth / 2, pageHeight - 30, { align: 'center' });

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(108, 117, 125);
    pdf.text('Professional Healthcare Training Solutions', pageWidth / 2, pageHeight - 25, { align: 'center' });
    pdf.text('This certificate is valid and verifiable through the IntaLearn system', pageWidth / 2, pageHeight - 20, { align: 'center' });

    // Date issued with elegant formatting
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(9);
    pdf.setTextColor(108, 117, 125);
    pdf.text(`Certificate issued on: ${new Date().toLocaleDateString('en-GB')} | Certificate ID: ${completionData.certificateId || 'N/A'}`, pageWidth / 2, pageHeight - 12, { align: 'center' });

    return pdf;
  };

  const generateCertificate = async (courseId, courseTitle, completionData) => {
    setGeneratingCertificate(true);

    try {
      // Create PDF document
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // Certificate dimensions
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Professional color palette
      const colors = {
        primary: [0, 94, 184] as [number, number, number],      // NHS Blue
        gold: [255, 193, 7] as [number, number, number],        // Gold accent
        darkGold: [255, 160, 0] as [number, number, number],    // Darker gold
        lightBlue: [232, 244, 253] as [number, number, number], // Light blue
        darkText: [33, 37, 41] as [number, number, number],     // Dark gray
        mediumText: [108, 117, 125] as [number, number, number], // Medium gray
        lightGray: [248, 249, 250] as [number, number, number], // Light gray
        white: [255, 255, 255] as [number, number, number],     // White
        success: [40, 167, 69] as [number, number, number]      // Success green
      };

      // Elegant background with subtle gradient effect
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');

      // Decorative header with gradient-like effect
      pdf.setFillColor(0, 94, 184);
      pdf.rect(0, 0, pageWidth, 25, 'F');
      pdf.setFillColor(25, 118, 210);
      pdf.rect(0, 20, pageWidth, 5, 'F');

      // Professional border frame
      pdf.setDrawColor(0, 94, 184);
      pdf.setLineWidth(3);
      pdf.rect(8, 8, pageWidth - 16, pageHeight - 16);

      // Inner elegant border
      pdf.setDrawColor(255, 193, 7);
      pdf.setLineWidth(1.5);
      pdf.rect(12, 12, pageWidth - 24, pageHeight - 24);

      // Decorative corner elements
      const cornerSize = 15;
      pdf.setFillColor(255, 193, 7);
      // Top left corner
      pdf.rect(12, 12, cornerSize, 3, 'F');
      pdf.rect(12, 12, 3, cornerSize, 'F');
      // Top right corner
      pdf.rect(pageWidth - 12 - cornerSize, 12, cornerSize, 3, 'F');
      pdf.rect(pageWidth - 15, 12, 3, cornerSize, 'F');
      // Bottom left corner
      pdf.rect(12, pageHeight - 15, cornerSize, 3, 'F');
      pdf.rect(12, pageHeight - 12 - cornerSize, 3, cornerSize, 'F');
      // Bottom right corner
      pdf.rect(pageWidth - 12 - cornerSize, pageHeight - 15, cornerSize, 3, 'F');
      pdf.rect(pageWidth - 15, pageHeight - 12 - cornerSize, 3, cornerSize, 'F');

      // Elegant title with enhanced typography
      pdf.setTextColor(0, 94, 184);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(28);
      pdf.text('CERTIFICATE OF COMPLETION', pageWidth / 2, 45, { align: 'center' });

      // Professional subtitle with spacing
      pdf.setTextColor(108, 117, 125);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(16);
      pdf.text('Professional Healthcare Training Programme', pageWidth / 2, 55, { align: 'center' });

      // Decorative ornamental line
      pdf.setFillColor(255, 193, 7);
      const lineY = 62;
      pdf.rect(pageWidth / 2 - 50, lineY, 100, 2, 'F');
      pdf.setFillColor(255, 160, 0);
      pdf.rect(pageWidth / 2 - 30, lineY - 1, 60, 4, 'F');

      // Elegant certification text
      pdf.setTextColor(33, 37, 41);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(14);
      pdf.text('This is to certify that', pageWidth / 2, 80, { align: 'center' });

      // Prominent name with decorative styling
      pdf.setFont('times', 'bold');
      pdf.setFontSize(26);
      pdf.setTextColor(0, 94, 184);
      const nurseName = `${nurseProfile.firstName || 'Professional'} ${nurseProfile.lastName || 'User'}`.trim();
      pdf.text(nurseName, pageWidth / 2, 95, { align: 'center' });

      // Underline for name
      const nameWidth = pdf.getTextWidth(nurseName);
      pdf.setDrawColor(255, 193, 7);
      pdf.setLineWidth(2);
      pdf.line(pageWidth / 2 - nameWidth / 2 - 10, 98, pageWidth / 2 + nameWidth / 2 + 10, 98);

      // Course completion description
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(14);
      pdf.setTextColor(33, 37, 41);
      pdf.text('has successfully completed the training course', pageWidth / 2, 110, { align: 'center' });

      // Course title with emphasis
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(20);
      pdf.setTextColor(0, 94, 184);
      pdf.text(courseTitle || 'Professional Training Course', pageWidth / 2, 125, { align: 'center' });

      // Achievement details with visual emphasis
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(14);
      pdf.setTextColor(40, 167, 69);
      pdf.text(`with a score of ${completionData.score || 'N/A'}%`, pageWidth / 2, 138, { align: 'center' });

      pdf.setTextColor(33, 37, 41);
      pdf.text('meeting all regulatory compliance requirements', pageWidth / 2, 148, { align: 'center' });

      // Elegant certificate details box
      const detailsY = 160;
      const boxHeight = 35;

      // Elegant details box with gradient-like effect
      pdf.setFillColor(248, 249, 250);
      pdf.rect(25, detailsY, pageWidth - 50, boxHeight, 'F');

      // Decorative border for details box
      pdf.setDrawColor(255, 193, 7);
      pdf.setLineWidth(1.5);
      pdf.rect(25, detailsY, pageWidth - 50, boxHeight);

      // Inner shadow effect
      pdf.setDrawColor(0, 94, 184);
      pdf.setLineWidth(0.5);
      pdf.rect(26, detailsY + 1, pageWidth - 52, boxHeight - 2);

      // Certificate details with improved typography
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(108, 117, 125);

      const leftColumn = 35;
      const rightColumn = pageWidth / 2 + 25;
      const labelSpacing = 10;

      // Left column labels
      pdf.text('Certificate ID:', leftColumn, detailsY + 10);
      pdf.text('Completion Date:', leftColumn, detailsY + 20);
      pdf.text('Valid Until:', leftColumn, detailsY + 30);

      // Right column labels
      pdf.text('Training Role:', rightColumn, detailsY + 10);
      pdf.text('Registration Number:', rightColumn, detailsY + 20);
      pdf.text('IntaLearn ID:', rightColumn, detailsY + 30);

      // Values with enhanced styling
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(33, 37, 41);

      pdf.text(completionData.certificateId || 'N/A', leftColumn + 38, detailsY + 10);
      pdf.text(new Date(completionData.completionDate).toLocaleDateString('en-GB'), leftColumn + 38, detailsY + 20);
      pdf.text(new Date(completionData.expiryDate).toLocaleDateString('en-GB'), leftColumn + 38, detailsY + 30);

      pdf.text(nurseProfile.nursingRole || 'Healthcare Professional', rightColumn + 43, detailsY + 10);
      pdf.text(nurseProfile.registrationNumber || 'N/A', rightColumn + 43, detailsY + 20);
      pdf.text(nurseAuth.email || 'N/A', rightColumn + 43, detailsY + 30);

      // Generate elegant QR code for verification
      const verificationUrl = `https://intastream.com/verify/${completionData.certificateId}`;
      const qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
        width: 300,
        margin: 3,
        color: {
          dark: '#005EB8',
          light: '#FFFFFF'
        }
      });

      // QR code with elegant frame
      const qrSize = 30;
      const qrX = pageWidth - 70;
      const qrY = detailsY + 5;

      // QR code background frame
      pdf.setFillColor(255, 255, 255);
      pdf.rect(qrX - 2, qrY - 2, qrSize + 4, qrSize + 4, 'F');
      pdf.setDrawColor(255, 193, 7);
      pdf.setLineWidth(1);
      pdf.rect(qrX - 2, qrY - 2, qrSize + 4, qrSize + 4);

      // Add QR code
      pdf.addImage(qrCodeDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

      // QR code label with enhanced styling
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(0, 94, 184);
      pdf.text('Scan to verify', qrX + qrSize / 2, qrY + qrSize + 8, { align: 'center' });

      // Add signature lines for authenticity
      const sigY = pageHeight - 50;
      pdf.setDrawColor(33, 37, 41);
      pdf.setLineWidth(0.5);

      // Director signature line
      pdf.line(50, sigY, 120, sigY);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(108, 117, 125);
      pdf.text('Training Director', 85, sigY + 5, { align: 'center' });

      // Date line
      pdf.line(pageWidth - 120, sigY, pageWidth - 50, sigY);
      pdf.text('Date of Issue', pageWidth - 85, sigY + 5, { align: 'center' });

      // Elegant footer with enhanced design
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(0, 94, 184);
      pdf.text('IntaLearn', pageWidth / 2, pageHeight - 30, { align: 'center' });

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(108, 117, 125);
      pdf.text('Professional Healthcare Training Solutions', pageWidth / 2, pageHeight - 25, { align: 'center' });
      pdf.text('This certificate is valid and verifiable through the IntaLearn system', pageWidth / 2, pageHeight - 20, { align: 'center' });

      // Date issued with elegant formatting
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(9);
      pdf.setTextColor(108, 117, 125);
      pdf.text(`Certificate issued on: ${new Date().toLocaleDateString('en-GB')} | Certificate ID: ${completionData.certificateId || 'N/A'}`, pageWidth / 2, pageHeight - 12, { align: 'center' });

      // Generate unique filename
      const filename = `${completionData.certificateId}_${nurseName.replace(/\s+/g, '_')}_Certificate.pdf`;

      // Save PDF data
      const pdfData = pdf.output('datauristring');

      // Store certificate data
      const certificateData = {
        professionalId: nurseAuth.professionalId,
        courseId: courseId,
        certificateId: completionData.certificateId,
        courseTitle: courseTitle,
        completionDate: completionData.completionDate,
        expiryDate: completionData.expiryDate,
        score: completionData.score,
        filename: filename,
        pdfData: pdfData
      };


      // Download PDF
      pdf.save(filename);

      return certificateData;

    } catch (error) {
      console.error('Error generating certificate:', error);
      showNotification('Error generating certificate. Please try again.', 'error');
      return null;
    } finally {
      setGeneratingCertificate(false);
    }
  };

  // Certificate management functions - now database-driven
  const downloadCertificate = async (certificateId) => {
    try {
      // Get the certificate data from the certificates list
      const certificate = await getCertificates();
      const cert = certificate.find(c => c.certificateNumber === certificateId);

      if (!cert) {
        showNotification('Certificate not found', 'error');
        return;
      }

      // Regenerate certificate PDF
      const completionData = {
        certificateId: cert.certificateNumber,
        completionDate: cert.completionDate,
        expiryDate: cert.expiryDate,
        score: cert.score
      };

      await generateCertificate(cert.courseId, cert.courseName, completionData);

      showNotification('Certificate downloaded successfully.', 'success');
    } catch (error) {
      console.error('Failed to download certificate:', error);
      showNotification('Failed to download certificate. Please try again.', 'error');
    }
  };

  const viewCertificate = async (certificateId) => {
    try {
      // Get the certificate data from the certificates list
      const certificate = await getCertificates();
      const cert = certificate.find(c => c.certificateNumber === certificateId);

      if (!cert) {
        showNotification('Certificate not found', 'error');
        return;
      }

      // Generate certificate PDF in memory
      const completionData = {
        certificateId: cert.certificateNumber,
        completionDate: cert.completionDate,
        expiryDate: cert.expiryDate,
        score: cert.score
      };

      // Generate PDF without downloading
      const certificateData = await generateCertificateForViewing(cert.courseId, cert.courseName, completionData);

      // Create blob from the PDF data that was generated
      const pdfBlob = certificateData.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');

      // Clean up the URL after opening
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Failed to view certificate:', error);
      showNotification('Failed to view certificate. Please try again.', 'error');
    }
  };

  const deleteCertificate = async (certificateId) => {
    try {
      const certificate = await apiService.getCertificate(certificateId);

      showConfirmation(`Are you sure you want to delete the certificate for "${certificate.courseTitle}"?`, async () => {
        try {
          await apiService.deleteCertificate(certificateId);

          showNotification('Certificate deleted successfully.', 'success');

          // Refresh certificate list
          const updatedCerts = await getCertificates();
          // This will trigger a re-render of the CertificateGallery component
        } catch (error) {
          console.error('Failed to delete certificate:', error);
          showNotification('Failed to delete certificate. Please try again.', 'error');
        }
      });
    } catch (error) {
      console.error('Failed to delete certificate:', error);
      showNotification('Failed to delete certificate. Please try again.', 'error');
    }
  };

  const getCertificates = async () => {
    try {
      if (!nurseAuth.professionalId) {
        return [];
      }

      const certificates = await apiService.getProfessionalCertificates(nurseAuth.professionalId);

      // If certificates is null, return empty array to prevent infinite loop
      if (!certificates) {
        console.log('Certificate API returned null - no certificates available');
        return [];
      }

      // Check if empty array and try to ensure certificates exist (but only once)
      if (certificates.length === 0) {
        try {
          await ensureCompletedCoursesHaveCertificates();
        } catch (certError) {
          console.error('Failed to ensure certificates exist:', certError);
        }
        // Don't retry the API call to prevent infinite loop
        return [];
      }

      return certificates;
    } catch (error) {
      console.error('Failed to load certificates:', error);
      return [];
    }
  };

  const ensureCompletedCoursesHaveCertificates = async () => {
    try {
      const progress = await apiService.getTrainingProgressForProfessional(nurseAuth.professionalId);
      const completedCourses = (progress as any).filter((p: any) => p.status === 'Completed' && p.passed && !p.certificateIssued);

      for (const course of completedCourses) {
        const certificateId = `${course.courseCode || 'CERT'}-${Date.now()}-${nurseAuth.email.split('@')[0]}`;
        try {
          await (apiService as any).storeCertificate({
            professionalId: nurseAuth.professionalId,
            courseId: course.courseId,
            certificateId: certificateId,
            courseTitle: course.courseName,
            completionDate: course.completionDate,
            expiryDate: course.expiryDate,
            score: course.latestScore,
            filename: `${certificateId}_Certificate.pdf`,
            pdfData: ''
          });
        } catch (error) {
          console.error('Failed to issue certificate for completed course:', error);
        }
      }
    } catch (error) {
      console.error('Failed to ensure certificates for completed courses:', error);
    }
  };

  // Certificate Gallery Component
  const CertificateGallery = () => {
    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadCertificates = async () => {
        setLoading(true);
        try {
          const certs = await getCertificates();
          setCertificates(certs);
        } catch (error) {
          console.error('Failed to load certificates:', error);
        } finally {
          setLoading(false);
        }
      };

      if (nurseAuth.professionalId) {
        loadCertificates();
      } else {
        setLoading(false);
      }
    }, [nurseAuth.professionalId]);

    if (loading) {
      return (
        <Grid.Col span={12}>
          <Paper p="lg" withBorder radius="md">
            <Text size="lg" fw={600} mb="xs">📜 My Certificates</Text>
            <Loader size="sm" />
          </Paper>
        </Grid.Col>
      );
    }

    if (!certificates || certificates.length === 0) {
      return (
        <Grid.Col span={12}>
          <Paper p="lg" withBorder radius="md">
            <Text size="lg" fw={600} mb="xs">📜 My Certificates</Text>
            <Text c="dimmed" mb="lg">Complete training courses to earn certificates</Text>
            <Text size="sm" c="dimmed">
              Your training certificates will appear here once you complete courses and assessments.
            </Text>
          </Paper>
        </Grid.Col>
      );
    }

    return (
      <Grid.Col span={12}>
        <Paper p="lg" withBorder radius="md">
          <Text size="lg" fw={600} mb="xs">📜 My Certificates</Text>
          <Text c="dimmed" mb="lg">Your completed training certificates</Text>

          <Grid>
            {certificates.map((certificate, index) => (
              <Grid.Col key={certificate.certificateNumber || certificate.id || `cert-${index}`} span={{ base: 12, sm: 6, md: 4 }}>
                <Paper p="md" withBorder radius="md" style={{ height: '100%' }}>
                  <Stack gap="sm">
                    <Text fw={600} size="sm" lineClamp={2}>
                      {certificate.courseTitle}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Completed: {new Date(certificate.completionDate).toLocaleDateString('en-GB')}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Score: {certificate.score}%
                    </Text>
                    <Text size="xs" c="dimmed">
                      Valid until: {new Date(certificate.expiryDate).toLocaleDateString('en-GB')}
                    </Text>
                    <Group gap="xs" mt="auto">
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => viewCertificate(certificate.certificateNumber)}
                      >
                        <EyeIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                        View
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => downloadCertificate(certificate.certificateNumber)}
                      >
                        <DocumentArrowDownIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                        Download
                      </Button>
                      <ActionIcon
                        size="sm"
                        variant="light"
                        color="red"
                        onClick={() => deleteCertificate(certificate.id)}
                      >
                        <TrashIcon style={{ width: '14px', height: '14px' }} />
                      </ActionIcon>
                    </Group>
                  </Stack>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>
        </Paper>
      </Grid.Col>
    );
  };

  // Logout handlers for different auth types
  const handleNurseLogout = () => {
    console.log('🚪 Nurse logout button clicked');

    showConfirmation('Are you sure you want to logout from IntaLearn?', () => {
      console.log('✅ Logout confirmed, proceeding with cleanup');

      // Call API logout to clear httpOnly authentication cookie
      apiService.logout().catch(error => {
        console.warn('Logout API call failed:', error);
      });

      // Disconnect SSE service to prevent connection issues
      sseService.disconnect();

      // Clear only nurse-specific data
      localStorage.removeItem(STORAGE_KEYS.NURSE_AUTH);
      localStorage.removeItem(STORAGE_KEYS.NURSE_PROFILE);

      // Clear tab session
      sessionStorage.removeItem(STORAGE_KEYS.TAB_CURRENT_STEP);
      sessionStorage.removeItem(STORAGE_KEYS.TAB_AUTH_TYPE);

      // Reset nurse state and return to nurse login
      setNurseAuth({ authenticated: false });
      setNurseProfile({});
      setCurrentTraining(null);
      // Clear training state on logout (UI state only)
      setCurrentSection(0);
      setAssessmentAnswers({});
      setAssessmentComplete(false);
      setAssessmentScore(0);

      // Reset authForm state to prevent cross-system contamination
      setAuthForm({
        username: '',
        password: '',
        termsAccepted: false,
        isAuthenticating: false,
        provider: null
      });

      navigateToStep('nurse-auth');
    }, () => {
      console.log('❌ Logout cancelled by user');
    });
  };

  const handleProviderLogout = () => {
    showConfirmation('Are you sure you want to logout from IntaSchedule?', () => {
      // Call API logout to clear httpOnly authentication cookie
      apiService.logout().catch(error => {
        console.warn('Logout API call failed:', error);
      });

      // Clear provider-specific data from both localStorage and sessionStorage
      localStorage.removeItem(STORAGE_KEYS.AUTH);
      sessionStorage.removeItem(STORAGE_KEYS.AUTH);
      localStorage.removeItem(STORAGE_KEYS.SELECTED_PROVIDER);

      // Clear tab session
      sessionStorage.removeItem(STORAGE_KEYS.TAB_CURRENT_STEP);
      sessionStorage.removeItem(STORAGE_KEYS.TAB_AUTH_TYPE);

      // Reset authForm state to prevent cross-system contamination
      setAuthForm({
        username: '',
        password: '',
        termsAccepted: false,
        isAuthenticating: false,
        provider: null
      });

      // Reset provider state and return to provider login
      navigateToStep('initial-auth');
    });
  };

  const handleRegistrationLogout = () => {
    showConfirmation('Are you sure you want to exit IntaProfile? Unsaved progress will be lost.', () => {
      // Clear registration-specific data
      localStorage.removeItem(STORAGE_KEYS.REGISTRATION_SESSION);
      localStorage.removeItem(STORAGE_KEYS.REGISTRATION_AUTH);

      // Clear tab session
      sessionStorage.removeItem(STORAGE_KEYS.TAB_CURRENT_STEP);
      sessionStorage.removeItem(STORAGE_KEYS.TAB_AUTH_TYPE);

      // Reset registration state and return to registration start
      setRegistrationForm(prev => ({ ...prev, currentStep: 'welcome' }));
      setRegistrationData({} as any);
      navigateToStep('staff-registration');
    });
  };

  // Get dynamic header text based on current step and context
  const getHeaderText = () => {
    switch (currentStep) {
      case 'landing':
        return 'Welcome To The IntaStream Portal';
      case 'initial-auth':
        return 'IntaSchedule Login';
      case 'scheduler':
      case 'booking-editor':
        // Get provider name from tab-specific auth state
        const savedAuth = loadFromStorage(STORAGE_KEYS.AUTH, { provider: null }, true); // Use sessionStorage
        if (savedAuth.provider) {
          // Check if this is an admin user by userType, not isAdminContext flag
          const isAdmin = savedAuth.provider.userType === 'ADMIN' || savedAuth.provider.id === -1;

          if (isAdmin) {
            // Admin user - show selected provider or generic
            if (selectedProviderId) {
              const selectedProvider = availableProviders.find(p => p.ProviderId.toString() === selectedProviderId);
              return selectedProvider ? `IntaSchedule (${selectedProvider.Description})` : 'IntaSchedule (Admin)';
            }
            return 'IntaSchedule (Admin)';
          } else {
            // Regular hospital user
            const hospitalName = savedAuth.provider.description ||
              savedAuth.provider.fullName ||
              'Burcot Hall Hospital';
            return `IntaSchedule (${hospitalName})`;
          }
        }
        return 'IntaSchedule';
      case 'nurse-auth':
        return 'IntaLearn Login';
      case 'nurse-dashboard':
      case 'training-module':
        return 'IntaLearn';
      case 'staff-registration':
        return 'IntaProfile';
      default:
        return 'IntaStream Hub';
    }
  };

  // Get logged-in user display info
  const getLoggedInUserInfo = () => {
    // Handle scheduler and booking contexts
    if (currentStep === 'scheduler' || currentStep === 'booking-editor') {
      if (authForm.provider) {
        console.log('=== EMAIL DEBUG - getLoggedInUserInfo ===');
        console.log('authForm.provider object:', authForm.provider);
        console.log('authForm.provider.email:', authForm.provider.email);
        console.log('authForm.provider.username:', authForm.provider.username);
        console.log('authForm.provider.userType:', authForm.provider.userType);
        console.log('authForm.provider.fullName:', authForm.provider.fullName);

        // Check if this is an admin user by userType from stored procedure
        const isAdmin = authForm.provider.userType === 'ADMIN';

        if (isAdmin) {
          console.log('=== ADMIN USER EMAIL INFO ===');
          const userInfo = {
            userType: 'Admin',
            email: authForm.provider.email || authForm.provider.username || 'admin@intastream.com',
            displayName: authForm.provider.fullName || 'Admin User'
          };
          console.log('Returning admin userInfo:', userInfo);
          return userInfo;
        } else {
          console.log('=== HOSPITAL USER EMAIL INFO ===');
          const userInfo = {
            userType: 'Hospital',
            email: authForm.provider.email || authForm.provider.username || 'hospital@intastream.com',
            displayName: authForm.provider.description || authForm.provider.fullName || 'Hospital User'
          };
          console.log('Returning hospital userInfo:', userInfo);
          return userInfo;
        }
      }
    }

    // Handle training system contexts
    if (currentStep === 'nurse-dashboard' || currentStep === 'training-module' || currentStep === 'nurse-profile') {
      if (nurseAuth.authenticated && nurseAuth.email) {
        // Check if this is an admin user in training context
        if (nurseAuth.userType === 'ADMIN') {
          return {
            userType: 'Admin',
            email: nurseAuth.email || nurseAuth.username || 'admin@intastream.com',
            displayName: nurseProfile.fullName || `${nurseProfile.firstName} ${nurseProfile.lastName}`.trim() || 'Admin User'
          };
        } else {
          // Regular professional user
          return {
            userType: 'Professional',
            email: nurseAuth.email || nurseAuth.username || 'professional@intastream.com',
            displayName: nurseProfile.fullName || `${nurseProfile.firstName} ${nurseProfile.lastName}`.trim() || 'Professional'
          };
        }
      }
    }

    return null;
  };

  // Get context-sensitive navigation buttons
  const getNavigationButtons = () => {
    const buttons = [];

    switch (currentStep) {
      case 'landing':
        buttons.push(
          <Button
            key="get-started"
            onClick={() => navigateToStep('provider-auth')}
            size="md"
            rightSection={<ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
          >
            Get Started
          </Button>
        );
        break;

      case 'scheduler':
      case 'booking-editor':
        buttons.push(
          <Button
            key="home"
            onClick={() => navigateToStep('landing')}
            variant="light"
            size="sm"
            leftSection={<HomeIcon style={{ width: '16px', height: '16px' }} />}
          >
            Back to Home
          </Button>
        );
        buttons.push(
          <Button
            key="logout"
            onClick={handleProviderLogout}
            variant="light"
            color="red"
            size="sm"
            leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
          >
            Logout
          </Button>
        );
        break;

      case 'nurse-profile':
        buttons.push(
          <Button
            key="home"
            onClick={() => navigateToStep('landing')}
            variant="light"
            size="sm"
            leftSection={<HomeIcon style={{ width: '16px', height: '16px' }} />}
          >
            Back to Home
          </Button>
        );
        buttons.push(
          <Button
            key="logout"
            onClick={handleNurseLogout}
            variant="light"
            color="red"
            size="sm"
            leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
          >
            Logout
          </Button>
        );
        break;

      case 'nurse-dashboard':
      case 'training-module':
        buttons.push(
          <Button
            key="home"
            onClick={() => navigateToStep('landing')}
            variant="light"
            size="sm"
            leftSection={<HomeIcon style={{ width: '16px', height: '16px' }} />}
          >
            Back to Home
          </Button>
        );
        if (currentStep === 'training-module') {
          buttons.push(
            <Button
              key="dashboard"
              onClick={() => navigateToStep('nurse-dashboard')}
              variant="light"
              size="sm"
              leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
            >
              Back to Dashboard
            </Button>
          );
        }
        buttons.push(
          <Button
            key="logout"
            onClick={handleNurseLogout}
            variant="light"
            color="red"
            size="sm"
            leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
          >
            Logout
          </Button>
        );
        break;

      case 'staff-registration':
        buttons.push(
          <Button
            key="home"
            onClick={() => navigateToStep('landing')}
            variant="light"
            size="sm"
            leftSection={<HomeIcon style={{ width: '16px', height: '16px' }} />}
          >
            Back to Home
          </Button>
        );
        // Only show logout button when user is logged in (past welcome screen)
        if (registrationForm.currentStep !== 'welcome') {
          buttons.push(
            <Button
              key="logout"
              onClick={handleRegistrationLogout}
              variant="light"
              color="red"
              size="sm"
              leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
            >
              Log out
            </Button>
          );
        }
        break;

      default:
        buttons.push(
          <Button
            key="home"
            onClick={() => navigateToStep('landing')}
            variant="light"
            size="sm"
            leftSection={<HomeIcon style={{ width: '16px', height: '16px' }} />}
          >
            Back to Home
          </Button>
        );
        break;
    }

    return buttons;
  };

  // Unified Header Component (standardized - logo, title, theme, navigation)
  const UnifiedHeader = () => {
    const userInfo = getLoggedInUserInfo();

    if (isMobile) {
      // Mobile layout: 3-row stack for better space and readability
      return (
        <Paper shadow="sm" p="sm" radius={0}>
          <Container size="xl">
            <Stack gap="xs">
              {/* Row 1: Logo, title, and theme toggle */}
              <Group justify="space-between" align="center">
                <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                  <img
                    src="/logo.png"
                    alt="IntaStream Hub Logo"
                    style={{
                      height: '30px',

                    }}
                  />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={600} style={{ lineHeight: 1.2 }}>
                      {(() => {
                        const text = getHeaderText();
                        // Mobile-optimized text formatting
                        if (text.includes('(') && text.includes(')')) {
                          const parts = text.split('(');
                          const mainPart = parts[0].trim();
                          const hospitalPart = parts[1]?.replace(')', '').trim();
                          if (hospitalPart) {
                            // Further shorten for mobile - just use hospital name
                            return hospitalPart.length > 20 ?
                              `${mainPart}` :
                              `${mainPart} - ${hospitalPart}`;
                          }
                        }
                        return text.length > 30 ? text.substring(0, 27) + '...' : text;
                      })()}
                    </Text>
                    {userInfo && (
                      <Text size="xs" c="dimmed" truncate style={{ lineHeight: 1.1 }}>
                        {userInfo.displayName || userInfo.email}
                      </Text>
                    )}
                  </Box>
                </Group>

                {/* Theme toggle button */}
                <ActionIcon
                  onClick={toggleColorScheme}
                  variant="light"
                  size="lg"
                  style={{ flexShrink: 0 }}
                >
                  {colorScheme === 'dark' ?
                    <SunIcon style={{ width: '18px', height: '18px' }} /> :
                    <MoonIcon style={{ width: '18px', height: '18px' }} />
                  }
                </ActionIcon>
              </Group>

              {/* Row 2: Navigation buttons with full space */}
              <Group justify="center" gap="sm">
                {getNavigationButtons().map((button, index) => (
                  React.cloneElement(button, {
                    key: index,
                    size: 'sm',
                    style: { flex: 1, minWidth: 0 }
                  })
                ))}
              </Group>
            </Stack>
          </Container>
        </Paper>
      );
    }

    // Desktop layout: Keep original horizontal layout
    return (
      <Paper shadow="sm" p="md" radius={0}>
        <Container size="xl">
          <Group justify="space-between">
            <Group gap="md">
              <img
                src="/logo.png"
                alt="IntaStream Hub Logo"
                style={{
                  height: '48px',

                }}
              />
              <div>
                <Text size="lg" fw={600}>{getHeaderText()}</Text>
                {userInfo && (
                  <Group gap="xs" mt={2}>
                    <UserIcon style={{ width: '14px', height: '14px', opacity: 0.7 }} />
                    <Text size="sm" c="dimmed">
                      {userInfo.userType}: {userInfo.email}
                    </Text>
                  </Group>
                )}
              </div>
            </Group>
            <Group gap="md">
              <Button
                onClick={toggleColorScheme}
                variant="light"
                size="sm"
                leftSection={colorScheme === 'dark' ?
                  <SunIcon style={{ width: '16px', height: '16px' }} /> :
                  <MoonIcon style={{ width: '16px', height: '16px' }} />
                }
              >
                {colorScheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </Button>
              {getNavigationButtons()}
            </Group>
          </Group>
        </Container>
      </Paper>
    );
  };

  // Sub-Header Component (app-specific action buttons)
  const SubHeader = () => {
    const buttons = [];

    // App-specific buttons based on current step
    if (currentStep === 'scheduler') {
      if (isMobile) {
        // Mobile: More compact layout with smaller buttons
        buttons.push(
          <Button
            key="help"
            onClick={() => setShowTutorial(!showTutorial)}
            variant={showTutorial ? "filled" : "light"}
            size="sm"
            leftSection={<QuestionMarkCircleIcon style={{ width: '14px', height: '14px' }} />}
            style={{ flex: '0 0 auto' }}
          >
            Help
          </Button>
        );

        buttons.push(
          <Button
            key="request-booking"
            onClick={handleNewBooking}
            variant="filled"
            size="sm"
            color="blue"
            leftSection={<PlusIcon style={{ width: '14px', height: '14px' }} />}
            style={{
              flex: '1 1 auto',
              fontWeight: 600,
              maxWidth: '200px'
            }}
          >
            Request Booking
          </Button>
        );
      } else {
        // Desktop: Keep original styling
        buttons.push(
          <Button
            key="help"
            onClick={() => setShowTutorial(!showTutorial)}
            variant={showTutorial ? "filled" : "light"}
            size="md"
            leftSection={<QuestionMarkCircleIcon style={{ width: '16px', height: '16px' }} />}
          >
            Help
          </Button>
        );

        buttons.push(
          <Button
            key="request-booking"
            onClick={handleNewBooking}
            variant="filled"
            size="md"
            color="blue"
            leftSection={<PlusIcon style={{ width: '16px', height: '16px' }} />}
            style={{
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              transform: 'scale(1.02)'
            }}
          >
            Request Booking
          </Button>
        );
      }
    }

    // Show sub-header only for pages that have app-specific buttons
    if (buttons.length === 0) {
      return null;
    }

    return (
      <Paper p={isMobile ? "sm" : "md"} radius={0} style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
        <Container size="xl">
          <Group justify="center" gap={isMobile ? "sm" : "lg"}>
            {buttons}
          </Group>
        </Container>
      </Paper>
    );
  };

  // Core Mandatory Training (CSTF-Aligned) - Required for ALL nurses
  const coreTraining = [
    {
      id: 'edi',
      title: 'Equality, Diversity, and Inclusion',
      description: 'Promoting inclusion and addressing discrimination in healthcare settings',
      legislation: 'Equality Act 2010',
      renewalPeriod: 'Annual',
      status: 'pending'
    },
    {
      id: 'health-safety',
      title: 'Health, Safety, and Welfare',
      description: 'Risk assessments, incident reporting, and workplace safety compliance',
      legislation: 'Health and Safety at Work Act 1974',
      renewalPeriod: 'Annual',
      status: 'pending'
    },
    {
      id: 'fire-safety',
      title: 'Fire Safety',
      description: 'Fire prevention, evacuation procedures, and fire equipment use',
      legislation: 'Regulatory Reform (Fire Safety) Order 2005',
      renewalPeriod: 'Annual',
      status: 'pending'
    },
    {
      id: 'infection-control',
      title: 'Infection Prevention and Control',
      description: 'Hand hygiene, PPE use, healthcare-associated infections, sepsis awareness',
      legislation: 'Standard precautions, MRSA/C. difficile management',
      renewalPeriod: 'Annual',
      status: 'pending'
    },
    {
      id: 'manual-handling',
      title: 'Moving and Handling (Manual Handling)',
      description: 'Safe techniques for moving patients and objects',
      legislation: 'Manual Handling Operations Regulations 1992',
      renewalPeriod: 'Annual',
      status: 'pending'
    },
    {
      id: 'safeguarding-adults',
      title: 'Safeguarding Adults',
      description: 'Recognizing and responding to abuse/neglect of vulnerable adults',
      legislation: 'Care Act 2014',
      renewalPeriod: 'Biennial',
      status: 'pending'
    },
    {
      id: 'safeguarding-children',
      title: 'Safeguarding Children',
      description: 'Protecting children from abuse and neglect',
      legislation: 'Children Act 1989/2004, Working Together to Safeguard Children',
      renewalPeriod: 'Biennial',
      status: 'pending'
    },
    {
      id: 'bls',
      title: 'Basic Life Support (BLS) / Resuscitation',
      description: 'CPR, AED use, and choking management',
      legislation: 'Resuscitation Council UK guidelines',
      renewalPeriod: 'Annual',
      status: 'pending'
    },
    {
      id: 'data-protection',
      title: 'Information Governance and Data Protection',
      description: 'GDPR compliance, confidentiality, secure handling of patient records',
      legislation: 'Data Protection Act 2018, GDPR',
      renewalPeriod: 'Annual',
      status: 'pending'
    },
    {
      id: 'prevent',
      title: 'Preventing Radicalisation (Prevent Awareness)',
      description: 'Identifying signs of radicalization and safeguarding procedures',
      legislation: 'Counter-Terrorism and Security Act 2015',
      renewalPeriod: 'Triennial',
      status: 'pending'
    },
    {
      id: 'conflict-resolution',
      title: 'Conflict Resolution',
      description: 'Managing challenging behavior and de-escalation techniques',
      legislation: 'Professional standards and workplace safety',
      renewalPeriod: 'Biennial',
      status: 'pending'
    },
    {
      id: 'complaints',
      title: 'Complaints Handling',
      description: 'Procedures for addressing patient complaints per NHS guidelines',
      legislation: 'NHS Constitution, Patient Experience standards',
      renewalPeriod: 'Biennial',
      status: 'pending'
    }
  ];

  // Database-driven role-specific training
  const getRoleSpecificTraining = (medicalRole) => {
    if (!medicalRole || medicalRole === 'Not Assigned') {
      return [];
    }

    // Map database roles to training courses
    const trainingMap = {
      'Registered General Nurse (RGN)': [
        { title: 'Medicines Management', description: 'Safe administration, storage, and disposal of medications', renewalPeriod: 'Annual' },
        { title: 'Pressure Ulcer Prevention', description: 'Risk assessment and management for immobile patients', renewalPeriod: 'Annual' },
        { title: 'Sepsis Awareness and Management', description: 'Early recognition and treatment of sepsis', renewalPeriod: 'Annual' },
        { title: 'Venepuncture and Cannulation', description: 'Blood sampling and IV access techniques', renewalPeriod: 'Biennial' },
        { title: 'Blood Transfusion Safety', description: 'Safe administration of blood products', renewalPeriod: 'Annual' },
        { title: 'Anaphylaxis Training', description: 'Managing allergic reactions, including epinephrine use', renewalPeriod: 'Annual' },
        { title: 'Catheter Care', description: 'Safe insertion and management of urinary catheters', renewalPeriod: 'Annual' }
      ],
      'Mental Health Nurse': [
        { title: 'Mental Capacity Act (MCA) and DoLS', description: 'Assessing capacity and protecting patient rights', renewalPeriod: 'Annual' },
        { title: 'De-escalation and Physical Restraint', description: 'Safe management of aggressive behavior (PMVA)', renewalPeriod: 'Annual' },
        { title: 'Suicide Risk Assessment', description: 'Identifying and managing patients at risk of self-harm', renewalPeriod: 'Annual' },
        { title: 'Substance Misuse Awareness', description: 'Supporting patients with addiction issues', renewalPeriod: 'Biennial' },
        { title: 'Dementia Awareness', description: 'Person-centered care for cognitive impairments', renewalPeriod: 'Biennial' }
      ],
      'Pediatric Nurse': [
        { title: 'Pediatric Basic Life Support (PBLS)', description: 'Age-specific resuscitation techniques', renewalPeriod: 'Annual' },
        { title: 'Neonatal Resuscitation', description: 'Managing newborns in critical conditions', renewalPeriod: 'Annual' },
        { title: 'Pediatric Medicines Management', description: 'Safe administration of medications to children', renewalPeriod: 'Annual' },
        { title: 'Child Protection', description: 'Recognizing signs of abuse in pediatric populations', renewalPeriod: 'Annual' }
      ],
      'Community Nurse': [
        { title: 'Lone Working', description: 'Safety protocols for working alone in community settings', renewalPeriod: 'Annual' },
        { title: 'Palliative and End-of-Life Care', description: 'Supporting patients and families in end-of-life situations', renewalPeriod: 'Biennial' },
        { title: 'Wound Care', description: 'Managing chronic wounds, including pressure ulcers', renewalPeriod: 'Annual' },
        { title: 'Catheter and Stoma Care', description: 'Community-based management of medical devices', renewalPeriod: 'Annual' },
        { title: 'Food Hygiene', description: 'Safe handling of food for patients in care settings', renewalPeriod: 'Triennial' }
      ],
      'Midwifery': [
        { title: 'Neonatal Resuscitation', description: 'Managing newborn emergencies', renewalPeriod: 'Annual' },
        { title: 'Maternal Sepsis Awareness', description: 'Recognizing and treating sepsis in pregnant/postpartum women', renewalPeriod: 'Annual' },
        { title: 'Fetal Monitoring', description: 'Interpreting cardiotocography (CTG) and fetal heart rates', renewalPeriod: 'Annual' },
        { title: 'Emergency Obstetric Skills', description: 'Managing complications like postpartum hemorrhage', renewalPeriod: 'Annual' }
      ],
      'Surgical/Theatre Nurse': [
        { title: 'Aseptic Technique', description: 'Maintaining sterile fields during surgery', renewalPeriod: 'Annual' },
        { title: 'Surgical Safety Checklist', description: 'WHO checklist compliance for patient safety', renewalPeriod: 'Annual' },
        { title: 'Anesthesia Awareness', description: 'Supporting patients during anesthesia administration', renewalPeriod: 'Annual' },
        { title: 'Infection Control in Theatres', description: 'Enhanced protocols for surgical environments', renewalPeriod: 'Annual' }
      ],
      'Critical Care/Intensive Care Nurse (ICU)': [
        { title: 'Advanced Life Support (ALS)', description: 'Advanced resuscitation techniques beyond BLS', renewalPeriod: 'Triennial' },
        { title: 'Ventilator Management', description: 'Operating and monitoring mechanical ventilators', renewalPeriod: 'Annual' },
        { title: 'Central Line Management', description: 'Care of central venous catheters', renewalPeriod: 'Annual' },
        { title: 'Sepsis Management', description: 'Advanced protocols for septic shock', renewalPeriod: 'Annual' },
        { title: 'Hemodynamic Monitoring', description: 'Interpreting invasive monitoring devices', renewalPeriod: 'Annual' }
      ],
      'Emergency Nurse': [
        { title: 'Advanced Life Support (ALS)', description: 'Advanced resuscitation techniques beyond BLS', renewalPeriod: 'Triennial' },
        { title: 'Triage Assessment', description: 'Rapid patient assessment and prioritization', renewalPeriod: 'Annual' },
        { title: 'Trauma Management', description: 'Initial assessment and stabilization of trauma patients', renewalPeriod: 'Annual' },
        { title: 'Mental Health Crisis', description: 'Managing psychiatric emergencies and self-harm', renewalPeriod: 'Annual' }
      ],
      'Oncology Nurse': [
        { title: 'Chemotherapy Administration', description: 'Safe handling and administration of cytotoxic drugs', renewalPeriod: 'Annual' },
        { title: 'Cancer Pain Management', description: 'Palliative care and pain control techniques', renewalPeriod: 'Annual' },
        { title: 'Radiation Safety', description: 'Protection protocols in radiotherapy environments', renewalPeriod: 'Annual' },
        { title: 'End-of-Life Care', description: 'Supporting patients and families in terminal care', renewalPeriod: 'Biennial' }
      ],
      'Recovery Nurse (PACU)': [
        { title: 'Post-Anesthetic Care', description: 'Monitoring patients recovering from anesthesia', renewalPeriod: 'Annual' },
        { title: 'Airway Management', description: 'Managing compromised airways in recovery', renewalPeriod: 'Annual' },
        { title: 'Pain Assessment', description: 'Post-operative pain evaluation and management', renewalPeriod: 'Annual' },
        { title: 'Emergency Response', description: 'Managing post-operative complications', renewalPeriod: 'Annual' }
      ]
    };

    return trainingMap[medicalRole] || [];
  };

  // Determine safeguarding levels based on database medical role
  const getSafeguardingLevels = (medicalRole) => {
    const levels = {
      'Pediatric Nurse': { adults: 'Level 2', children: 'Level 3' },
      'Mental Health Nurse': { adults: 'Level 3', children: 'Level 2' },
      'Midwifery': { adults: 'Level 2', children: 'Level 3' },
      'Community Nurse': { adults: 'Level 2', children: 'Level 2' },
      'Critical Care/Intensive Care Nurse (ICU)': { adults: 'Level 2', children: 'Level 2' },
      'Surgical/Theatre Nurse': { adults: 'Level 1', children: 'Level 1' },
      'Emergency Nurse': { adults: 'Level 2', children: 'Level 2' },
      'Oncology Nurse': { adults: 'Level 2', children: 'Level 2' },
      'Recovery Nurse (PACU)': { adults: 'Level 1', children: 'Level 1' },
      'Registered General Nurse (RGN)': { adults: 'Level 2', children: 'Level 2' }
    };

    return levels[medicalRole] || { adults: 'Level 2', children: 'Level 2' };
  };

  // EDI Training Content Structure
  const ediTrainingContent = {
    sections: [
      {
        id: 'introduction',
        title: 'Introduction to Equality, Diversity, and Inclusion',
        content: {
          overview: 'Understanding the legal framework and importance of EDI in healthcare',
          learningPoints: [
            'Understand the Equality Act 2010 and its healthcare applications',
            'Recognize protected characteristics and their relevance to patient care',
            'Identify the importance of EDI in providing quality healthcare'
          ],
          educationalContent: [
            {
              title: 'The Equality Act 2010 in Healthcare',
              content: 'The Equality Act 2010 is the primary legislation governing equality and discrimination in the UK, including healthcare settings. This Act consolidated and strengthened previous anti-discrimination laws, creating a single, comprehensive framework. For healthcare professionals, this means you have legal duties to ensure that all patients receive equal treatment regardless of their personal characteristics. The Act applies to all aspects of healthcare delivery, from initial patient contact through treatment decisions to discharge planning. Understanding this Act is essential because it not only protects patients from discrimination but also provides a framework for delivering truly inclusive care that meets everyone\'s needs.'
            },
            {
              title: 'Protected Characteristics and Patient Care',
              content: 'The Act identifies nine protected characteristics: age, disability, gender reassignment, marriage and civil partnership, pregnancy and maternity, race, religion or belief, sex, and sexual orientation. In healthcare, these characteristics are particularly relevant because they often influence how patients experience illness, interact with healthcare systems, and respond to treatment. For example, cultural beliefs may affect pain expression, religious practices might influence treatment timing, or communication needs may vary based on disability. Recognizing these characteristics helps you provide personalized, respectful care that acknowledges each patient as an individual while ensuring equitable treatment for all.'
            },
            {
              title: 'Why EDI Matters in Healthcare',
              content: 'Equality, diversity, and inclusion are not just legal requirements—they are fundamental to providing high-quality, safe healthcare. Research consistently shows that discrimination and bias in healthcare lead to poorer health outcomes, reduced patient satisfaction, and increased health inequalities. When patients feel respected and understood, they are more likely to engage with treatment, disclose important information, and follow medical advice. Conversely, experiences of discrimination can lead to delayed care-seeking, non-adherence to treatment, and ultimately worse health outcomes. By embracing EDI principles, healthcare professionals contribute to better patient outcomes and help reduce health inequalities across different population groups.'
            }
          ]
        }
      },
      {
        id: 'legal-framework',
        title: 'Legal Framework: The Equality Act 2010',
        content: {
          overview: 'Comprehensive understanding of the Equality Act 2010',
          learningPoints: [
            'Nine protected characteristics under the Act',
            'Types of discrimination: direct, indirect, harassment, victimization',
            'Legal obligations for healthcare providers',
            'Reasonable adjustments in healthcare settings'
          ],
          educationalContent: [
            {
              title: 'Understanding the Nine Protected Characteristics',
              content: 'The Equality Act protects people from discrimination based on nine specific characteristics. Age covers people of all ages, though some age-based decisions may be justified (like age-appropriate wards). Disability includes physical, mental, and learning disabilities, as well as conditions like diabetes or HIV. Gender reassignment protects people who are transitioning or have transitioned gender. Marriage and civil partnership covers both married people and civil partners. Pregnancy and maternity protects women during pregnancy and maternity leave. Race includes color, nationality, ethnic or national origins. Religion or belief covers religious faiths and philosophical beliefs like veganism. Sex protects both men and women from sex-based discrimination. Sexual orientation covers heterosexual, lesbian, gay, and bisexual people. In healthcare, understanding these characteristics helps you recognize when special considerations or protections may apply.'
            },
            {
              title: 'Types of Discrimination Explained',
              content: 'Direct discrimination occurs when someone is treated less favorably because of a protected characteristic—for example, refusing treatment because of someone\'s race. Indirect discrimination happens when a policy that applies to everyone puts people with a protected characteristic at a disadvantage—like requiring all patients to remove religious headwear when it\'s not medically necessary. Harassment involves unwanted conduct that creates an intimidating or offensive environment, such as making inappropriate comments about someone\'s sexuality. Victimization occurs when someone is treated badly because they have complained about discrimination or helped someone else complain. In healthcare settings, any of these could occur and would be unlawful, potentially leading to legal action and professional conduct issues.'
            },
            {
              title: 'Healthcare Provider Legal Obligations',
              content: 'Healthcare providers have specific legal duties under the Equality Act. They must not discriminate against patients, staff, or visitors. They must make reasonable adjustments for disabled people to ensure equal access to services. They must prevent harassment and victimization. Providers also have a Public Sector Equality Duty, requiring them to eliminate discrimination, advance equality of opportunity, and foster good relations between different groups. This means healthcare organizations must actively consider equality in all their decisions and policies. As a healthcare professional, you play a crucial role in helping your organization meet these obligations through your daily practice and interactions with patients and colleagues.'
            },
            {
              title: 'Reasonable Adjustments in Practice',
              content: 'Reasonable adjustments are changes made to remove barriers that might prevent someone with a disability from accessing healthcare. Examples include providing information in different formats (large print, audio, easy read), allowing extra time for appointments, offering sign language interpreters, or making physical modifications to ensure accessibility. The adjustment must be reasonable—considering factors like cost, practicality, and effectiveness. What\'s reasonable varies depending on the organization\'s size and resources. Importantly, you don\'t need to wait for someone to ask for adjustments; if you can see someone might need help, you should offer it. The goal is ensuring everyone can access healthcare services effectively, regardless of their disability.'
            }
          ]
        }
      },
      {
        id: 'healthcare-application',
        title: 'Applying EDI Principles in Healthcare',
        content: {
          overview: 'Practical application of EDI principles in nursing practice',
          learningPoints: [
            'Culturally sensitive care delivery',
            'Communication with diverse patient populations',
            'Religious and cultural considerations in treatment',
            'Supporting LGBTQ+ patients and families'
          ],
          educationalContent: [
            {
              title: 'Delivering Culturally Sensitive Care',
              content: 'Culturally sensitive care means recognizing that people from different cultural backgrounds may have different beliefs about health, illness, and treatment. This includes understanding that pain expression varies across cultures—some cultures encourage stoic responses while others are more expressive. Family involvement in healthcare decisions also varies culturally; some cultures prioritize family decision-making over individual autonomy. Food preferences, body modesty, and gender preferences for healthcare providers can all be influenced by cultural background. Providing culturally sensitive care doesn\'t mean compromising medical standards, but rather adapting your approach to respect cultural differences while ensuring safe, effective treatment. This might involve working with interpreters, respecting dietary requirements, or accommodating cultural practices around death and dying.'
            },
            {
              title: 'Effective Communication with Diverse Populations',
              content: 'Communication barriers can significantly impact healthcare quality and safety. Language differences are obvious barriers, but communication challenges go beyond language. People with hearing impairments may need you to face them when speaking, ensure good lighting for lip reading, or provide written information. Those with learning disabilities might need simpler language, more time, or visual aids. Mental health conditions can affect how people process information or express themselves. Age also influences communication needs—older adults might have different health literacy levels or prefer different communication styles. The key is to assess each person\'s communication needs and adapt accordingly. This might mean speaking more slowly, using simple language, providing information in different formats, or involving family members or advocates when appropriate.'
            },
            {
              title: 'Religious and Cultural Treatment Considerations',
              content: 'Religion and culture can significantly influence healthcare experiences and decisions. Some religions have specific requirements around fasting, which may affect medication timing or surgical procedures. Many faiths have particular practices around prayer times, dietary restrictions, or gender preferences for healthcare providers. End-of-life care is particularly influenced by religious and cultural beliefs, with different traditions around family involvement, pain management, and after-death practices. Some cultures may prefer traditional or complementary therapies alongside conventional medicine. It\'s important to ask patients about any religious or cultural needs rather than making assumptions. When religious or cultural preferences conflict with medical recommendations, try to find compromises that respect beliefs while maintaining safety—this might involve consulting with religious leaders or cultural liaison services.'
            },
            {
              title: 'Supporting LGBTQ+ Patients and Families',
              content: 'LGBTQ+ patients often face discrimination in healthcare settings, leading to delayed care-seeking and poorer health outcomes. Creating an inclusive environment starts with using inclusive language—ask about "partners" rather than assuming marital status or gender of partners. Respect chosen names and pronouns, especially for transgender patients. Understand that family structures may be diverse, with chosen families being as important as biological ones. Be aware that LGBTQ+ patients may have specific health needs or concerns, such as hormone therapy for transgender patients or mental health impacts of discrimination. Confidentiality is particularly important—don\'t assume it\'s safe to disclose someone\'s sexual orientation or gender identity to family members or other healthcare providers without explicit permission. Training in LGBTQ+ cultural competency helps you provide affirming, respectful care that improves health outcomes for these communities.'
            }
          ]
        }
      },
      {
        id: 'scenarios',
        title: 'Scenario-Based Learning',
        content: {
          overview: 'Real-world healthcare scenarios requiring EDI consideration',
          learningPoints: [
            'Religious observance and medication timing',
            'Communication barriers and reasonable adjustments',
            'Cultural considerations in end-of-life care',
            'Addressing unconscious bias in clinical decisions'
          ],
          educationalContent: [
            {
              title: 'Managing Religious Observance and Treatment Schedules',
              content: 'Religious observances can significantly impact healthcare delivery, requiring healthcare professionals to balance spiritual needs with medical requirements. During Ramadan, Muslim patients may fast from dawn to sunset, affecting medication timing, blood glucose monitoring, and surgical schedules. The key is working collaboratively with patients and pharmacy teams to adjust medication regimens while maintaining therapeutic effectiveness. This might involve switching to modified-release formulations, adjusting timing to before dawn or after sunset, or in some cases, discussing with religious authorities whether medical exemptions apply. Similarly, Sabbath observances may affect when some patients can sign consent forms or participate in non-emergency procedures. Jewish patients might prefer not to use electronic devices on Saturdays, affecting how they interact with call systems or electronic entertainment. The goal is finding solutions that respect religious commitments while ensuring safe, effective care.'
            },
            {
              title: 'Overcoming Communication Barriers',
              content: 'Effective communication is fundamental to safe healthcare, yet many barriers can impede this. Language barriers are common and require professional interpreters, not family members who may filter information or lack medical vocabulary. For patients with hearing impairments, reasonable adjustments might include facing the patient when speaking, ensuring good lighting for lip reading, using written communication, or arranging sign language interpreters. Patients with learning disabilities may need extra time, simpler language, visual aids, or the presence of a familiar person or advocate. Some patients may have cultural communication styles that differ from typical healthcare interactions—for example, some cultures discourage direct eye contact with authority figures, or may prefer family spokespeople. Recognizing and addressing these barriers isn\'t just good practice; it\'s essential for obtaining informed consent, ensuring understanding of treatment plans, and maintaining patient safety.'
            },
            {
              title: 'Cultural Competence in End-of-Life Care',
              content: 'End-of-life care is deeply influenced by cultural and religious beliefs, requiring sensitive, individualized approaches. Different cultures have varying perspectives on truth-telling about prognosis—while Western medicine typically emphasizes patient autonomy and direct communication, some cultures prefer family-mediated communication to protect patients from distressing news. Pain management approaches also vary; some cultures view suffering as meaningful or worry about addiction to pain medications. After death, different traditions have specific requirements about body handling, timing of procedures, and family involvement. Islamic tradition requires rapid burial, often within 24 hours, while some Hindu families may want to wash and dress the body themselves. Some cultures prefer same-gender healthcare providers for intimate care. Understanding these differences helps you provide respectful, appropriate care that honors the patient\'s and family\'s values while meeting medical and legal requirements.'
            },
            {
              title: 'Recognizing and Addressing Unconscious Bias',
              content: 'Unconscious bias affects healthcare decisions and outcomes, often leading to disparities in care quality. Research shows that healthcare providers may unconsciously assess pain differently based on race, age, or gender, potentially under-treating pain in certain groups. Assumptions about compliance or lifestyle might influence treatment recommendations—for example, assuming older patients won\'t adhere to complex medication regimens, or that certain ethnic groups have different pain tolerances. Socioeconomic bias can affect how seriously symptoms are taken or how much time is spent explaining treatment options. To address unconscious bias, regularly reflect on your decision-making processes, seek diverse perspectives when making complex decisions, and challenge your assumptions about patients. Use standardized assessment tools when possible, and ensure your clinical decisions are based on evidence rather than assumptions. When you notice disparities in care or outcomes, investigate potential bias and work to address systemic issues that may be contributing to inequitable care.'
            }
          ]
        }
      }
    ],
    assessment: {
      passingScore: 80,
      questions: [
        {
          id: 1,
          type: 'multiple-choice',
          question: 'Under the Equality Act 2010, how many protected characteristics are legally recognized?',
          options: ['7', '8', '9', '10'],
          correctAnswer: 2,
          explanation: 'The Equality Act 2010 recognizes 9 protected characteristics: age, disability, gender reassignment, marriage and civil partnership, pregnancy and maternity, race, religion or belief, sex, and sexual orientation.'
        },
        {
          id: 2,
          type: 'multiple-choice',
          question: 'A Muslim patient requests that their medication be given after sunset during Ramadan. What is the most appropriate response?',
          options: [
            'Explain that medication times cannot be changed',
            'Work with pharmacy to adjust timing while maintaining therapeutic effectiveness',
            'Ask the patient to take medication early',
            'Suggest the patient skip doses during religious observance'
          ],
          correctAnswer: 1,
          explanation: 'Healthcare providers must make reasonable adjustments to accommodate religious observance while ensuring patient safety and therapeutic effectiveness.'
        },
        {
          id: 3,
          type: 'multiple-choice',
          question: 'Which of the following is an example of indirect discrimination?',
          options: [
            'Refusing treatment based on sexual orientation',
            'A policy requiring all patients to remove religious headwear for X-rays when not medically necessary',
            'Making offensive comments about a patient\'s race',
            'Providing preferential treatment based on age'
          ],
          correctAnswer: 1,
          explanation: 'Indirect discrimination occurs when a policy applies to everyone but disadvantages people with a protected characteristic. Unnecessary removal of religious headwear would disadvantage certain religious groups.'
        },
        {
          id: 4,
          type: 'multiple-choice',
          question: 'What is the primary purpose of making reasonable adjustments in healthcare?',
          options: [
            'To comply with hospital policies',
            'To ensure equal access to healthcare services',
            'To reduce healthcare costs',
            'To speed up treatment processes'
          ],
          correctAnswer: 1,
          explanation: 'Reasonable adjustments ensure that people with disabilities or other protected characteristics can access healthcare services on an equal basis with others.'
        },
        {
          id: 5,
          type: 'multiple-choice',
          question: 'A patient with hearing impairment is struggling to understand admission questions. What should you do first?',
          options: [
            'Speak louder',
            'Ask a family member to interpret',
            'Face the patient, speak clearly, and ensure good lighting',
            'Write everything down immediately'
          ],
          correctAnswer: 2,
          explanation: 'Initial communication adjustments include facing the patient, speaking clearly with good lip visibility, and ensuring adequate lighting for lip reading before considering other interventions.'
        },
        {
          id: 6,
          type: 'multiple-choice',
          question: 'Which behavior constitutes harassment under the Equality Act 2010?',
          options: [
            'Making a single inappropriate joke',
            'Unwanted conduct that creates an intimidating or offensive environment',
            'Disagreeing with a colleague professionally',
            'Asking about someone\'s cultural background professionally'
          ],
          correctAnswer: 1,
          explanation: 'Harassment is unwanted conduct related to a protected characteristic that violates dignity or creates an intimidating, hostile, degrading, humiliating or offensive environment.'
        },
        {
          id: 7,
          type: 'multiple-choice',
          question: 'Cultural competence in healthcare primarily means:',
          options: [
            'Knowing about every culture',
            'Treating everyone exactly the same',
            'Understanding and respecting cultural differences while providing quality care',
            'Speaking multiple languages'
          ],
          correctAnswer: 2,
          explanation: 'Cultural competence involves understanding, respecting, and appropriately responding to cultural differences while maintaining high standards of care for all patients.'
        },
        {
          id: 8,
          type: 'multiple-choice',
          question: 'If you witness discrimination against a colleague, what should you do?',
          options: [
            'Ignore it if it doesn\'t involve you directly',
            'Tell the colleague to handle it themselves',
            'Report it according to workplace policies',
            'Wait to see if it happens again'
          ],
          correctAnswer: 2,
          explanation: 'Healthcare professionals have a responsibility to report discrimination and create inclusive environments. Most organizations have specific reporting procedures for such incidents.'
        },
        {
          id: 9,
          type: 'multiple-choice',
          question: 'When providing care to LGBTQ+ patients, it is most important to:',
          options: [
            'Assume their sexual orientation',
            'Use inclusive language and respect their identity',
            'Provide different medical treatment',
            'Ask detailed personal questions'
          ],
          correctAnswer: 1,
          explanation: 'Using inclusive language, respecting identity, and avoiding assumptions creates a safe healthcare environment for LGBTQ+ patients.'
        },
        {
          id: 10,
          type: 'multiple-choice',
          question: 'Unconscious bias in healthcare can be addressed by:',
          options: [
            'Denying that bias exists',
            'Reflecting on decision-making and seeking diverse perspectives',
            'Making decisions quickly without overthinking',
            'Treating all patients identically regardless of needs'
          ],
          correctAnswer: 1,
          explanation: 'Addressing unconscious bias requires self-reflection, awareness of potential biases, and actively seeking diverse perspectives in clinical decision-making.'
        }
      ]
    }
  };

  // Health, Safety, and Welfare Training Content Structure
  const healthSafetyTrainingContent = {
    sections: [
      {
        id: 'introduction',
        title: 'Introduction to Health, Safety, and Welfare',
        content: {
          overview: 'Understanding legal obligations and creating safe working environments in healthcare',
          learningPoints: [
            'Understand the Health and Safety at Work Act 1974 and its healthcare applications',
            'Recognize common workplace hazards and risk assessment principles',
            'Learn incident reporting procedures and investigation processes',
            'Understand duty of care responsibilities for patient and staff safety'
          ],
          educationalContent: [
            {
              title: 'The Health and Safety at Work Act 1974 in Healthcare',
              content: 'The Health and Safety at Work Act 1974 is the primary legislation governing workplace safety in the UK, including healthcare settings. This Act established the fundamental principle that employers must ensure the health, safety, and welfare of their employees "so far as is reasonably practicable." In healthcare, this means providing safe working environments, proper equipment, adequate training, and clear procedures. The Act applies to all healthcare settings, from large hospitals to community clinics. It requires employers to conduct risk assessments, provide safety information and training, maintain safe equipment, and ensure safe systems of work. For healthcare professionals, understanding this Act is crucial because you have both rights (to work in a safe environment) and responsibilities (to follow safety procedures and report hazards). The Act also covers patient safety where it relates to staff actions, making it integral to healthcare delivery.'
            },
            {
              title: 'Common Healthcare Workplace Hazards',
              content: 'Healthcare environments present unique safety challenges due to the combination of human factors, complex equipment, and hazardous substances. Physical hazards include slips and falls from wet floors, manual handling injuries from lifting patients, and sharps injuries from needles and medical instruments. Chemical hazards arise from medications, cleaning products, anesthetic gases, and chemotherapy drugs. Biological hazards include exposure to infectious diseases, contaminated materials, and potentially dangerous microorganisms. Ergonomic hazards result from prolonged standing, repetitive motions, and awkward positioning during patient care. Psychosocial hazards include workplace stress, violence from patients or relatives, and emotional demands of healthcare work. Recognizing these hazards is the first step in creating safer working environments and protecting both staff and patients from harm.'
            },
            {
              title: 'Risk Assessment Principles',
              content: 'Risk assessment is a systematic process for identifying hazards and evaluating the risks they pose. The five-step process involves: identifying hazards, determining who might be harmed and how, evaluating risks and deciding on precautions, recording findings and implementing them, and reviewing and updating assessments regularly. In healthcare, risk assessments must consider both routine activities and emergency situations. They should involve staff who actually do the work, as they understand the practical challenges. Risk assessments must be proportionate to the risk and appropriate to the work environment. They should lead to practical control measures that actually reduce risk, not just tick-box exercises. Regular review is essential because healthcare environments change frequently with new equipment, procedures, and patient populations.'
            },
            {
              title: 'Duty of Care and Professional Responsibilities',
              content: 'Duty of care in healthcare extends beyond clinical practice to include safety responsibilities for colleagues, patients, and visitors. This means following safety procedures even when under pressure, reporting hazards and incidents promptly, and participating in safety training and initiatives. Professional bodies like the NMC emphasize that nurses must work safely and effectively, including maintaining their own health and wellbeing to perform their role effectively. Duty of care also means speaking up about safety concerns, even when it might be uncomfortable or challenging. This includes questioning practices that seem unsafe, reporting near misses, and supporting colleagues in maintaining safety standards. Understanding duty of care helps create a culture where safety is everyone\'s responsibility, not just management\'s concern.'
            }
          ]
        }
      },
      {
        id: 'legal-framework',
        title: 'Legal Framework and Regulations',
        content: {
          overview: 'Comprehensive understanding of health and safety legislation in healthcare',
          learningPoints: [
            'Health and Safety at Work Act 1974 - employer and employee duties',
            'Management of Health and Safety at Work Regulations 1999',
            'Control of Substances Hazardous to Health (COSHH) Regulations',
            'Reporting of Injuries, Diseases and Dangerous Occurrences Regulations (RIDDOR)',
            'Health and safety enforcement and penalties'
          ],
          educationalContent: [
            {
              title: 'Employer and Employee Duties Under HASAWA 1974',
              content: 'The Health and Safety at Work Act creates specific duties for both employers and employees. Employers must provide and maintain safe plant and systems of work, ensure safe use and handling of substances, provide information and training, maintain a safe workplace with safe access and egress, and provide a safe working environment. They must also prepare a written health and safety policy if they employ five or more people, and consult with employees on health and safety matters. Employees have duties too: they must take reasonable care for their own health and safety and that of others affected by their actions, cooperate with their employer on health and safety matters, not interfere with or misuse anything provided for health and safety, and report dangerous situations. In healthcare, this means following procedures, using protective equipment properly, reporting hazards immediately, and participating in safety training. Both employers and employees can face legal consequences for failing to meet these duties.'
            },
            {
              title: 'Management of Health and Safety at Work Regulations 1999',
              content: 'These regulations expand on the Health and Safety at Work Act by requiring employers to make more specific arrangements for managing health and safety. They require formal risk assessments for five or more employees, appointment of competent persons to assist with health and safety duties, establishment of procedures for serious and imminent danger, provision of health and safety information to employees, and cooperation between employers sharing workplaces. In healthcare, this often means having dedicated health and safety officers, regular risk assessment reviews, emergency evacuation procedures, and clear protocols for different types of incidents. The regulations also require specific considerations for pregnant workers, young people, and temporary workers. They emphasize the need for health surveillance where appropriate, which is particularly relevant in healthcare where staff may be exposed to infectious diseases or hazardous substances.'
            },
            {
              title: 'COSHH Regulations in Healthcare Settings',
              content: 'The Control of Substances Hazardous to Health (COSHH) Regulations require employers to assess and control exposure to hazardous substances. In healthcare, this includes medications (especially chemotherapy drugs), cleaning chemicals, laboratory reagents, anesthetic gases, and biological agents. Employers must identify hazardous substances, assess health risks from exposure, prevent or adequately control exposure, ensure control measures are used and maintained, monitor exposure where necessary, carry out health surveillance where appropriate, prepare plans for accidents and emergencies, and provide information and training. For healthcare staff, this means understanding which substances are hazardous, following safe handling procedures, using appropriate personal protective equipment, reporting spillages or accidents immediately, and participating in health surveillance when required. COSHH assessments must be reviewed regularly and updated when procedures or substances change.'
            },
            {
              title: 'RIDDOR Reporting Requirements',
              content: 'The Reporting of Injuries, Diseases and Dangerous Occurrences Regulations (RIDDOR) require certain work-related incidents to be reported to the Health and Safety Executive. Reportable incidents include deaths, specified injuries (fractures, amputations, serious burns, loss of consciousness, acute illness from toxins), injuries causing absence from work for more than seven days, and occupational diseases. In healthcare, this might include needlestick injuries leading to infection, back injuries from manual handling, exposure to infectious diseases, or accidents involving medical equipment. Reports must be made immediately for deaths and serious injuries, within 10 days for other injuries, and as soon as diagnosed for diseases. RIDDOR helps track workplace safety trends and can lead to investigations or enforcement action. Understanding RIDDOR requirements helps ensure proper reporting and demonstrates commitment to learning from incidents to prevent recurrence.'
            },
            {
              title: 'Health and Safety Enforcement and Penalties',
              content: 'Health and safety law is enforced by the Health and Safety Executive (HSE) and local authorities. They have powers to investigate incidents, inspect workplaces, issue improvement or prohibition notices, and prosecute offenders. Penalties for health and safety offenses have increased significantly, with unlimited fines for serious breaches and potential imprisonment for individuals. In healthcare, enforcement action can result from patient safety incidents, staff injuries, or systemic failures in safety management. Organizations can face reputational damage, financial penalties, and increased scrutiny. Individual healthcare professionals can also face personal liability, professional sanctions, and criminal prosecution in serious cases. Understanding enforcement helps emphasize why safety compliance is essential - it\'s not just about following rules, but about protecting lives and careers. The "corporate killing" offense can apply when management failures lead to deaths, highlighting the serious legal consequences of poor safety management.'
            }
          ]
        }
      },
      {
        id: 'risk-assessment',
        title: 'Risk Assessment and Management',
        content: {
          overview: 'Systematic approach to identifying and controlling workplace risks',
          learningPoints: [
            'Risk assessment methodology: identification, evaluation, control',
            'Hierarchy of risk controls: elimination, substitution, engineering, administrative, PPE',
            'Healthcare-specific hazards: biological, chemical, physical, ergonomic',
            'Documentation and review of risk assessments',
            'Involving staff in risk assessment processes'
          ],
          educationalContent: [
            {
              title: 'Risk Assessment Methodology in Healthcare',
              content: 'Effective risk assessment follows a systematic five-step process that must be applied consistently across healthcare settings. Step 1 involves identifying hazards by walking through the workplace, observing activities, consulting staff, reviewing incident reports, and considering all aspects of work including emergency procedures. Step 2 determines who might be harmed - this includes staff, patients, visitors, contractors, and the public - and considers how they might be harmed, including long-term health effects. Step 3 evaluates risks by considering the likelihood and severity of harm, existing control measures, and the effectiveness of current precautions. Step 4 records significant findings and implements additional control measures where necessary. Step 5 involves regular review and updating, especially when there are changes to procedures, equipment, or incidents occur. In healthcare, this process must consider the dynamic nature of clinical environments, varying patient acuity, and the interaction between different types of hazards.'
            },
            {
              title: 'Understanding the Hierarchy of Risk Controls',
              content: 'The hierarchy of controls provides a systematic approach to selecting the most effective risk control measures, listed from most to least effective. Elimination completely removes the hazard - for example, eliminating the use of latex gloves to prevent allergic reactions. Substitution replaces the hazard with something less dangerous - using safer cleaning chemicals or needleless injection systems. Engineering controls isolate people from hazards through design - installing sharps disposal units, improving ventilation systems, or providing lifting equipment. Administrative controls change how people work - implementing procedures, training, job rotation, or warning signs. Personal Protective Equipment (PPE) protects individual workers - gloves, masks, aprons, or eyewear. Effective risk management typically combines multiple levels of control, but relies primarily on the higher-level controls rather than just PPE. Understanding this hierarchy helps prioritize the most effective and sustainable safety solutions.'
            },
            {
              title: 'Healthcare-Specific Hazard Categories',
              content: 'Healthcare environments present unique combinations of hazards that require specialized understanding and control measures. Biological hazards include infectious diseases (bloodborne pathogens, respiratory infections, multi-resistant organisms), contaminated sharps, and biological waste. These require strict infection control procedures, appropriate PPE, safe disposal systems, and vaccination programs. Chemical hazards encompass medications (especially cytotoxic drugs), cleaning agents, anesthetic gases, laboratory chemicals, and disinfectants. Control measures include proper storage, handling procedures, ventilation systems, and emergency response protocols. Physical hazards include radiation from X-ray equipment, noise from machinery, temperature extremes, and slips/trips/falls from wet floors or cluttered areas. Ergonomic hazards result from manual handling of patients, prolonged standing, repetitive movements, and awkward postures. Psychosocial hazards include workplace stress, violence, shift work, and emotional demands. Each category requires specific assessment approaches and targeted control measures.'
            },
            {
              title: 'Documentation and Review Processes',
              content: 'Proper documentation of risk assessments is both a legal requirement and essential for effective risk management. Documentation must include the assessment date, assessor details, hazards identified, people at risk, existing controls, additional measures needed, implementation timescales, and review dates. In healthcare, assessments should be specific to different areas (wards, theatres, departments) and activities (patient handling, medication administration, cleaning). Documentation should be accessible to relevant staff and regularly communicated through team meetings and training sessions. Review processes must be systematic, with scheduled regular reviews (typically annually) and event-triggered reviews following incidents, near misses, changes in procedures, or introduction of new equipment. Reviews should involve frontline staff who understand practical challenges and can identify new hazards or control measure failures. Effective review processes create a continuous improvement cycle that enhances safety over time.'
            },
            {
              title: 'Staff Involvement in Risk Assessment',
              content: 'Meaningful staff involvement is crucial for effective risk assessment because frontline workers have detailed knowledge of actual working conditions, potential hazards, and practical control measures. Involvement should occur throughout the process - from initial hazard identification through to monitoring control measure effectiveness. Staff should be encouraged to report hazards, near misses, and suggestions for improvement without fear of blame or retribution. This requires creating a positive safety culture where reporting is seen as helpful rather than troublesome. Consultation methods include safety committees, regular team meetings, suggestion schemes, safety walks with managers, and incident investigation participation. Training staff in risk assessment principles helps them contribute more effectively and understand why certain control measures are necessary. When staff are involved in developing control measures, they are more likely to implement them consistently. Regular feedback to staff about how their input has influenced safety improvements reinforces the value of their participation and encourages continued engagement.'
            }
          ]
        }
      },
      {
        id: 'incident-reporting',
        title: 'Incident Reporting and Investigation',
        content: {
          overview: 'Effective systems for reporting, investigating, and learning from incidents',
          learningPoints: [
            'Types of incidents: accidents, near misses, occupational health issues',
            'Immediate response and emergency procedures',
            'Reporting systems and timescales',
            'Investigation techniques and root cause analysis',
            'Learning from incidents and implementing improvements'
          ],
          educationalContent: [
            {
              title: 'Understanding Different Types of Incidents',
              content: 'Healthcare incidents fall into several categories, each requiring appropriate recognition and response. Accidents involve actual injury or damage - such as needlestick injuries, medication errors causing harm, patient falls, or equipment failures resulting in injury. Near misses are events that could have caused harm but didn\'t, either through chance or timely intervention - like catching a medication error before administration, preventing a patient fall, or identifying faulty equipment before use. Occupational health issues include work-related illnesses such as back injuries from manual handling, stress-related conditions, exposure to infectious diseases, or allergic reactions to workplace substances. Dangerous occurrences are events with potential for serious harm even if no one was actually injured - such as oxygen tank explosions, major chemical spills, or structural failures. Understanding these categories is crucial because all types provide valuable learning opportunities, not just those that result in actual harm. Near misses are particularly valuable because they highlight weaknesses before serious harm occurs.'
            },
            {
              title: 'Immediate Response and Emergency Procedures',
              content: 'The immediate response to incidents must prioritize safety and appropriate care while preserving evidence for investigation. First priority is always ensuring the safety of the injured person and others in the area - this might involve first aid, emergency medical treatment, or evacuation. Simultaneously, secure the scene to prevent further incidents and preserve evidence for investigation. This includes photographing the area if safe to do so, identifying witnesses, and ensuring equipment or substances involved are not moved unnecessarily. Emergency procedures must be activated when appropriate - calling emergency services, notifying management, or implementing specific protocols for different types of incidents. Documentation should begin immediately with basic facts: time, location, people involved, immediate actions taken, and witness details. Communication is crucial - informing relevant managers, family members (if appropriate), and regulatory bodies as required. The key is having clear procedures that everyone knows and practices, so the response is automatic even under stress.'
            },
            {
              title: 'Reporting Systems and Legal Timescales',
              content: 'Healthcare organizations must have robust reporting systems that ensure appropriate information reaches the right people within required timescales. Internal reporting should be immediate for serious incidents, with formal reports completed within 24-48 hours for most incidents. Different types of incidents have different reporting chains - patient safety incidents may need to go to clinical governance teams, while staff injuries go to occupational health and management. External reporting requirements vary by incident type and severity. RIDDOR reports to HSE must be made immediately for deaths and serious injuries, within 10 days for other injuries causing more than 7 days off work, and as soon as diagnosed for occupational diseases. Care Quality Commission notifications may be required for patient safety incidents. Professional body reporting might be necessary for clinical incidents. Understanding who needs to know what, when, helps ensure compliance and supports learning. Modern reporting systems should be user-friendly, accessible 24/7, and provide feedback to reporters about outcomes and actions taken.'
            },
            {
              title: 'Investigation Techniques and Root Cause Analysis',
              content: 'Effective incident investigation goes beyond identifying obvious causes to understand underlying system failures that allowed the incident to occur. Root cause analysis (RCA) is a systematic approach that asks "why" repeatedly to trace back from the immediate cause to fundamental issues. The investigation team should include people with relevant expertise and those independent of the incident. Data collection involves interviewing witnesses, reviewing relevant policies and procedures, examining physical evidence, and analyzing contributing factors like workload, training, equipment, and environmental conditions. Timeline reconstruction helps understand the sequence of events and decision points. Human factors analysis considers how design, training, fatigue, communication, and other factors influenced human performance. System analysis examines whether policies, procedures, equipment, and oversight were adequate and properly implemented. The goal is identifying practical recommendations that prevent recurrence, not blame. Good investigations balance thoroughness with timeliness, ensuring findings are available while memories are fresh and changes can be implemented quickly.'
            },
            {
              title: 'Learning from Incidents and Implementing Improvements',
              content: 'The ultimate purpose of incident reporting and investigation is learning and improvement, not blame or punishment. Effective learning systems share findings widely, implement changes systematically, and monitor effectiveness over time. Analysis should identify patterns and trends across multiple incidents, not just focus on individual events. This might reveal systemic issues like inadequate training, poor equipment design, or staffing problems that contribute to various types of incidents. Recommendations must be SMART (Specific, Measurable, Achievable, Relevant, Time-bound) and address root causes rather than just symptoms. Implementation should be planned carefully with clear responsibilities, timelines, and success measures. Communication of learning should reach all relevant staff through multiple channels - training sessions, team meetings, newsletters, and policy updates. Regular monitoring and evaluation ensures changes are working as intended and identifies any unintended consequences. Creating a learning culture requires leadership commitment to improvement over blame, staff confidence in reporting systems, and visible evidence that reporting leads to positive changes. This encourages continued reporting and engagement with safety initiatives.'
            }
          ]
        }
      }
    ],
    assessment: {
      passingScore: 80,
      questions: [
        {
          id: 1,
          type: 'multiple-choice',
          question: 'Under the Health and Safety at Work Act 1974, what is the primary duty of employers?',
          options: [
            'To provide free personal protective equipment to all staff',
            'To ensure, so far as is reasonably practicable, the health, safety and welfare of employees',
            'To conduct annual health and safety training for all staff',
            'To report all accidents to the Health and Safety Executive'
          ],
          correctAnswer: 1,
          explanation: 'Section 2 of the Health and Safety at Work Act 1974 places a general duty on employers to ensure, so far as is reasonably practicable, the health, safety and welfare at work of all employees.'
        },
        {
          id: 2,
          type: 'multiple-choice',
          question: 'What does RIDDOR stand for?',
          options: [
            'Risk Identification and Dangerous Discharge Occurrence Regulations',
            'Reporting of Injuries, Diseases and Dangerous Occurrences Regulations',
            'Risk Investigation and Documentation of Dangerous Occurrence Reports',
            'Regulatory Investigation of Dangerous Incidents and Occurrence Reports'
          ],
          correctAnswer: 1,
          explanation: 'RIDDOR stands for Reporting of Injuries, Diseases and Dangerous Occurrences Regulations, which requires employers to report certain workplace incidents to the Health and Safety Executive.'
        },
        {
          id: 3,
          type: 'multiple-choice',
          question: 'In the hierarchy of risk controls, which is the most effective method?',
          options: [
            'Personal Protective Equipment (PPE)',
            'Administrative controls and training',
            'Elimination of the hazard',
            'Engineering controls and guards'
          ],
          correctAnswer: 2,
          explanation: 'Elimination of the hazard is the most effective control method in the hierarchy of risk controls, as it completely removes the risk rather than just reducing exposure.'
        },
        {
          id: 4,
          type: 'multiple-choice',
          question: 'Which of the following incidents must be reported under RIDDOR?',
          options: [
            'Any injury requiring first aid treatment',
            'A work-related injury resulting in over 7 days off work',
            'All near miss incidents with no injury',
            'Minor cuts and bruises occurring at work'
          ],
          correctAnswer: 1,
          explanation: 'Under RIDDOR, work-related injuries that result in a worker being away from work or unable to perform their normal work duties for more than 7 consecutive days must be reported.'
        },
        {
          id: 5,
          type: 'multiple-choice',
          question: 'What does COSHH stand for in healthcare settings?',
          options: [
            'Control of Safe Handling of Hazardous materials',
            'Committee on Safety and Health in Healthcare',
            'Control of Substances Hazardous to Health',
            'Clinical Oversight of Staff Health and Hygiene'
          ],
          correctAnswer: 2,
          explanation: 'COSHH stands for Control of Substances Hazardous to Health, regulations that require employers to control exposure to hazardous substances in the workplace.'
        },
        {
          id: 6,
          type: 'multiple-choice',
          question: 'Who has the legal duty to take reasonable care for their own health and safety at work?',
          options: [
            'Only the employer',
            'Only the health and safety representative',
            'Only senior management',
            'Every employee'
          ],
          correctAnswer: 3,
          explanation: 'Under Section 7 of the Health and Safety at Work Act 1974, every employee has a legal duty to take reasonable care for their own health and safety and that of others who may be affected by their actions.'
        },
        {
          id: 7,
          type: 'multiple-choice',
          question: 'What should be the immediate priority following a workplace accident?',
          options: [
            'Complete the incident report form',
            'Investigate the cause of the accident',
            'Ensure the safety of the injured person and others',
            'Notify the Health and Safety Executive'
          ],
          correctAnswer: 2,
          explanation: 'The immediate priority following any workplace accident is to ensure the safety and wellbeing of the injured person and prevent further harm to others.'
        },
        {
          id: 8,
          type: 'multiple-choice',
          question: 'How often should risk assessments be reviewed in healthcare settings?',
          options: [
            'Every 5 years regardless of changes',
            'Only when accidents occur',
            'Regularly and when significant changes occur',
            'Once annually on a fixed date'
          ],
          correctAnswer: 2,
          explanation: 'Risk assessments should be reviewed regularly and whenever there are significant changes to work activities, equipment, or when incidents occur that may indicate the assessment needs updating.'
        },
        {
          id: 9,
          type: 'multiple-choice',
          question: 'What is a "near miss" incident?',
          options: [
            'An accident that resulted in minor injury only',
            'An event that could have resulted in injury but did not',
            'An incident that happened nearly a month ago',
            'A safety inspection that nearly found problems'
          ],
          correctAnswer: 1,
          explanation: 'A near miss is an unplanned event that could have resulted in injury, illness, or damage but did not. Reporting near misses helps prevent future accidents.'
        },
        {
          id: 10,
          type: 'multiple-choice',
          question: 'Which statement best describes the concept of "reasonably practicable" in health and safety law?',
          options: [
            'Doing everything possible regardless of cost',
            'Balancing the risk against the cost and difficulty of preventing it',
            'Following minimum legal requirements only',
            'Implementing the cheapest available safety measures'
          ],
          correctAnswer: 1,
          explanation: '"Reasonably practicable" means balancing the risk against the trouble, time and money needed to control it. The greater the risk, the more likely it is reasonable to go to substantial expense to control it.'
        }
      ]
    }
  };

  // Training content and handlers
  const handleStartTraining = (courseId) => {
    setCurrentTraining(courseId);
    setCurrentSection(0);
    setAssessmentAnswers({});
    setAssessmentComplete(false);
    setAssessmentScore(0);
    navigateToStep('training-module');
  };

  // Save training progress to database
  const saveTrainingProgress = async (courseId, progressData) => {
    try {
      const status = progressData.completed ? 'Completed' : 'InProgress';
      const score = progressData.score !== undefined ? progressData.score : assessmentScore;
      const sectionProgressData = JSON.stringify({
        currentSection: progressData.currentSection !== undefined ? progressData.currentSection : currentSection,
        answers: progressData.answers || assessmentAnswers
      });

      await apiService.saveTrainingProgress(
        nurseProfile.professionalId,
        courseId,
        status,
        score,
        sectionProgressData
      );
    } catch (error) {
      console.error('Failed to save training progress:', error);
    }
  };

  // Load training progress from database
  const loadTrainingProgress = async (courseId) => {
    try {
      const progressResponse = await apiService.getTrainingProgressForProfessional(nurseProfile.professionalId);
      const existingProgress = (progressResponse as any).find((p: any) => p.courseId === courseId);

      if (existingProgress) {
        // Parse sectionProgress if it exists, otherwise start from 0
        const savedSectionProgress = existingProgress.sectionProgress ?
          JSON.parse(existingProgress.sectionProgress) : { currentSection: 0 };
        setCurrentSection(savedSectionProgress.currentSection || 0);
        setAssessmentComplete(existingProgress.status === 'Completed');
        setAssessmentScore(existingProgress.latestScore || 0);
        if (savedSectionProgress.answers) {
          setAssessmentAnswers(savedSectionProgress.answers);
        }
        return existingProgress;
      }
      return null;
    } catch (error) {
      console.error('Failed to load training progress:', error);
      return null;
    }
  };

  // Database-driven training handler
  const handleStartDatabaseTraining = async (courseId) => {
    try {
      console.log('Starting database training for courseId:', courseId);

      // Load course content from database
      await loadCourseContent(courseId);

      // Load existing progress for this course from database (for resume functionality)
      await loadTrainingProgress(courseId);

      // Reset training state to allow fresh training sessions
      // (even if course was previously completed, user can retake)
      setCurrentSection(0);
      setAssessmentComplete(false);
      setAssessmentScore(0);
      setAssessmentAnswers({});

      // Set current training state and persist to session storage
      setCurrentTraining(courseId);
      saveToStorage('intastream_current_training', courseId, true);
      saveToStorage('intastream_current_section', 0, true);
      saveToStorage('intastream_assessment_answers', {}, true);

      // Navigate to training module
      navigateToStep('training-module');
    } catch (error) {
      console.error('Failed to start training:', error);
      showNotification('Failed to load training course', 'error');
    }
  };

  const handleAssessmentAnswer = async (questionId, selectedAnswer) => {
    const updatedAnswers = {
      ...assessmentAnswers,
      [questionId]: selectedAnswer
    };
    setAssessmentAnswers(updatedAnswers);

    // Save progress to database (answers only)
    if (currentTraining) {
      await saveTrainingProgress(currentTraining, { answers: updatedAnswers });
    }
  };

  // Helper function to navigate to section and save progress
  const navigateToSection = async (sectionIndex) => {
    setCurrentSection(sectionIndex);

    // Save current section to session storage for persistence
    saveToStorage('intastream_current_section', sectionIndex, true);

    // Save current section progress to database
    if (currentTraining) {
      await saveTrainingProgress(currentTraining, { currentSection: sectionIndex });
    }
  };

  const handleSubmitAssessment = async () => {
    // Get assessment from database course content
    let assessment;
    try {
      // Parse assessment from JSON string
      const assessmentData = typeof currentCourseContent.assessment === 'string'
        ? JSON.parse(currentCourseContent.assessment)
        : currentCourseContent.assessment;

      // Convert assessment format to match UI expectations
      assessment = {
        passingScore: currentCourseContent.passingScore || 70,
        questions: assessmentData?.map((q, index) => ({
          id: `q${index + 1}`,
          question: q.question,
          options: q.options,
          correctAnswer: q.correct, // Database uses 'correct' field with index
          explanation: q.explanation
        })) || []
      };
    } catch (error) {
      console.error('Failed to parse course content for assessment:', error);
      return;
    }

    if (!assessment || !assessment.questions || assessment.questions.length === 0) {
      console.error('No assessment questions found in course content');
      return;
    }

    let correctAnswers = 0;

    assessment.questions.forEach(question => {
      if (assessmentAnswers[question.id] === question.correctAnswer) {
        correctAnswers++;
      }
    });

    const score = Math.round((correctAnswers / assessment.questions.length) * 100);
    setAssessmentScore(score);
    setAssessmentComplete(true);

    // Save progress to database
    try {
      const course = dbCourses.find(c => c.courseId === currentTraining);
      const status = score >= (assessment.passingScore || 70) ? 'Completed' : 'Failed';

      await (apiService.saveTrainingProgress as any)(
        nurseAuth.professionalId,
        currentTraining,
        status,
        score
      );

      // Mark certificate as issued if passed
      if (score >= (assessment.passingScore || 70)) {
        const certificateId = `${course?.courseCode || 'CERT'}-${Date.now()}-${nurseAuth.email.split('@')[0]}`;
        try {
          await (apiService as any).storeCertificate({
            professionalId: nurseAuth.professionalId,
            courseId: currentTraining,
            certificateId: certificateId,
            courseTitle: course?.courseName || 'Training Course',
            completionDate: new Date().toISOString(),
            expiryDate: calculateExpiryDate(currentTraining, new Date().toISOString()),
            score: score,
            filename: `${certificateId}_Certificate.pdf`,
            pdfData: ''
          });
        } catch (error) {
          console.error('Failed to mark certificate as issued:', error);
        }
      }

      if (score >= (assessment.passingScore || 70)) {
        // Passed - complete the training
        const completionDate = new Date().toISOString();
        const certificateId = `${course?.courseCode || 'CERT'}-${Date.now()}-${nurseAuth.email.split('@')[0]}`;
        const expiryDate = calculateExpiryDate(currentTraining, completionDate);

        const trainingData = {
          completed: true,
          completionDate: completionDate,
          expiryDate: expiryDate,
          score: score,
          certificateId: certificateId
        };

        setTrainingProgress({
          ...trainingProgress,
          [currentTraining]: trainingData
        });

        // Auto-generate certificate and clear session
        setTimeout(async () => {
          if (course) {
            await generateCertificate(currentTraining, course.courseName, trainingData);
          }

          // Clear training session data on successful completion
          saveToStorage('intastream_current_training', null, true);
          saveToStorage('intastream_current_section', 0, true);
          saveToStorage('intastream_assessment_answers', {}, true);
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to save training progress:', error);
      // Still allow completion locally even if database save fails
    }
  };

  const handleCompleteTraining = (courseId) => {
    setCurrentTraining(null);
    setCurrentStep('nurse-dashboard');
  };

  const calculateExpiryDate = (courseId, completionDate) => {
    const course = coreTraining.find(c => c.id === courseId);
    const completion = new Date(completionDate);

    switch (course?.renewalPeriod) {
      case 'Annual':
        completion.setFullYear(completion.getFullYear() + 1);
        break;
      case 'Biennial':
        completion.setFullYear(completion.getFullYear() + 2);
        break;
      case 'Triennial':
        completion.setFullYear(completion.getFullYear() + 3);
        break;
      default:
        completion.setFullYear(completion.getFullYear() + 1);
    }

    return completion.toISOString();
  };

  // Get week dates
  const getWeekDates = (date) => {
    const week = [];
    const startDate = new Date(date);
    const day = startDate.getDay();
    const diff = startDate.getDate() - day + (day === 0 ? -6 : 1);
    startDate.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      week.push(currentDate);
    }
    return week;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getShiftType = (startTime, endTime, startDate, endDate) => {
    // Night shift: starts at 8PM (20:00) and ends at 8AM (08:00) next day
    if (startTime === '20:00' && endTime === '08:00' && endDate !== startDate) {
      return 'Night Shift';
    }
    // Day shift: starts at 8AM (08:00) and ends at 8PM (20:00) same day
    if (startTime === '08:00' && endTime === '20:00' && endDate === startDate) {
      return 'Day Shift';
    }
    // Custom shift: any other time combination
    return 'Custom Shift';
  };

  const formatDateUK = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };


  // Load appointments when app starts if user is authenticated (but not for admin users)
  useEffect(() => {
    if (authForm.provider && currentStep === 'scheduler') {
      // Don't auto-load for admin users - they need to select a provider first
      const isAdmin = authForm.provider.userType === 'ADMIN' || authForm.provider.id === -1;
      if (!isAdmin) {
        loadAppointments(authForm.provider.id, { currentWeek }).catch(error => {
          console.error('Failed to load appointments on startup:', error);
          // Don't block the app if appointments fail to load
        });
      }
    }
  }, [currentStep, authForm.provider, currentWeek]); // Run when currentStep, provider, or currentWeek changes

  // Clear authentication state when navigating to landing page to ensure module separation
  useEffect(() => {
    if (currentStep === 'landing') {
      console.log('User navigated to landing page - clearing module-specific auth states for clean separation');

      // Clear nurse authentication state when returning to landing to prevent auto-redirect
      // This ensures IntaLearn doesn't interfere with landing page navigation
      setNurseAuth({
        email: '',
        provider: '',
        authenticated: false,
        professionalId: null,
        fullName: '',
        username: '',
        userType: ''
      });

      // Clear nurse profile state
      setNurseProfile({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        nursingRole: '',
        registrationNumber: '',
        profileComplete: false,
        fullName: ''
      });

      // Clear localStorage to prevent auto-redirects, but preserve cookies
      // This allows users to return to their authenticated modules if they choose
      // while preventing automatic redirects from the landing page
      localStorage.removeItem('intastream_nurse_auth');
      sessionStorage.removeItem('intastream_tab_current_step');
      sessionStorage.removeItem('intastream_tab_auth_type');
    }
  }, [currentStep]);

  const handleAuthSubmit = async () => {
    if (!authForm.username || !authForm.password || !authForm.termsAccepted) {
      showNotification('Please complete all required fields and accept the terms and conditions', 'warning');
      return;
    }

    setAuthForm(prev => ({ ...prev, isAuthenticating: true }));

    try {
      const response = await apiService.authenticateProvider(authForm.username, authForm.password);

      // The stored procedure GetValidatedUserRecord gives us everything we need
      const provider = {
        id: response.providerId || -1,  // Use providerId from stored procedure
        username: response.email,       // Email from stored procedure
        email: response.email,          // Email from stored procedure  
        userType: response.userType,    // "ADMIN" or "PRO_V" from stored procedure
        providerId: response.providerId, // ProviderId from stored procedure
        description: response.medicalRole || 'User', // Hospital name or "IntaStream Admin" from stored procedure
        fullName: response.forename || response.email, // FullName from stored procedure
        forename: response.forename,
        mobileNumber: response.mobileNumber
      };

      console.log('=== STORED PROCEDURE AUTHENTICATION ===');
      console.log('Raw API response:', response);
      console.log('UserType from DB:', response.userType);
      console.log('ProviderId from DB:', response.providerId);
      console.log('Email from DB:', response.email);
      console.log('FullName from DB:', response.forename);
      console.log('Created provider object:', provider);

      if (response.userType === 'ADMIN') {
        console.log('=== ADMIN USER LOGIN ===');
        console.log('- Admin can manage all hospitals via dropdown');
        console.log('- Email from stored procedure:', response.email);
        console.log('- ProviderId from stored procedure for admin:', response.providerId);

        // Admin user - IntaStream office staff who can manage all hospitals
        setAdminUser(provider);
        setAuthForm(prev => ({
          ...prev,
          isAuthenticating: false,
          provider: {
            ...provider,
            isAdminContext: true,
            adminUser: provider
          }
        }));

        // Auto-select provider if provided by stored procedure (for admin users with specific provider access)
        if (response.providerId && response.providerId !== -1) {
          console.log('Auto-selecting provider for admin user during login:', response.providerId);
          setSelectedProviderId(response.providerId.toString());
        }

        // Clear any existing appointment data for admin users
        setLiveAppointments([]);

        // Load providers list for dropdown - admin can select any hospital
        await loadProviders();

        // Auto-select provider for admin users
        // If stored procedure returned a specific providerId, use it; otherwise default to provider 8 (Burcot Hall Hospital)
        const providerToSelect = (response.providerId && response.providerId !== -1) ? response.providerId : 8;
        console.log('Auto-selecting provider for admin user:', providerToSelect);
        setSelectedProviderId(providerToSelect.toString());
        // Load appointments for the selected provider
        await loadAppointments(providerToSelect, { currentWeek });

        // Reset week view to current week for new user login
        const today = new Date();
        today.setHours(12, 0, 0, 0);
        setCurrentWeek(today);
        setSelectedDate(today);

        navigateToStep('scheduler');

      } else if (response.userType === 'PRO_V') {
        console.log('=== HOSPITAL USER LOGIN ===');
        console.log('- Hospital user can only see their hospital (no dropdown)');
        console.log('- ProviderId from stored procedure:', response.providerId);
        console.log('- Email from stored procedure:', response.email);

        // Hospital user (PRO_V) - can only see their own hospital's shifts
        setAuthForm(prev => ({
          ...prev,
          isAuthenticating: false,
          provider: provider
        }));

        // Load appointments directly for their hospital (no dropdown needed)
        await loadAppointments(response.providerId, { currentWeek });

        // Reset week view to current week for new user login
        const today = new Date();
        today.setHours(12, 0, 0, 0);
        setCurrentWeek(today);
        setSelectedDate(today);

        navigateToStep('scheduler');

      } else {
        console.error('Unknown user type from stored procedure:', response.userType);
        showNotification('Invalid user type. Please contact support.', 'error');
      }
    } catch (error) {
      setAuthForm(prev => ({ ...prev, isAuthenticating: false }));
      showNotification('Authentication failed. Please check your username and password.', 'error');
      console.error('Authentication error:', error);
    }
  };

  const handleProviderSelection = async () => {
    if (!selectedProviderId) {
      showNotification('Please select a provider to continue', 'warning');
      return;
    }

    try {
      // Find the selected provider details
      const selectedProvider = availableProviders.find(p => p.ProviderId.toString() === selectedProviderId);

      if (!selectedProvider) {
        showNotification('Selected provider not found', 'error');
        return;
      }

      // Create a provider object that combines admin user with selected provider context
      const providerContext = {
        ...adminUser,
        id: selectedProvider.ProviderId,
        description: selectedProvider.Description,
        isAdminContext: true,
        adminUser: adminUser
      };

      setAuthForm(prev => ({
        ...prev,
        provider: providerContext
      }));

      // Load appointments for the selected provider
      await loadAppointments(selectedProvider.ProviderId, { currentWeek });

      // Reset week view to current week for new provider selection
      const today = new Date();
      today.setHours(12, 0, 0, 0);
      setCurrentWeek(today);
      setSelectedDate(today);

      setIsSelectingProvider(false);
      navigateToStep('scheduler');
    } catch (error) {
      console.error('Error selecting provider:', error);
      showNotification('Failed to load provider data', 'error');
    }
  };

  const handleLogout = () => {
    // Clear authentication state
    setAuthForm({
      username: '',
      password: '',
      termsAccepted: false,
      isAuthenticating: false,
      provider: null
    });

    // Clear admin state
    setAdminUser(null);
    setSelectedProviderId('');

    // Reset week view to current week for next user
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    setCurrentWeek(today);
    setSelectedDate(today);
    setIsSelectingProvider(false);
    setAvailableProviders([]);

    // Clear appointments and other data
    setLiveAppointments([]);

    // Return to landing page
    navigateToStep('landing');
  };


  const handleNurseEmailAuth = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showNotification('Please enter a valid email address', 'warning');
      return false;
    }

    setNurseAuth({
      email: email,
      provider: 'email',
      authenticated: true
    });

    if (nurseProfile.profileComplete) {
      navigateToStep('nurse-dashboard');
    } else {
      navigateToStep('nurse-profile');
    }
    return true;
  };

  const handleNurseAuth = async (username, password) => {
    try {
      const response = await apiService.authenticateProfessional(username, password);

      // Check if this is an admin user
      if (response.userType === 'ADMIN') {
        // Admin user in training context - ONLY set nurseAuth, NOT authForm.provider
        // This prevents cross-system authentication contamination
        const adminUserInfo = {
          fullName: response.forename ? `${response.forename} ${response.surname || ''}`.trim() : 'Admin User',
          professionalId: response.professionalId
        };

        // Set up ONLY nurseAuth for admin in training context
        setNurseAuth({
          email: username,
          username: username,
          professionalId: response.professionalId,
          fullName: adminUserInfo.fullName,
          provider: 'database',
          authenticated: true,
          userType: 'ADMIN'
        });

        // Set nurse profile for admin user using new comprehensive response
        setNurseProfile({
          firstName: response.forename || 'Admin',
          lastName: response.surname || 'User',
          email: username,
          fullName: adminUserInfo.fullName,
          profileComplete: true, // Admin users don't need profile completion
          // Include additional profile data from the new API response
          nursingRole: response.medicalRole || 'Admin',
          mobileNumber: response.mobileNumber,
          addressLine1: response.addressLine1,
          addressLine2: response.addressLine2,
          addressLine3: response.addressLine3,
          addressLine4: response.addressLine4,
          postcode: response.postcode,
          telephoneNumber: response.telephoneNumber,
          dateOfBirth: response.dateOfBirth
        });

        // Go to nurse dashboard for admin users in training context
        navigateToStep('nurse-dashboard');
        return true;
      } else {
        // Regular professional user - use comprehensive API response
        // Set authentication state
        setNurseAuth({
          email: response.email || username,
          username: username,
          professionalId: response.professionalId,
          fullName: response.forename ? `${response.forename} ${response.surname || ''}`.trim() : username,
          provider: 'database',
          authenticated: true,
          userType: response.userType
        });

        // Populate nurse profile from comprehensive database response
        setNurseProfile({
          firstName: response.forename || '',
          lastName: response.surname || '',
          email: response.email || username,
          phoneNumber: response.mobileNumber || '',
          address: {
            street: response.addressLine1 || '',
            city: response.addressLine3 || '', // Using addressLine3 as city
            postcode: response.postcode || '',
            country: 'United Kingdom'
          },
          nursingRole: response.medicalRole || 'Not Assigned',
          registrationNumber: '',
          qualifications: [],
          profileComplete: true, // Mark as complete since data comes from database
          // Include all additional profile data from new API response
          fullName: response.forename ? `${response.forename} ${response.surname || ''}`.trim() : username,
          mobileNumber: response.mobileNumber,
          addressLine1: response.addressLine1,
          addressLine2: response.addressLine2,
          addressLine3: response.addressLine3,
          addressLine4: response.addressLine4,
          telephoneNumber: response.telephoneNumber,
          dateOfBirth: response.dateOfBirth
        });

        // Go directly to dashboard since profile is populated from database
        navigateToStep('nurse-dashboard');
        return true;
      }
    } catch (error) {
      console.error('Nurse authentication error:', error);
      showNotification('Authentication failed. Please check your username and password.', 'error');
      return false;
    }
  };

  const handleNurseProfileSubmit = () => {
    if (!nurseProfile.firstName || !nurseProfile.lastName || !nurseProfile.phoneNumber ||
      !nurseProfile.address?.street || !nurseProfile.address?.city || !nurseProfile.address?.postcode ||
      !nurseProfile.nursingRole) {
      showNotification('Please complete all required fields', 'warning');
      return;
    }

    setNurseProfile({
      ...nurseProfile,
      profileComplete: true
    });

    setCurrentStep('nurse-dashboard');
  };

  const handleBookingSubmit = async () => {
    if (!bookingForm.specialization || !bookingForm.startDate || !bookingForm.startTime ||
      !bookingForm.endDate || !bookingForm.endTime) {
      showNotification('Please complete all required fields', 'warning');
      return;
    }

    const startDateTime = new Date(`${bookingForm.startDate}T${bookingForm.startTime}`);
    const endDateTime = new Date(`${bookingForm.endDate}T${bookingForm.endTime}`);

    if (endDateTime <= startDateTime) {
      showNotification('End time must be after start time', 'warning');
      return;
    }

    if (editingBooking) {
      if (editingBooking.isLive && editingBooking.appointmentId) {
        // Update live appointment via API
        try {
          console.log('Updating appointment ID:', editingBooking.appointmentId);
          console.log('EditingBooking object:', editingBooking);

          const appointmentData = {
            startDateTime: startDateTime.toISOString(),
            endDateTime: endDateTime.toISOString(),
            location: bookingForm.specialization,
            notes: bookingForm.notes || '',
            assignedStaffId: bookingForm.assignedStaffId || ''
          };

          console.log('Appointment data to send:', appointmentData);

          const response = await apiService.updateAppointment(editingBooking.appointmentId, appointmentData);

          if ((response as any).success) {
            // Refresh live appointments to show updated data
            if (authForm.provider && authForm.provider.id) {
              const isAdmin = authForm.provider.userType === 'ADMIN' || authForm.provider.id === -1;
              if (isAdmin && selectedProviderId) {
                await loadAppointments(parseInt(selectedProviderId), { currentWeek });
              } else if (!isAdmin) {
                await loadAppointments(authForm.provider.id, { currentWeek });
              }
            }
            showNotification('Booking updated successfully!', 'success');
          }
        } catch (error) {
          console.error('Failed to update appointment:', error);
          showNotification('Failed to update booking. Please try again.', 'error');
          return;
        }
      } else {
        showNotification('You must be logged in to update bookings.', 'error');
        return;
      }
    } else {
      // Create new booking via API if authenticated
      if (authForm.provider) {
        try {
          // Use selected provider for admin, own provider for hospital users
          const targetProviderId = (authForm.provider.userType === 'ADMIN' && selectedProviderId)
            ? parseInt(selectedProviderId)
            : authForm.provider.id;

          const staffRequest = {
            startDateTime: startDateTime.toISOString(),
            endDateTime: endDateTime.toISOString(),
            location: bookingForm.specialization,
            requestedBy: authForm.provider.username,
            notes: bookingForm.notes || '',
            providerId: targetProviderId
          };

          console.log('Creating appointment with:', {
            userType: authForm.provider.userType,
            selectedProviderId,
            authFormProviderId: authForm.provider.id,
            targetProviderId
          });

          const response = await apiService.createStaffRequest(targetProviderId, staffRequest);

          if ((response as any).success) {
            // Reload appointments to show the new booking
            const isAdmin = authForm.provider.userType === 'ADMIN' || authForm.provider.id === -1;
            const providerIdToLoad = (isAdmin && selectedProviderId)
              ? parseInt(selectedProviderId)
              : authForm.provider.id;
            await loadAppointments(providerIdToLoad, { currentWeek });
            showNotification('Staff request created successfully', 'success');
          }
        } catch (error) {
          console.error('Error creating staff request:', error);
          showNotification('Failed to create staff request. Please try again.', 'error');
          return;
        }
      } else {
        showNotification('You must be logged in to create bookings.', 'error');
        return;
      }
    }

    closeBooking();
    setEditingBooking(null);
    setBookingForm({
      specialization: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      notes: '',
      assignedStaffId: ''
    });
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeek);
    newDate.setDate(currentWeek.getDate() + (direction * 7));
    setCurrentWeek(newDate);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleNewBooking = () => {
    const defaultDate = selectedDate && selectedDate instanceof Date ? selectedDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    setBookingForm({
      specialization: '',
      startDate: defaultDate,
      startTime: '08:00',
      endDate: defaultDate,
      endTime: '20:00',
      notes: '',
      assignedStaffId: ''
    });
    setEditingBooking(null);
    setHighlightSpecialization(true);
    openBooking();

    // Fade out the highlight after the modal opens
    setTimeout(() => {
      setHighlightSpecialization(false);
    }, 2000);
  };

  const handleEditBooking = (booking) => {
    setBookingForm({
      specialization: booking.specialization,
      startDate: booking.startDate,
      startTime: booking.startTime,
      endDate: booking.endDate,
      endTime: booking.endTime,
      notes: booking.notes,
      assignedStaffId: booking.assignedStaffId || ''
    });
    setEditingBooking(booking);
    setHighlightSpecialization(false); // No highlight when editing
    openBooking();
  };

  const setDayShift = () => {
    const startDate = bookingForm.startDate || new Date().toISOString().split('T')[0];
    setBookingForm({
      ...bookingForm,
      startTime: '08:00',
      endTime: '20:00',
      startDate: startDate,
      endDate: startDate
    });
  };

  const setNightShift = () => {
    const startDate = bookingForm.startDate || new Date().toISOString().split('T')[0];
    const nextDay = new Date(startDate);
    nextDay.setDate(nextDay.getDate() + 1);
    const endDate = nextDay.toISOString().split('T')[0];

    setBookingForm({
      ...bookingForm,
      startTime: '20:00',
      endTime: '08:00',
      startDate: startDate,
      endDate: endDate
    });
  };

  const handleDeleteBooking = (booking) => {
    setBookingToDelete(booking);
    openDeleteConfirm();
  };

  const confirmDelete = async () => {
    if (bookingToDelete) {
      try {
        if (bookingToDelete.isLive) {
          // For live appointments, call API to cancel the appointment
          await apiService.deleteAppointment(bookingToDelete.appointmentId);
          // Remove from local state immediately for better UX
          setLiveAppointments(liveAppointments.filter(appointment =>
            appointment.id !== bookingToDelete.appointmentId
          ));
        } else {
          showNotification('Cannot delete: booking not found in database.', 'error');
        }
      } catch (error) {
        console.error('Failed to delete appointment:', error);
        // Could show an error message to user here
      } finally {
        setBookingToDelete(null);
        closeDeleteConfirm();
      }
    }
  };

  // Touch handlers for mobile swipe navigation
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Swipe left - next day
      const newDate = new Date(selectedDate);
      newDate.setDate(selectedDate.getDate() + 1);
      setSelectedDate(newDate);
      setCurrentWeek(newDate);
    }
    if (isRightSwipe) {
      // Swipe right - previous day
      const newDate = new Date(selectedDate);
      newDate.setDate(selectedDate.getDate() - 1);
      setSelectedDate(newDate);
      setCurrentWeek(newDate);
    }
  };

  // Load medical roles from API
  const loadMedicalRoles = async () => {
    if (isLoadingMedicalRoles || medicalRoles.length > 0) return; // Prevent duplicate calls

    setIsLoadingMedicalRoles(true);
    try {
      const roles = await apiService.getMedicalRoles();
      setMedicalRoles(roles);
      console.log('Loaded medical roles:', roles.length);
    } catch (error) {
      console.error('Error loading medical roles:', error);
    } finally {
      setIsLoadingMedicalRoles(false);
    }
  };

  // Load professionals from API for admin staff assignment
  const loadProfessionals = async () => {
    if (isLoadingProfessionals || professionals.length > 0) return; // Prevent duplicate calls

    setIsLoadingProfessionals(true);
    try {
      const profs = await (apiService as any).getProfessionals();
      setProfessionals(profs);
      console.log('Loaded professionals:', profs.length);
    } catch (error) {
      console.error('Error loading professionals:', error);
    } finally {
      setIsLoadingProfessionals(false);
    }
  };

  // Load providers from API for admin user selection
  const loadProviders = async () => {
    try {
      const providers = await apiService.getProviders();
      setAvailableProviders(providers);
    } catch (error) {
      console.error('Error loading providers:', error);
      showNotification('Failed to load providers', 'error');
    }
  };

  // Load database courses for training system
  const loadDatabaseCourses = async (professionalId) => {
    if (coursesLoading || !professionalId) return;

    setCoursesLoading(true);
    setCoursesError(null);
    try {
      console.log('Loading database courses for professional ID:', professionalId);
      const courses = await apiService.getRequiredCoursesForProfessional(professionalId);
      console.log('Loaded database courses:', courses);
      setDbCourses(courses || []);

      // Also load training progress
      const progressData = await apiService.getTrainingProgressForProfessional(professionalId);
      console.log('Loaded training progress:', progressData);

      // Convert progress data to the format expected by the UI
      const progressMap = {};
      if (progressData && Array.isArray(progressData)) {
        progressData.forEach(progress => {
          progressMap[(progress as any).courseId] = {
            completed: (progress as any).status === 'Completed',
            completionDate: (progress as any).completionDate,
            expiryDate: (progress as any).expiryDate,
            score: (progress as any).latestScore,
            certificateId: (progress as any).certificateId
          };
        });
      }
      setTrainingProgress(progressMap);

    } catch (error) {
      console.error('Error loading database courses:', error);
      setCoursesError('Failed to load training courses: ' + error.message);
    } finally {
      setCoursesLoading(false);
    }
  };

  // Load course content from database
  const loadCourseContent = async (courseId) => {
    try {
      console.log('Loading course content for courseId:', courseId);
      const content = await apiService.getCourseContent(courseId);
      console.log('Loaded course content:', content);
      setCurrentCourseContent(content);
      return content;
    } catch (error) {
      console.error('Error loading course content:', error);
      throw error;
    }
  };

  // Staff Registration handlers
  const validateNINumber = (niNumber) => {
    // UK National Insurance Number format: 2 letters + 6 digits + 1 letter
    const niRegex = /^[A-CEGHJ-PR-TW-Z]{1}[A-CEGHJ-NPR-TW-Z]{1}[0-9]{6}[A-D]{1}$/i;
    return niRegex.test(niNumber.replace(/\s/g, ''));
  };

  const checkExistingSession = async (niNumber) => {
    if (!niNumber || niNumber.length < 9) {
      // Reset session check state if NI number is too short
      setRegistrationForm(prev => ({
        ...prev,
        hasExistingSession: false,
        sessionChecked: false
      }));
      return;
    }

    const trimmedNI = niNumber.replace(/\s/g, '').toUpperCase();

    if (!validateNINumber(trimmedNI)) {
      setRegistrationForm(prev => ({
        ...prev,
        hasExistingSession: false,
        sessionChecked: false
      }));
      return;
    }

    try {
      // Check for existing session first
      const existingSession = await loadRegistrationSession(trimmedNI);

      // If no session, check for existing professional record
      let existingProfessional = null;
      if (!existingSession) {
        try {
          existingProfessional = await apiService.getProfessionalByNI(trimmedNI);
        } catch (error) {
          console.log('No existing professional found during session check');
        }
      }

      setRegistrationForm(prev => ({
        ...prev,
        hasExistingSession: !!(existingSession || existingProfessional),
        sessionChecked: true,
        userType: existingProfessional?.UserType || 'USER'
      }));
    } catch (error) {
      console.error('Error checking for existing session:', error);
      setRegistrationForm(prev => ({
        ...prev,
        hasExistingSession: false,
        sessionChecked: true
      }));
    }
  };

  // Debounced session checking to avoid too many API calls
  const debouncedCheckExistingSession = React.useMemo(() => {
    let timeoutId;
    return (niNumber) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => checkExistingSession(niNumber), 500);
    };
  }, []);

  const handleBeginRegistration = async () => {
    const trimmedNI = registrationForm.niNumber.replace(/\s/g, '').toUpperCase();

    if (!trimmedNI) {
      showNotification('Please enter your National Insurance Number', 'warning');
      return;
    }

    if (!validateNINumber(trimmedNI)) {
      showNotification('Please enter a valid UK National Insurance Number (e.g., AB123456C)', 'warning');
      return;
    }

    setRegistrationForm({
      ...registrationForm,
      isValidating: true
    });

    try {
      // If we already know there's an existing session, load it
      if (registrationForm.hasExistingSession) {
        const existingSession = await loadRegistrationSession(trimmedNI);
        if (existingSession) {
          // Resume from existing session
          setRegistrationForm({
            ...registrationForm,
            niNumber: trimmedNI,
            currentStep: 'personal-details', // Start from step 1 but with loaded data
            isValidating: false
          });
          console.log('Resuming registration from existing session');
          return;
        }
      }

      // Check for existing professional record by NI number
      let existingProfessional = null;
      try {
        existingProfessional = await apiService.getProfessionalByNI(trimmedNI);
        console.log('Professional lookup result:', existingProfessional);

        // Update form state to show existing professional found
        if (existingProfessional) {
          setRegistrationForm(prev => ({
            ...prev,
            hasExistingSession: true,
            sessionChecked: true
          }));
        }
      } catch (error) {
        console.log('No existing professional found, starting fresh registration');
        setRegistrationForm(prev => ({
          ...prev,
          hasExistingSession: false,
          sessionChecked: true
        }));
      }

      // Save authentication state for registration system
      saveToStorage(STORAGE_KEYS.REGISTRATION_AUTH, {
        authenticated: true,
        niNumber: trimmedNI
      });

      // Save tab session to maintain state across refreshes
      saveToStorage(STORAGE_KEYS.TAB_CURRENT_STEP, 'staff-registration', true);
      saveToStorage(STORAGE_KEYS.TAB_AUTH_TYPE, 'registration', true);

      // Start fresh or pre-populate with existing professional data
      const baseFormData = {
        ...registrationData,
        nationalInsuranceNumber: trimmedNI
      };

      if (existingProfessional) {
        // Store admin status for admin panel display
        setRegistrationForm(prev => ({
          ...prev,
          userType: existingProfessional.UserType || 'USER'
        }));

        // Pre-populate form with existing professional data
        const prePopulatedData = {
          ...baseFormData,
          // Basic Information
          title: existingProfessional.Title || '',
          forename: existingProfessional.Forename || '',
          surname: existingProfessional.Surname || '',
          fullName: existingProfessional.FullName || '',
          emailAddress: existingProfessional.EmailAddress || '',
          mobilePhoneNumber: existingProfessional.MobileNumber || '',
          landLineNumber: existingProfessional.TelephoneNumber || '',
          dateOfBirth: existingProfessional.DateOfBirth || '',

          // Address Information
          addressLine1: existingProfessional.Address?.street || '',
          addressLine2: existingProfessional.Address?.line2 || '',
          addressLine3: existingProfessional.Address?.line3 || '',
          addressLine4: existingProfessional.Address?.line4 || '',
          addressLine5: existingProfessional.Address?.line5 || '',
          postcode: existingProfessional.Address?.postcode || '',

          // Professional Credentials
          nmcPinHpcNumber: existingProfessional.NMCPinHpcNumber || '',
          nmcPinHpcRegistrationDate: existingProfessional.NMCPinHpcRegistrationDate || '',
          nmcPinHpcExpiryDate: existingProfessional.NMCPinHpcExpiryDate || '',
          dbsNumber: existingProfessional.DBSNumber || '',
          dbsUpdateReference: existingProfessional.DBSUpdateReference || '',
          dbsRegistrationDate: existingProfessional.DBSRegistrationDate || '',
          dbsExpiryDate: existingProfessional.DBSExpiryDate || '',
          passportNumber: existingProfessional.PassportNumber || '',
          passportExpiryDate: existingProfessional.PassportExpiryDate || '',

          // Next of Kin
          nextOfKinAddress: existingProfessional.NextOfKinAddress || '',
          nextOfKinPhone: existingProfessional.NextOfKinPhone || '',
          nextOfKinRelationship: existingProfessional.NextOfKinRelationship || '',

          // Immigration & Financial
          visaExpiryDate: existingProfessional.VisaExpiryDate || '',
          hasBritishEUPassport: existingProfessional.HasBritishEUPassport,
          isEUCitizen: existingProfessional.IsEUCitizen,
          otherWorkPermits: existingProfessional.OtherWorkPermits || '',
          bankAccountName: existingProfessional.BankAccountName || '',
          bankAccountNumber: existingProfessional.BankAccountNumber || '',
          bankSortCode: existingProfessional.BankSortCode || '',

          // Work Preferences
          startDate: existingProfessional.StartDate || '',
          hasProfessionalIndemnity: existingProfessional.HasProfessionalIndemnity,
          professionalIndemnityNumber: existingProfessional.ProfessionalIndemnityNumber || '',
          professionalIndemnityEffectiveDate: existingProfessional.ProfessionalIndemnityEffectiveDate || '',

          // Medical & Specializations
          medicalRoleId: existingProfessional.MedicalRoleId || null,
          assignedMedicalRole: existingProfessional.MedicalRole || '',
          specializations: existingProfessional.Specializations || {},
          competencyScores: existingProfessional.CompetencyScores || {}
        };

        setRegistrationData(prePopulatedData);

        // Set the professional ID for updates
        setRegistrationForm({
          ...registrationForm,
          niNumber: trimmedNI,
          currentStep: 'personal-details',
          isValidating: false,
          professionalId: existingProfessional.ProfessionalId
        });

        console.log('Pre-populated registration form with existing professional data');
      } else {
        // Start with fresh form
        setRegistrationData(baseFormData);
        setRegistrationForm({
          ...registrationForm,
          niNumber: trimmedNI,
          currentStep: 'personal-details',
          isValidating: false,
          professionalId: null
        });
        console.log('Starting new registration');
      }

    } catch (error) {
      console.error('Error during registration start:', error);
      // Continue with new registration if checks fail
      setRegistrationForm({
        ...registrationForm,
        niNumber: trimmedNI,
        currentStep: 'personal-details',
        isValidating: false
      });
    }
  };


  // File upload service
  const ComplianceItemType = {
    CV: 0,
    DBS: 1,
    NMC: 2,
    PassportScan: 3,
    ImmunisationRecord: 4,
    FortyEightHourOptOut: 5,
    References: 6,
    Qualifications: 7,
    IndemnityInsurance: 8,
    ApplicationForm: 9,
    CandidateContract: 10,
    IncorporationCertificate: 11,
    CompletedHealthQuestionnaire: 12
  };

  const fileTypeMapping = {
    cv: ComplianceItemType.CV,
    photoId: ComplianceItemType.PassportScan,
    dbsCertificate: ComplianceItemType.DBS,
    nmcCertificate: ComplianceItemType.NMC,
    passportScans: ComplianceItemType.PassportScan,
    immunizationRecords: ComplianceItemType.ImmunisationRecord,
    references: ComplianceItemType.References,
    professionalQualifications: ComplianceItemType.Qualifications,
    professionalIndemnityInsurance: ComplianceItemType.IndemnityInsurance,
    companyFormationCertificate: ComplianceItemType.IncorporationCertificate
  };

  const createDraftProfessionalRecord = async (niNumber) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/professionals/draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nationalInsuranceNumber: niNumber
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log(`Created draft professional record for NI: ${niNumber} with ID: ${result.professionalId}`);

      return result.professionalId;
    } catch (error) {
      console.error('Error creating draft professional record:', error);
      throw error;
    }
  };

  const uploadFile = async (file, fileType, professionalId) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fieldDataType', fileTypeMapping[fileType]);
      formData.append('fileName', file.name);
      formData.append('fileSize', file.size);

      const response = await fetch(`${getApiBaseUrl()}/professionals/${professionalId}/files`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log(`File uploaded: ${file.name} with ID: ${result.fileId}`);

      return result.fileId;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleFileUpload = async (file, fileType) => {
    try {
      // Update state to show uploading
      setRegistrationData(prev => ({
        ...prev,
        files: {
          ...prev.files,
          [fileType]: {
            ...prev.files[fileType],
            file: file,
            uploading: true,
            fileName: file.name
          }
        }
      }));

      // Ensure we have a professional ID
      let professionalId = registrationForm.professionalId;
      if (!professionalId) {
        professionalId = await createDraftProfessionalRecord(registrationForm.niNumber);
        setRegistrationForm(prev => ({
          ...prev,
          professionalId: professionalId
        }));
      }

      // Upload the file
      const fileId = await uploadFile(file, fileType, professionalId);

      // Update state to show upload complete
      setRegistrationData(prev => ({
        ...prev,
        files: {
          ...prev.files,
          [fileType]: {
            ...prev.files[fileType],
            uploading: false,
            uploaded: true,
            fileId: fileId
          }
        }
      }));

      console.log(`File ${file.name} uploaded successfully with ID: ${fileId}`);

      // Auto-save session after successful file upload
      await saveRegistrationSession();

    } catch (error) {
      console.error('File upload failed:', error);

      // Update state to show upload failed
      setRegistrationData(prev => ({
        ...prev,
        files: {
          ...prev.files,
          [fileType]: {
            ...prev.files[fileType],
            uploading: false,
            uploaded: false,
            file: null,
            fileName: ''
          }
        }
      }));

      showNotification('File upload failed. Please try again.', 'error');
    }
  };

  const saveRegistrationSession = async () => {
    try {
      if (!registrationForm.niNumber) {
        console.log('No NI number available, skipping session save');
        return;
      }

      const sessionData = {
        nationalInsuranceNumber: registrationForm.niNumber,
        currentStep: registrationForm.currentStep,
        professionalId: registrationForm.professionalId || 0,
        formData: JSON.stringify(registrationData)
      };

      const response = await fetch(`${getApiBaseUrl()}/registration/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sessionData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('Registration session saved successfully');
    } catch (error) {
      console.error('Error saving registration session:', error);
    }
  };

  const loadRegistrationSession = async (niNumber) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/registration/sessions/${niNumber}`);

      if (response.status === 404) {
        console.log('No existing session found for NI:', niNumber);
        return null;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const sessionData = await response.json();
      const formData = JSON.parse(sessionData.formData);

      console.log('Loading existing session for NI:', niNumber);

      // Restore form data
      setRegistrationData(formData);

      // Restore professional ID if available
      if (sessionData.professionalId) {
        setRegistrationForm(prev => ({
          ...prev,
          professionalId: sessionData.professionalId
        }));
      }

      return sessionData;
    } catch (error) {
      console.error('Error loading registration session:', error);
      return null;
    }
  };

  const navigateToRegistrationStep = async (stepName) => {
    try {
      // Save current session before navigating
      await saveRegistrationSession();

      // Navigate to the new step
      setRegistrationForm(prev => ({
        ...prev,
        currentStep: stepName
      }));

      console.log(`Navigated to step: ${stepName}`);
    } catch (error) {
      console.error('Error during step navigation:', error);
      // Still navigate even if save fails
      setRegistrationForm(prev => ({
        ...prev,
        currentStep: stepName
      }));
    }
  };

  const completeRegistration = async () => {
    try {
      if (!registrationForm.niNumber) {
        showNotification('Missing National Insurance Number', 'error');
        return;
      }

      // Save final session state
      await saveRegistrationSession();

      // Complete the registration
      const response = await fetch(`${getApiBaseUrl()}/registration/complete/${registrationForm.niNumber}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      showNotification(`Profile saved successfully! Your Professional ID is: ${result.professionalId}`, 'success', 'Registration Complete');

      // Navigate back to landing page
      navigateToStep('landing');

      // Reset form data
      setRegistrationForm({
        niNumber: '',
        currentStep: 'welcome',
        isValidating: false,
        professionalId: null,
        hasExistingSession: false,
        sessionChecked: false,
        userType: 'USER'
      });

      // Reset registration data
      setRegistrationData({
        // Personal Information
        title: '',
        dateOfBirth: '',
        forename: '',
        surname: '',
        fullName: '',

        // Professional Credentials
        nmcPinHpcNumber: '',
        nmcPinHpcRegistrationDate: '',
        nmcPinHpcExpiryDate: '',
        dbsNumber: '',
        dbsUpdateReference: '',
        dbsRegistrationDate: '',
        dbsExpiryDate: '',
        passportNumber: '',

        // Address Information
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        addressLine4: '',
        addressLine5: '',
        postcode: '',

        // Contact Information
        emailAddress: '',
        mobilePhoneNumber: '',
        landLineNumber: '',

        // Next of Kin
        nextOfKinName: '',
        nextOfKinPhone: '',
        nextOfKinRelationship: '',
        nextOfKinAddress: '',

        // Clinical Specializations
        specializations: {
          anaestheticsODP: false,
          hcaHealthCareAssistant: false,
          hduItu: false,
          paediatrics: false,
          recovery: false,
          sterilisationTechnician: false,
          wardRN: false,
          scrubGeneral: false,
          scrubCardiac: false,
          scrubENT: false,
          scrubGynaecology: false,
          scrubLaparoscopic: false,
          scrubMicrovascular: false,
          scrubNeuro: false,
          scrubOptha: false,
          scrubOrtho: false,
          scrubPlastics: false,
          scrubSpine: false,
          scrubThoracic: false,
          scrubUrology: false,
          scrubSfaAsp: false,
          emergencyRoomNurse: false
        },

        // Work Preferences
        workType: '',
        clinicalAreas: '',
        startDate: '',
        canDrive: null,
        hasProfessionalIndemnity: null,
        professionalIndemnityNumber: '',
        professionalIndemnityEffectiveDate: '',

        // Financial Information
        taxStatement: '',
        hasStudentLoan: null,
        studentLoanBeforeApril6: null,
        studentLoanDirectDebit: null,
        studentLoanType: '',
        hasPostgraduateLoan: null,
        postgraduateLoanBeforeApril6: null,
        postgraduateLoanDirectDebit: null,
        preferredPaymentMethod: '',
        bankAccountName: '',
        bankAccountNumber: '',
        bankSortCode: '',
        gpNameAddress: '',

        // Health & Medical
        hasIllnessDisability: null,
        workRelatedIllness: null,
        currentTreatment: null,
        needsAdjustments: null,
        hasMRSA: null,
        hasCDiff: null,
        tuberculosisOutsideUK: null,
        tuberculosisBCGVaccination: null,
        tuberculosisCough: null,
        tuberculosisWeightLoss: null,
        tuberculosisFever: null,
        tuberculosisContact: null,
        chickenPoxShingles: null,
        bloodBorneVirusContact: null,
        tripleVaccination: null,
        vaccinationSummary: '',
        covid19VaccinationStatus: '',

        // Immigration Status
        visaExpiryDate: '',
        hasBritishEUPassport: null,
        isEUCitizen: null,
        otherWorkPermits: '',

        // Employment History (3 employers)
        employers: [
          { nameAddress: '', position: '', dateFrom: '', dateTo: '' },
          { nameAddress: '', position: '', dateFrom: '', dateTo: '' },
          { nameAddress: '', position: '', dateFrom: '', dateTo: '' }
        ],

        // Qualifications (3 qualifications)
        qualifications: [
          { name: '', institution: '', dateFrom: '', dateTo: '' },
          { name: '', institution: '', dateFrom: '', dateTo: '' },
          { name: '', institution: '', dateFrom: '', dateTo: '' }
        ],

        // Professional References (2 references)
        references: [
          {
            employerName: '', workplaceAddress: '', startDate: '', endDate: '',
            refereeName: '', refereeTitle: '', refereeRelationship: '',
            refereePhone: '', refereeEmail: ''
          },
          {
            employerName: '', workplaceAddress: '', startDate: '', endDate: '',
            refereeName: '', refereeTitle: '', refereeRelationship: '',
            refereePhone: '', refereeEmail: ''
          }
        ],

        // Organizations (3 sets)
        organizations: [
          { name: '', number: '', date: '', expiryDate: '' },
          { name: '', number: '', date: '', expiryDate: '' },
          { name: '', number: '', date: '', expiryDate: '' }
        ],

        // Background Checks
        criminalConvictions: null,
        policeInquiries: null,
        otherCountryPoliceCheck: null,
        backgroundCheckDetails: '',
        fitnessToPracticeInvestigation: null,
        disciplinaryAction: null,

        // Agreement & Signature
        dateSigned: '',
        termsAccepted: false,
        emailAddressSignature: '',

        // Medical Role Assignment
        medicalRoleId: null,
        assignedMedicalRole: '',

        // Missing properties for TypeScript compliance
        passportExpiryDate: '',
        competencyScores: {
          anaestheticsODP: 0,
          hcaHealthCareAssistant: 0,
          hduItu: 0,
          paediatrics: 0,
          recovery: 0,
          sterilisationTechnician: 0,
          wardRN: 0,
          scrubGeneral: 0,
          scrubCardiac: 0,
          scrubENT: 0,
          scrubGynaecology: 0,
          scrubLaparoscopic: 0,
          scrubMicrovascular: 0,
          scrubNeuro: 0,
          scrubOptha: 0,
          scrubOrtho: 0,
          scrubPlastics: 0,
          scrubSpine: 0,
          scrubThoracic: 0,
          scrubUrology: 0,
          scrubSfaAsp: 0,
          emergencyRoomNurse: 0
        },

        // File uploads
        files: {
          cv: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
          photoId: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
          dbsCertificate: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
          nmcCertificate: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
          passportScans: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
          immunizationRecords: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
          references: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
          professionalQualifications: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
          professionalIndemnityInsurance: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null },
          companyFormationCertificate: { file: null, uploading: false, uploaded: false, fileName: '', fileId: null }
        }
      });

      console.log('Registration completed and form reset');

    } catch (error) {
      console.error('Error completing registration:', error);
      showNotification('Profile save failed. Please try again.', 'error');
    }
  };

  const FileUploadControl = ({ fileType, label, description, required = false }) => {
    const fileState = registrationData.files[fileType];

    return (
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          {label} {required && <span style={{ color: 'red' }}>*</span>}
        </Text>
        {description && (
          <Text size="xs" c="dimmed">{description}</Text>
        )}

        <Group gap="sm">
          <input
            type="file"
            id={`file-${fileType}`}
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                handleFileUpload(file, fileType);
              }
            }}
            disabled={fileState.uploading}
          />

          <Button
            variant="light"
            size="sm"
            onClick={() => document.getElementById(`file-${fileType}`).click()}
            disabled={fileState.uploading}
            leftSection={fileState.uploading ? null : <DocumentArrowDownIcon style={{ width: '16px', height: '16px' }} />}
            loading={fileState.uploading}
          >
            {fileState.uploading ? 'Uploading...' : 'Choose File'}
          </Button>

          {fileState.uploaded && (
            <Group gap="xs">
              <CheckCircleIcon style={{ width: '16px', height: '16px', color: 'green' }} />
              <Text size="sm" c="green">
                {fileState.fileName}
              </Text>
            </Group>
          )}
        </Group>
      </Stack>
    );
  };

  const weekDates = getWeekDates(currentWeek);

  // Wait for theme to load before rendering to prevent flash
  if (!themeLoaded) {
    return (
      <MantineProvider forceColorScheme="light">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#ffffff'
        }}>
          <img
            src="/logo.png"
            alt="IntaStream Hub Logo"
            style={{
              height: '36px',
              marginBottom: '16px'
            }}
          />
          <Text size="sm" c="dimmed">Loading...</Text>
        </div>
      </MantineProvider>
    );
  }

  // Landing Page (check first)
  if (currentStep === 'landing') {
    const LandingPageContent = () => {
      const theme = useMantineTheme();
      const stats = [
        { label: 'Years Experience', value: '8+' },
        { label: 'Qualified Nurses', value: '500+' },
        { label: '', value: '100 Miles', subtitle: 'The Midlands Area' },
        { label: '', value: 'Rapid Reply', subtitle: 'Calls Answered Instantly' }
      ];

      const features = [
        {
          icon: <UserGroupIcon style={{ width: '24px', height: '24px' }} />,
          title: 'Expert Nursing Staff',
          description: 'Theatre specialists across all disciplines plus experienced ward nurses, all vetted and qualified.'
        },
        {
          icon: <ClockIcon style={{ width: '24px', height: '24px' }} />,
          title: 'Rapid Response',
          description: 'SMS notifications to nurses within a 100 mile radius of the midlands area. Fast matching and confirmation for urgent staffing needs.'
        },
        {
          icon: <ComputerDesktopIcon style={{ width: '24px', height: '24px' }} />,
          title: 'Advanced Technology',
          description: 'Bespoke software platform handling scheduling, invoicing, records, and real-time communications.'
        },
        {
          icon: <ShieldCheckIcon style={{ width: '24px', height: '24px' }} />,
          title: 'Trusted Partnership',
          description: 'Your organisation will be assigned an ex-medical professional who will be fully experienced in understanding your requirements and sourcing the best staff for the job.'
        }
      ];

      const specializations = [
        'Anaesthetics (ODP)', 'HDU/ITU', 'Pediatrics', 'Recovery', 'Scrub (General)',
        'Scrub (Cardiac)', 'Scrub (ENT)', 'Scrub (Gynae)', 'Scrub (Lap)', 'Scrub (Neuro)',
        'Scrub (Ortho)', 'Scrub (Plastics)', 'Ward (RN)', 'Emergency Room', 'HCA/Auxiliary'
      ];

      return (
        <Box style={{ minHeight: '100vh' }}>
          <Paper shadow="sm" p="md" radius={0}>
            <Container size="xl">
              <Group justify="space-between">
                <Group gap="md">
                  <img
                    src="/logo.png"
                    alt="IntaStream Hub Logo"
                    style={{
                      height: '48px',

                    }}
                  />
                  <div>
                    <Text size="lg" fw={600}>Welcome To The IntaStream Portal</Text>
                    <Group gap="xs" align="center" mt={2}>
                      <PhoneIcon style={{ width: '18px', height: '18px', color: '#ff6b35' }} />
                      <Text size="lg" fw={700} c="orange.6" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                        Call Us On: 07543 027 702
                      </Text>
                    </Group>
                  </div>
                </Group>
                <Group gap="md">
                  <Button
                    onClick={toggleColorScheme}
                    variant="light"
                    size="sm"
                    leftSection={colorScheme === 'dark' ?
                      <SunIcon style={{ width: '16px', height: '16px' }} /> :
                      <MoonIcon style={{ width: '16px', height: '16px' }} />
                    }
                  >
                    {colorScheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                  </Button>
                  {/* Request Staff - Development only */}
                  {process.env.NODE_ENV === 'development' && (
                    <Button
                      onClick={() => {
                        navigateToStep('initial-auth');
                      }}
                      size="md"
                      rightSection={<ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
                    >
                      Request Staff
                    </Button>
                  )}
                </Group>
              </Group>
            </Container>
          </Paper>

          <Container size="xl" py="xl">
            <Grid gutter="xl" align="center">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="xl">
                  <Title order={1} size="3.5rem" fw={700} lh={1.1}>
                    Expert Healthcare Staffing
                    <Text span c="blue" inherit> Rapid Response When You Need It</Text>
                  </Title>
                  <Text size="xl" c="dimmed" lh={1.6}>
                    With 8+ years of experience and 500+ qualified nurses, IntaStream delivers rapid,
                    reliable staffing solutions for hospitals across the UK. Our advanced technology
                    platform ensures you get the right staff, at the right time.
                  </Text>
                  <Stack gap="md" align="flex-start" style={{ width: 'fit-content' }}>
                    <Group gap="md">
                      {/* Request Staff - Development only */}
                      {process.env.NODE_ENV === 'development' && (
                        <Button
                          onClick={() => {
                            navigateToStep('initial-auth');
                          }}
                          size="xl"
                          radius="md"
                          style={{ fontWeight: 600, minWidth: '220px' }}
                          rightSection={<ArrowRightIcon style={{ width: '20px', height: '20px' }} />}
                        >
                          Request Staff
                        </Button>
                      )}
                      {/* IntaLearn - Development only */}
                      {process.env.NODE_ENV === 'development' && (
                        <Button
                          onClick={() => {
                            navigateToStep('nurse-auth');
                          }}
                          variant="light"
                          size="xl"
                          radius="md"
                          style={{ fontWeight: 600, minWidth: '240px' }}
                          leftSection={<AcademicCapIcon style={{ width: '20px', height: '20px' }} />}
                        >
                          IntaLearn (Mandatory Training)
                        </Button>
                      )}
                    </Group>
                    {/* IntaProfile - Development only */}
                    {process.env.NODE_ENV === 'development' && (
                      <Button
                        onClick={() => setCurrentStep('staff-registration')}
                        size="xl"
                        color="blue"
                        radius="md"
                        style={{ fontWeight: 600, minWidth: '240px' }}
                        leftSection={<UserIcon style={{ width: '20px', height: '20px' }} />}
                      >
                        IntaProfile (Staff Registration)
                      </Button>
                    )}

                    {/* Emergency Contact - Most Prominent */}
                    <Paper
                      p="lg"
                      radius="lg"
                      style={{
                        background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                        border: '3px solid #ff8c42',
                        boxShadow: '0 8px 32px rgba(255, 107, 53, 0.3)',
                        cursor: 'pointer',
                        transform: 'scale(1.02)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 12px 40px rgba(255, 107, 53, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 8px 32px rgba(255, 107, 53, 0.3)';
                      }}
                      onClick={() => window.open('tel:07543027702')}
                    >
                      <Group gap="lg" align="center" justify="center">
                        <PhoneIcon style={{ width: '32px', height: '32px', color: 'white', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
                        <div style={{ textAlign: 'center' }}>
                          <Text size="md" c="white" fw={600} style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>Need urgent staffing?</Text>
                          <Text size="2rem" fw={900} c="white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)', letterSpacing: '1px' }}>
                            07543 027 702
                          </Text>
                          {useMediaQuery('(max-width: 768px)') && (
                            <Text size="sm" c="white" fw={500} style={{ opacity: 0.9, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                              Click to call now
                            </Text>
                          )}
                        </div>
                      </Group>
                    </Paper>
                  </Stack>
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Paper p="xl" radius="lg" withBorder>
                  <img
                    src="/healthcare_industry.jpg"
                    alt="Healthcare professionals"
                    style={{ width: '100%', height: '300px', objectFit: 'cover', borderRadius: '8px' }}
                  />
                </Paper>
              </Grid.Col>
            </Grid>
          </Container>

          <Paper py="xl" bg={colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.blue[0]}>
            <Container size="xl">
              <Grid>
                {stats.map((stat, index) => (
                  <Grid.Col key={index} span={{ base: 6, sm: 3 }}>
                    <Stack align="center" gap="xs">
                      <Text size="3rem" fw={700} c="blue">{stat.value}</Text>
                      {stat.label && <Text size="lg" c="dimmed" ta="center">{stat.label}</Text>}
                      {stat.subtitle && <Text size="lg" c="dimmed" ta="center">{stat.subtitle}</Text>}
                    </Stack>
                  </Grid.Col>
                ))}
              </Grid>
            </Container>
          </Paper>

          <Container size="xl" py="xl">
            <Stack align="center" gap="xl" mb="xl">
              <Title order={2} ta="center" size="2.5rem">Why Choose IntaStream?</Title>
              <Text size="xl" c="dimmed" ta="center" maw={600}>
                Built by healthcare professionals for healthcare professionals.
                Our proven track record speaks for itself.
              </Text>
            </Stack>

            <Grid gutter="xl">
              {features.map((feature, index) => (
                <Grid.Col key={index} span={{ base: 12, md: 6 }}>
                  <Card p="xl" radius="lg" h="100%">
                    <Group gap="lg" align="flex-start">
                      <Paper p="md" radius="md" bg={colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.blue[0]}>
                        <Box c="blue">{feature.icon}</Box>
                      </Paper>
                      <Stack gap="sm" style={{ flex: 1 }}>
                        <Title order={3} size="1.5rem">{feature.title}</Title>
                        <Text c="dimmed" lh={1.6}>{feature.description}</Text>
                      </Stack>
                    </Group>
                  </Card>
                </Grid.Col>
              ))}
            </Grid>
          </Container>

          <Paper py="xl" bg={colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0]}>
            <Container size="xl">
              <Stack align="center" gap="xl">
                <Title order={2} ta="center" size="2.5rem">Our Specializations</Title>
                <Text size="xl" c="dimmed" ta="center" maw={700}>
                  From theatre specialists to ward nurses, we cover all major healthcare disciplines
                  with experienced, qualified professionals.
                </Text>
                <Grid gutter="md">
                  {specializations.map((spec, index) => (
                    <Grid.Col key={index} span={{ base: 6, sm: 4, md: 3 }}>
                      <Badge
                        variant="light"
                        size="lg"
                        radius="md"
                        style={{ width: '100%', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        {spec}
                      </Badge>
                    </Grid.Col>
                  ))}
                </Grid>
              </Stack>
            </Container>
          </Paper>

          <Container size="xl" py="xl">
            <Stack align="center" gap="xl">
              <Title order={2} ta="center" size="2.5rem">How It Works</Title>
              <Text size="xl" c="dimmed" ta="center" maw={600}>
                Our streamlined process gets you the staff you need, fast.
              </Text>

              <Timeline active={3} bulletSize={40} lineWidth={3}>
                <Timeline.Item bullet={<CheckCircleIcon style={{ width: '20px', height: '20px' }} />} title="IntaSchedule Request">
                  <Text c="dimmed" size="lg">
                    Submit your staffing request through our secure online platform.
                    Specify specialization, dates, and shift requirements.
                  </Text>
                </Timeline.Item>

                <Timeline.Item bullet={<UserGroupIcon style={{ width: '20px', height: '20px' }} />} title="Automated Matching">
                  <Text c="dimmed" size="lg">
                    Our system instantly matches qualified nurses within a 100 mile radius of the midlands area
                    and sends SMS notifications to available staff.
                  </Text>
                </Timeline.Item>

                <Timeline.Item bullet={<PhoneIcon style={{ width: '20px', height: '20px' }} />} title="Rapid Response">
                  <Text c="dimmed" size="lg">
                    Nurses respond via SMS. We screen responses & confirm suitability for the role & assign the best candidate.
                  </Text>
                </Timeline.Item>

                <Timeline.Item bullet={<ShieldCheckIcon style={{ width: '20px', height: '20px' }} />} title="Confirmed & Ready">
                  <Text c="dimmed" size="lg">
                    Booking confirmed! You receive instant notification and the assigned nurse receives all shift details & GPS navigation directions to your organisation.
                  </Text>
                </Timeline.Item>
              </Timeline>
            </Stack>
          </Container>

          <Paper py="xl" bg={theme.colors.blue[6]}>
            <Container size="xl">
              <Stack align="center" gap="xl">
                <Title order={2} ta="center" size="2.5rem" c="white">
                  Ready to Solve Your Staffing Challenges?
                </Title>
                <Text size="xl" c="white" ta="center" maw={600} style={{ opacity: 0.9 }}>
                  Join hospitals across the UK who trust IntaStream for reliable,
                  professional healthcare staffing solutions.
                </Text>
                <Stack gap="md" align="center">
                  <Group gap="md">
                    {/* Request Staff - Development only */}
                    {process.env.NODE_ENV === 'development' && (
                      <Button
                        onClick={() => {
                          navigateToStep('initial-auth');
                        }}
                        size="xl"
                        variant="white"
                        color="blue"
                        radius="md"
                        style={{ fontWeight: 600, fontSize: '1.1rem', padding: '16px 32px' }}
                        rightSection={<ArrowRightIcon style={{ width: '20px', height: '20px' }} />}
                      >
                        Request Staff
                      </Button>
                    )}
                    {/* IntaLearn - Development only */}
                    {process.env.NODE_ENV === 'development' && (
                      <Button
                        onClick={() => {
                          navigateToStep('nurse-auth');
                        }}
                        size="xl"
                        variant="outline"
                        color="white"
                        radius="md"
                        style={{ fontWeight: 600, fontSize: '1.1rem', padding: '16px 32px', borderColor: 'white', color: 'white' }}
                        leftSection={<AcademicCapIcon style={{ width: '20px', height: '20px' }} />}
                      >
                        IntaLearn (Mandatory Training)
                      </Button>
                    )}
                  </Group>
                  {/* IntaProfile - Development only */}
                  {process.env.NODE_ENV === 'development' && (
                    <Button
                      onClick={() => setCurrentStep('staff-registration')}
                      size="xl"
                      variant="outline"
                      color="white"
                      radius="md"
                      style={{ fontWeight: 600, fontSize: '1.1rem', padding: '16px 32px', borderColor: 'white', color: 'white' }}
                      leftSection={<UserIcon style={{ width: '20px', height: '20px' }} />}
                    >
                      IntaProfile (Staff Registration)
                    </Button>
                  )}

                  {/* Admin-only Data Mapping Button */}
                  {((authForm.provider &&
                    (authForm.provider.userType === 'ADMIN' ||
                      authForm.provider.email === 'maria@intastream.com' ||
                      authForm.provider.id === -1)) ||
                    (nurseAuth.authenticated && nurseAuth.userType === 'ADMIN')) && (
                      <Button
                        onClick={() => {
                          navigateToStep('csv-mapping-admin');
                        }}
                        size="lg"
                        variant="outline"
                        color="yellow"
                        radius="md"
                        style={{ fontWeight: 600, fontSize: '1rem', padding: '12px 24px', borderColor: 'yellow', color: 'yellow', width: '100%', maxWidth: '400px' }}
                        leftSection={<DocumentCheckIcon style={{ width: '18px', height: '18px' }} />}
                      >
                        Admin: CSV Data Mapping
                      </Button>
                    )}
                </Stack>
              </Stack>
            </Container>
          </Paper>

          <Paper p="xl" radius={0}>
            <Container size="xl">
              <Group justify="space-between">
                <Group gap="md">
                  <img
                    src="/logo.png"
                    alt="IntaStream Hub Logo"
                    style={{
                      height: '39px',

                    }}
                  />
                  <Text c="dimmed">© 2025 IntaStream Hub. Comprehensive Healthcare Solutions.</Text>
                </Group>
                <Group gap="lg">
                  <Anchor href="#" c="dimmed">Privacy Policy</Anchor>
                  <Anchor href="#" c="dimmed">Terms of Service</Anchor>
                  <Anchor href="#" c="dimmed">Contact Us</Anchor>
                </Group>
              </Group>
            </Container>
          </Paper>
        </Box>
      );
    };

    return (
      <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
        <LandingPageContent />
      </MantineWrapper>
    );
  }

  // Original Landing Page (temporarily disabled)
  if (false && currentStep === 'landing') {
    return (
      <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
        {(() => {
          const stats = [
            { label: 'Years Experience', value: '8+' },
            { label: 'Qualified Nurses', value: '500+' },
            { label: '', value: '100 Miles', subtitle: 'The Midlands Area' },
            { label: '', value: 'Rapid Reply', subtitle: 'Calls Answered Instantly' }
          ];

          const features = [
            {
              icon: <UserGroupIcon style={{ width: '24px', height: '24px' }} />,
              title: 'Expert Nursing Staff',
              description: 'Theatre specialists across all disciplines plus experienced ward nurses, all vetted and qualified.'
            },
            {
              icon: <ClockIcon style={{ width: '24px', height: '24px' }} />,
              title: 'Rapid Response',
              description: 'SMS notifications to nurses within a 100 mile radius of the midlands area. Fast matching and confirmation for urgent staffing needs.'
            },
            {
              icon: <ComputerDesktopIcon style={{ width: '24px', height: '24px' }} />,
              title: 'Advanced Technology',
              description: 'Bespoke software platform handling scheduling, invoicing, records, and real-time communications.'
            },
            {
              icon: <ShieldCheckIcon style={{ width: '24px', height: '24px' }} />,
              title: 'Trusted Partnership',
              description: 'Your organisation will be assigned an ex-medical professional who will be fully experienced in understanding your requirements and sourcing the best staff for the job.'
            }
          ];

          const specializations = [
            'Anaesthetics (ODP)', 'HDU/ITU', 'Pediatrics', 'Recovery', 'Scrub (General)',
            'Scrub (Cardiac)', 'Scrub (ENT)', 'Scrub (Gynae)', 'Scrub (Lap)', 'Scrub (Neuro)',
            'Scrub (Ortho)', 'Scrub (Plastics)', 'Ward (RN)', 'Emergency Room', 'HCA/Auxiliary'
          ];

          return (
            <Box style={{ minHeight: '100vh' }}>
              <Paper shadow="sm" p="md" radius={0}>
                <Container size="xl">
                  <Group justify="space-between">
                    <Group gap="md">
                      <img
                        src="/logo.png"
                        alt="IntaStream Hub Logo"
                        style={{
                          height: '48px',

                        }}
                      />
                      <Text size="lg" fw={600}>Welcome To The IntaStream Portal</Text>
                    </Group>
                    <Group gap="md">
                      <Button
                        onClick={toggleColorScheme}
                        variant="light"
                        size="sm"
                        leftSection={colorScheme === 'dark' ?
                          <SunIcon style={{ width: '16px', height: '16px' }} /> :
                          <MoonIcon style={{ width: '16px', height: '16px' }} />
                        }
                      >
                        {colorScheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                      </Button>
                      {/* Request Staff - Development only */}
                      {process.env.NODE_ENV === 'development' && (
                        <Button
                          onClick={() => {
                            navigateToStep('initial-auth');
                          }}
                          size="md"
                          rightSection={<ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
                        >
                          Request Staff
                        </Button>
                      )}
                    </Group>
                  </Group>
                </Container>
              </Paper>

              <Container size="xl" py="xl">
                <Grid gutter="xl" align="center">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Stack gap="xl">
                      <Title order={1} size="3.5rem" fw={700} lh={1.1}>
                        Expert Healthcare Staffing
                        <Text span c="blue" inherit> Rapid Response When You Need It</Text>
                      </Title>
                      <Text size="xl" c="dimmed" lh={1.6}>
                        With 8+ years of experience and 500+ qualified nurses, IntaStream delivers rapid,
                        reliable staffing solutions for hospitals across the UK. Our advanced technology
                        platform ensures you get the right staff, at the right time.
                      </Text>
                      <Stack gap="md" align="flex-start" style={{ width: 'fit-content' }}>
                        <Group gap="md">
                          {/* Request Staff - Development only */}
                          {process.env.NODE_ENV === 'development' && (
                            <Button
                              onClick={() => {
                                navigateToStep('initial-auth');
                              }}
                              size="xl"
                              radius="md"
                              style={{ fontWeight: 600, minWidth: '220px' }}
                              rightSection={<ArrowRightIcon style={{ width: '20px', height: '20px' }} />}
                            >
                              Request Staff
                            </Button>
                          )}
                          {/* IntaLearn - Development only */}
                          {process.env.NODE_ENV === 'development' && (
                            <Button
                              onClick={() => {
                                navigateToStep('nurse-auth');
                              }}
                              variant="light"
                              size="xl"
                              radius="md"
                              style={{ fontWeight: 600, minWidth: '240px' }}
                              leftSection={<AcademicCapIcon style={{ width: '20px', height: '20px' }} />}
                            >
                              IntaLearn (Mandatory Training)
                            </Button>
                          )}
                        </Group>
                        {/* IntaProfile - Development only */}
                        {process.env.NODE_ENV === 'development' && (
                          <Button
                            onClick={() => setCurrentStep('staff-registration')}
                            size="lg"
                            color="blue"
                            radius="md"
                            style={{ fontWeight: 600, minWidth: '200px' }}
                            leftSection={<UserIcon style={{ width: '18px', height: '18px' }} />}
                          >
                            IntaProfile (Staff Registration)
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Paper p="xl" radius="lg" withBorder>
                      <img
                        src="/healthcare_industry.jpg"
                        alt="Healthcare professionals"
                        style={{ width: '100%', height: '300px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                    </Paper>
                  </Grid.Col>
                </Grid>
              </Container>

              <Paper py="xl" bg={colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.blue[0]}>
                <Container size="xl">
                  <Grid>
                    {stats.map((stat, index) => (
                      <Grid.Col key={index} span={{ base: 6, sm: 3 }}>
                        <Stack align="center" gap="xs">
                          <Text size="3rem" fw={700} c="blue">{stat.value}</Text>
                          {stat.label && <Text size="lg" c="dimmed" ta="center">{stat.label}</Text>}
                          {stat.subtitle && <Text size="sm" c="dimmed" ta="center">{stat.subtitle}</Text>}
                        </Stack>
                      </Grid.Col>
                    ))}
                  </Grid>
                </Container>
              </Paper>

              <Container size="xl" py="xl">
                <Stack align="center" gap="xl" mb="xl">
                  <Title order={2} ta="center" size="2.5rem">Why Choose IntaStream?</Title>
                  <Text size="xl" c="dimmed" ta="center" maw={600}>
                    Built by healthcare professionals for healthcare professionals.
                    Our proven track record speaks for itself.
                  </Text>
                </Stack>

                <Grid gutter="xl">
                  {features.map((feature, index) => (
                    <Grid.Col key={index} span={{ base: 12, md: 6 }}>
                      <Card p="xl" radius="lg" h="100%">
                        <Group gap="lg" align="flex-start">
                          <Paper p="md" radius="md" bg={colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.blue[0]}>
                            <Box c="blue">{feature.icon}</Box>
                          </Paper>
                          <Stack gap="sm" style={{ flex: 1 }}>
                            <Title order={3} size="1.5rem">{feature.title}</Title>
                            <Text c="dimmed" lh={1.6}>{feature.description}</Text>
                          </Stack>
                        </Group>
                      </Card>
                    </Grid.Col>
                  ))}
                </Grid>
              </Container>

              <Paper py="xl" bg={colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0]}>
                <Container size="xl">
                  <Stack align="center" gap="xl">
                    <Title order={2} ta="center" size="2.5rem">Our Specializations</Title>
                    <Text size="xl" c="dimmed" ta="center" maw={700}>
                      From theatre specialists to ward nurses, we cover all major healthcare disciplines
                      with experienced, qualified professionals.
                    </Text>
                    <Grid gutter="md">
                      {specializations.map((spec, index) => (
                        <Grid.Col key={index} span={{ base: 6, sm: 4, md: 3 }}>
                          <Badge
                            variant="light"
                            size="lg"
                            radius="md"
                            style={{ width: '100%', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            {spec}
                          </Badge>
                        </Grid.Col>
                      ))}
                    </Grid>
                  </Stack>
                </Container>
              </Paper>

              <Container size="xl" py="xl">
                <Stack align="center" gap="xl">
                  <Title order={2} ta="center" size="2.5rem">How It Works</Title>
                  <Text size="xl" c="dimmed" ta="center" maw={600}>
                    Our streamlined process gets you the staff you need, fast.
                  </Text>

                  <Timeline active={3} bulletSize={40} lineWidth={3}>
                    <Timeline.Item bullet={<CheckCircleIcon style={{ width: '20px', height: '20px' }} />} title="IntaSchedule Request">
                      <Text c="dimmed" size="lg">
                        Submit your staffing request through our secure online platform.
                        Specify specialization, dates, and shift requirements.
                      </Text>
                    </Timeline.Item>

                    <Timeline.Item bullet={<UserGroupIcon style={{ width: '20px', height: '20px' }} />} title="Automated Matching">
                      <Text c="dimmed" size="lg">
                        Our system instantly matches qualified nurses within a 100 mile radius of the midlands area
                        and sends SMS notifications to available staff.
                      </Text>
                    </Timeline.Item>

                    <Timeline.Item bullet={<PhoneIcon style={{ width: '20px', height: '20px' }} />} title="Rapid Response">
                      <Text c="dimmed" size="lg">
                        Nurses respond via SMS. We monitor responses and confirm
                        availability with an instant response.
                      </Text>
                    </Timeline.Item>

                    <Timeline.Item bullet={<ShieldCheckIcon style={{ width: '20px', height: '20px' }} />} title="Confirmed & Ready">
                      <Text c="dimmed" size="lg">
                        Booking confirmed! You receive instant notification and the
                        assigned nurse receives all shift details.
                      </Text>
                    </Timeline.Item>
                  </Timeline>
                </Stack>
              </Container>

              <Paper py="xl" bg={theme.colors.blue[6]}>
                <Container size="xl">
                  <Stack align="center" gap="xl">
                    <Title order={2} ta="center" size="2.5rem" c="white">
                      Ready to Solve Your Staffing Challenges?
                    </Title>
                    <Text size="xl" c="white" ta="center" maw={600} style={{ opacity: 0.9 }}>
                      Join hospitals across the UK who trust IntaStream for reliable,
                      professional healthcare staffing solutions.
                    </Text>
                    <Stack gap="md" align="center">
                      <Group gap="md">
                        {/* Request Staff - Development only */}
                        {process.env.NODE_ENV === 'development' && (
                          <Button
                            onClick={() => {
                              navigateToStep('initial-auth');
                            }}
                            size="xl"
                            variant="white"
                            color="blue"
                            radius="md"
                            style={{ fontWeight: 600, fontSize: '1.1rem', padding: '16px 32px' }}
                            rightSection={<ArrowRightIcon style={{ width: '20px', height: '20px' }} />}
                          >
                            Request Staff
                          </Button>
                        )}
                        {/* IntaLearn - Development only */}
                        {process.env.NODE_ENV === 'development' && (
                          <Button
                            onClick={() => {
                              navigateToStep('nurse-auth');
                            }}
                            size="xl"
                            variant="outline"
                            color="white"
                            radius="md"
                            style={{ fontWeight: 600, fontSize: '1.1rem', padding: '16px 32px', borderColor: 'white', color: 'white' }}
                            leftSection={<AcademicCapIcon style={{ width: '20px', height: '20px' }} />}
                          >
                            IntaLearn (Mandatory Training)
                          </Button>
                        )}
                      </Group>
                      {/* IntaProfile - Development only */}
                      {process.env.NODE_ENV === 'development' && (
                        <Button
                          onClick={() => setCurrentStep('staff-registration')}
                          size="lg"
                          color="blue"
                          radius="md"
                          style={{ fontWeight: 600, minWidth: '200px' }}
                          leftSection={<UserIcon style={{ width: '18px', height: '18px' }} />}
                        >
                          IntaProfile (Staff Registration)
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Container>
              </Paper>

              <Paper p="xl" radius={0}>
                <Container size="xl">
                  <Group justify="space-between">
                    <Group gap="md">
                      <img
                        src="/logo.png"
                        alt="IntaStream Hub Logo"
                        style={{
                          height: '39px',

                        }}
                      />
                      <Text c="dimmed">© 2025 IntaStream Hub. Comprehensive Healthcare Solutions.</Text>
                    </Group>
                    <Group gap="lg">
                      <Anchor href="#" c="dimmed">Privacy Policy</Anchor>
                      <Anchor href="#" c="dimmed">Terms of Service</Anchor>
                      <Anchor href="#" c="dimmed">Contact Us</Anchor>
                    </Group>
                  </Group>
                </Container>
              </Paper>
            </Box>
          );
        })()}
      </MantineWrapper>
    );
  }

  // Admin-only Data Mapping Interface
  if (currentStep === 'csv-mapping-admin') {
    // Check if user is admin via Login table UserType
    const isAdminUser = (authForm.provider &&
      (authForm.provider.userType === 'ADMIN' ||
        authForm.provider.email === 'maria@intastream.com' ||
        authForm.provider.id === -1)) ||
      (nurseAuth.authenticated && nurseAuth.userType === 'ADMIN') ||
      (registrationForm.userType === 'ADMIN');

    if (!isAdminUser) {
      return (
        <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
          <Container size="md" style={{ minHeight: '100vh', paddingTop: '4rem' }}>
            <Paper shadow="lg" p="xl" radius="md">
              <Stack align="center" gap="lg">
                <Title order={2} c="red">Access Denied</Title>
                <Text>This interface is only available to administrators.</Text>
                <Button onClick={() => navigateToStep('landing')}>
                  Return to Home
                </Button>
              </Stack>
            </Paper>
          </Container>
        </MantineWrapper>
      );
    }

    // Fixed CSV mapping interface - optimized for performance
    const adminEmail = authForm.provider?.email || nurseAuth.email ||
      (registrationForm.professionalId ? `professional-${registrationForm.professionalId}@intastream.com` : 'unknown@intastream.com');

    return (
      <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
        <Container size="xl" style={{ minHeight: '100vh', paddingTop: '2rem' }}>
          <OptimizedCSVMappingInterface
            onClose={() => navigateToStep('landing')}
            adminEmail={adminEmail}
          />
        </Container>
      </MantineWrapper>
    );
  }

  // CSV Data Mapping Interface
  if (currentStep === 'csv-mapping') {
    const csvColumns = [
      'Timestamp', 'Title', 'Date of Birth', 'Forename(s)', 'Surname', 'National Insurance Number',
      'NMC/PIN/HPC Number', 'NMC/PIN/HPC Number Registration Date', 'NMC/PIN/HPC Number Expiry Date',
      'DBS Number', 'DBS Update Reference Number', 'DBS Registration Date', 'DBS Expiry Date',
      'Passport Number', 'Passport Style Photograph For Photo ID', 'Address Line 1', 'Address Line 2',
      'Address Line 3', 'Address Line 4', 'Address Line 5', 'Postcode', 'Email Address',
      'Mobile Phone Number', 'Land Line Number', 'Next of Kin (Full Name)', 'Next of Kin (Contact Telephone #)',
      'Next of Kin (Relationship Status)', 'Next of Kin (Address Inc. Country of Residence)',
      'Anaesthetics (ODP)', 'HCA (Health Care Assistant)', 'HDU/ITU', 'Paediatrics', 'Recovery',
      'Sterilisation Technician (SSD)', 'Ward (RN)', 'Scrub (General)', 'Scrub (Cardiac)', 'Scrub (ENT)',
      'Scrub (Gynaecology)', 'Scrub (Laparoscopic)', 'Scrub (Microvascular)', 'Scrub (Neuro)',
      'Scrub (Optha)', 'Scrub (Ortho)', 'Scrub (Plastics)', 'Scrub (Spine)', 'Scrub (Thoracic)',
      'Scrub (Urology)', 'Scrub (SFA/ASP)', 'Emergency Room Nurse (RN)', 'Upload your CV',
      'Photo ID (Passport Photo)', 'DBS Certificate', 'NMC Certificate', 'Passport Scans (Front and Back)',
      'Immunization Records (I.D. verified)', 'References', 'Professional Qualifications & Certificates',
      'Professional Indemnity Insurance', 'Company Formation Certificate (If Self Employed)',
      'Which Statement Applies To You', 'Full Name (As Shown On Bank Account)', 'Account Number',
      'Sort Code', 'GP (General Practitioner) - Name & Address', 'VISA Expiry Date (Required if you have a VISA number)',
      'Do you have a British or EU Passport?', 'Are you an EU citizen?', 'What kind of work you are looking for?',
      'Please Choose The Clinical Area(s) You Wish To Work In', 'When Can You Start Work?', 'Do You drive?',
      'Do you have professional indemnity insurance?', 'Professional Indemnity Insurance Number',
      'Effective Date', 'Organization # 1 - Name', 'Organization # 1 - Application, Registration or ID Number',
      'Organization # 1 - Date', 'Organization # 1 - Expiry Date', 'Employer # 1 - Name & Address',
      'Employer # 1 - Position, Grade or Speciality', 'Employer # 1 - Date (From)', 'Employer # 1 - Date (To)',
      'Employer # 2 - Name & Address', 'Employer # 2 - Position, Grade or Speciality', 'Employer # 2 - Date (From)',
      'Employer # 2 - Date (To)', 'Employer # 3 - Name & Address', 'Employer # 3 - Position, Grade or Speciality',
      'Employer # 3 - Date (From)', 'Employer # 3 - Date (To)', 'Qualification Name # 1',
      'Qualification # 1 - Awarded By Institution', 'Qualification # 1 - Date (From)', 'Qualification # 1 -Date (To)',
      'Professional Reference # 1 - Employer\'s Name', 'Professional Reference # 1 - Address of Workplace',
      'Professional Reference # 1 - Start Date', 'Professional Reference # 1 - End Date',
      'Professional Reference # 1 - Referee - Full Name', 'Professional Reference # 1 - Referee - Professional Title',
      'Professional Reference # 1 - Referee - Professional Relationship', 'Professional Reference # 1 - Referee - Telephone #',
      'Professional Reference # 1 - Referee - Email Address', 'Professional Reference # 2 - Employer\'s Name',
      'Professional Reference # 2 - Address of Workplace', 'Professional Reference # 2 - Start Date',
      'Professional Reference # 2 - End Date', 'Professional Reference # 2 - Referee - Full Name',
      'Professional Reference # 2 - Referee - Professional Title', 'Professional Reference # 2 - Referee - Professional Relationship',
      'Professional Reference # 2 - Referee - Telephone #', 'Professional Reference # 2 - Referee - Email Address'
    ];

    // CSV columns from Google Forms (already defined above)

    const handleMappingChange = (csvColumn, dbColumn) => {
      // Only update local state - no API calls at all
      setMappings(prev => ({
        ...prev,
        [csvColumn]: dbColumn
      }));

      console.log(`Local update: ${csvColumn} -> ${dbColumn}`);
    };

    const handleNotesChange = (csvColumn, note) => {
      // Only update local state - no API calls
      setNotes(prev => ({
        ...prev,
        [csvColumn]: note
      }));
    };

    const saveMappingsConfiguration = async () => {
      try {
        const adminEmail = authForm.provider?.email || nurseAuth.email ||
          (registrationForm.professionalId ? `professional-${registrationForm.professionalId}@intastream.com` : 'unknown@intastream.com');

        const configurationData = {
          mappings,
          notes,
          timestamp: new Date().toISOString()
        };

        await apiService.saveMappingConfiguration(
          'Google Forms CSV Mapping',
          'Field mapping configuration for Google Forms CSV import',
          configurationData,
          adminEmail
        );

        showNotification('Mapping configuration saved successfully!', 'success', 'Configuration Saved');
      } catch (error) {
        console.error('Error saving mapping configuration:', error);
        showNotification('Error saving mapping configuration', 'error');
      }
    };

    const saveAllChanges = async () => {
      try {
        // Clear any pending timeouts
        clearTimeout(window.mappingTimeout);
        clearTimeout(window.notesTimeout);

        const adminEmail = authForm.provider?.email || nurseAuth.email ||
          (registrationForm.professionalId ? `professional-${registrationForm.professionalId}@intastream.com` : 'unknown@intastream.com');

        // Save all current mappings
        const savePromises = Object.entries(mappings).map(([csvColumn, dbColumn]) =>
          apiService.saveFieldMapping(csvColumn, dbColumn as string, notes[csvColumn] || '', adminEmail)
        );

        await Promise.all(savePromises);
        showNotification('All changes saved successfully!', 'success', 'Changes Saved');
        console.log('Manual save completed for all mappings');
      } catch (error) {
        console.error('Error saving all changes:', error);
        showNotification('Error saving changes', 'error');
      }
    };

    return (
      <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
        <Container size="xl" style={{ minHeight: '100vh', paddingTop: '2rem' }}>
          <Stack gap="lg">
            <Paper shadow="lg" p="xl" radius="md">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <div>
                    <Title order={2}>CSV to Database Field Mapping</Title>
                    <Text c="dimmed">Map Google Forms CSV fields to ProfessionalSet database columns</Text>
                  </div>
                  <Group gap="md">
                    <Button variant="light" onClick={() => navigateToStep('landing')}>
                      Back to Home
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          setLoadingMappings(true);
                          const existingMappings = await apiService.getFieldMappings();
                          const mappingsObj = {};
                          existingMappings.forEach(mapping => {
                            mappingsObj[mapping.csvColumn] = mapping.databaseColumn;
                          });
                          setMappings(mappingsObj);
                          showNotification('Existing mappings loaded successfully!', 'success');
                        } catch (error) {
                          showNotification('No existing mappings found', 'warning');
                        } finally {
                          setLoadingMappings(false);
                        }
                      }}
                      leftSection={<DocumentArrowDownIcon style={{ width: '16px', height: '16px' }} />}
                      variant="outline"
                      color="blue"
                      loading={loadingMappings}
                    >
                      Load Existing
                    </Button>
                    <Button
                      onClick={saveAllChanges}
                      leftSection={<CheckCircleIcon style={{ width: '16px', height: '16px' }} />}
                      variant="filled"
                      color="green"
                    >
                      Save All Mappings
                    </Button>
                  </Group>
                </Group>

                <Alert>
                  <Text size="sm">
                    <strong>Admin Access:</strong> This interface allows mapping of CSV columns from Google Forms
                    registration data to ProfessionalSet database fields. <strong>Local Mode:</strong> All changes are stored locally
                    for maximum performance. Click "Load Existing" to import saved mappings or "Save All Mappings" to persist to database.
                    Use the search box to filter database columns.
                  </Text>
                </Alert>

                <Paper p="md" withBorder>
                  <Group gap="md" align="center">
                    <Text fw={600}>Search Database Columns:</Text>
                    <TextInput
                      placeholder="Type to filter database columns..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ flex: 1, maxWidth: '400px' }}
                      leftSection={<span>🔍</span>}
                    />
                    {loadingMappings && <Loader size="sm" />}
                  </Group>
                </Paper>
              </Stack>
            </Paper>

            <Paper shadow="md" p="lg" radius="md">
              <Stack gap="md">
                <Group justify="space-between">
                  <Title order={4}>Field Mappings ({csvColumns.length} CSV columns → {DB_COLUMNS.length} database columns)</Title>
                  <Badge size="lg" variant="light">
                    {Object.keys(mappings).filter(key => mappings[key]).length} / {csvColumns.length} mapped
                  </Badge>
                </Group>

                <ScrollArea h={600}>
                  <Stack gap="sm">
                    {csvColumns.map((csvColumn, index) => (
                      <Paper key={index} p="md" withBorder radius="sm">
                        <Grid gutter="md" align="center">
                          <Grid.Col span={4}>
                            <Text size="sm" fw={500} lineClamp={2}>
                              <Badge size="xs" mr="xs">{index + 1}</Badge>
                              {csvColumn}
                            </Text>
                          </Grid.Col>
                          <Grid.Col span={3}>
                            <Select
                              placeholder="Select database column"
                              data={[
                                { value: '', label: '-- No mapping --' },
                                ...filteredDbColumns.map(col => ({ value: col, label: col }))
                              ]}
                              value={mappings[csvColumn] ?? ''}
                              onChange={(value) => handleMappingChange(csvColumn, value)}
                              searchable
                              size="sm"
                              disabled={savingMapping === csvColumn}
                              rightSection={savingMapping === csvColumn ? <Loader size="xs" /> : null}
                            />
                          </Grid.Col>
                          <Grid.Col span={5}>
                            <TextInput
                              placeholder="Notes for data transformation..."
                              value={notes[csvColumn] ?? ''}
                              onChange={(e) => handleNotesChange(csvColumn, e.target.value)}
                              size="sm"
                            />
                          </Grid.Col>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                </ScrollArea>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </MantineWrapper>
    );
  }

  // IntaProfile - Staff Registration
  if (currentStep === 'staff-registration') {
    return (
      <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
        <UnifiedHeader />
        <Container size="lg" style={{ minHeight: '100vh', paddingTop: '2rem' }}>

          {registrationForm.currentStep === 'welcome' && (
            <Paper shadow="lg" p="xl" radius="md">
              <Stack align="center" gap="lg">
                <img
                  src="/logo.png"
                  alt="IntaStream Hub Logo"
                  style={{
                    height: '72px',

                  }}
                />
                <div style={{ textAlign: 'center' }}>
                  <Title order={1} mb="xs">IntaProfile</Title>
                  <Text c="dimmed">Manage your professional healthcare profile</Text>
                </div>
              </Stack>

              <Paper p="lg" withBorder radius="md" mt="xl">
                <Stack gap="md">
                  <Text size="lg" fw={600} c="blue">Welcome to IntaStream Healthcare Staffing</Text>
                  <Text>
                    Create or update your professional healthcare profile. This secure platform manages all your information for employment verification and compliance.
                  </Text>
                  <Group gap="md">
                    <div>
                      <Text size="sm" fw={600}>✓ Secure Data Handling</Text>
                      <Text size="xs" c="dimmed">All information encrypted and GDPR compliant</Text>
                    </div>
                    <div>
                      <Text size="sm" fw={600}>✓ Save & Resume</Text>
                      <Text size="xs" c="dimmed">Complete at your own pace</Text>
                    </div>
                    <div>
                      <Text size="sm" fw={600}>✓ Fast Processing</Text>
                      <Text size="xs" c="dimmed">Quick review and approval</Text>
                    </div>
                  </Group>
                </Stack>
              </Paper>

              <Stack gap="lg" mt="xl">
                <Text size="md" fw={600}>Get Started</Text>
                <Text size="sm" c="dimmed">
                  Please enter your National Insurance Number to begin. This allows us to save your progress
                  and match against existing records.
                </Text>

                <TextInput
                  label="National Insurance Number *"
                  placeholder="e.g., AB123456C"
                  description="This will be used to save your progress and is required for employment verification"
                  size="md"
                  style={{ maxWidth: '400px' }}
                  value={registrationForm.niNumber}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRegistrationForm({
                      ...registrationForm,
                      niNumber: newValue
                    });
                    // Check for existing session when user types (debounced)
                    debouncedCheckExistingSession(newValue);
                  }}
                  disabled={registrationForm.isValidating}
                />

                <Group gap="md">
                  <Button
                    size="lg"
                    rightSection={<ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
                    onClick={handleBeginRegistration}
                    loading={registrationForm.isValidating}
                    disabled={registrationForm.isValidating || !registrationForm.niNumber}
                    color={registrationForm.hasExistingSession ? 'green' : 'blue'}
                  >
                    {registrationForm.hasExistingSession ? 'Continue Profile' : 'Create Profile'}
                  </Button>

                  {registrationForm.sessionChecked && registrationForm.hasExistingSession && (
                    <Text size="sm" c="green" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                      Profile found - Continue where you left off
                    </Text>
                  )}

                  {registrationForm.sessionChecked && !registrationForm.hasExistingSession && registrationForm.niNumber && validateNINumber(registrationForm.niNumber) && (
                    <Text size="sm" c="dimmed">
                      New profile
                    </Text>
                  )}

                  {/* Admin Badge - Show when admin user is detected */}
                  {registrationForm.userType === 'ADMIN' && (
                    <Paper p="sm" withBorder radius="md" style={{ border: '2px solid #ffd43b', backgroundColor: '#fff9c4' }}>
                      <Group gap="xs">
                        <ShieldCheckIcon style={{ width: '18px', height: '18px', color: '#f59f00' }} />
                        <Text size="sm" fw={600} c="yellow.7">
                          Administrator Access
                        </Text>
                        <Text size="xs" c="dimmed">
                          Admin tools available after login
                        </Text>
                      </Group>
                    </Paper>
                  )}
                </Group>
              </Stack>
            </Paper>
          )}

          {registrationForm.currentStep === 'personal-details' && (
            <Paper shadow="lg" p="xl" radius="md">
              <Stack gap="lg">
                <Group justify="space-between" align="center">
                  <div>
                    <Title order={2}>Personal Details</Title>
                    <Text c="dimmed">Step 1 of 8 - Basic personal information</Text>
                  </div>
                  <Text size="sm" c="dimmed">NI: {registrationForm.niNumber}</Text>
                </Group>

                {registrationForm.professionalId && (
                  <Paper p="md" withBorder radius="md">
                    <Group gap="md">
                      <CheckCircleIcon style={{ width: '20px', height: '20px', color: 'var(--mantine-color-green-6)' }} />
                      <div>
                        <Text size="sm" fw={600} c="green">Existing Professional Record Found</Text>
                        <Text size="xs" c="dimmed">
                          Your information has been pre-populated from our database. Please review and update as needed.
                        </Text>
                      </div>
                    </Group>
                  </Paper>
                )}

                {/* Admin Panel - Only visible for admin users in IntaProfile */}
                {registrationForm.userType === 'ADMIN' && (
                  <Paper p="lg" withBorder radius="md" style={{ border: '2px solid #ffd43b' }}>
                    <Stack gap="md">
                      <Group justify="space-between" align="center">
                        <div>
                          <Text size="lg" fw={600} c="yellow.7">
                            <ShieldCheckIcon style={{ width: '20px', height: '20px', marginRight: '8px', display: 'inline' }} />
                            Admin Note
                          </Text>
                          <Text size="sm" c="dimmed">For CSV Data Mapping, use the standalone interface at: localhost:3000/csv-mapping.html</Text>
                        </div>
                      </Group>

                      <Group gap="sm">
                        <Button
                          variant="light"
                          color="blue"
                          leftSection={<HomeIcon style={{ width: '16px', height: '16px' }} />}
                          onClick={() => navigateToStep('landing')}
                        >
                          Return to Landing
                        </Button>
                      </Group>
                    </Stack>
                  </Paper>
                )}

                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <Select
                      label="Title *"
                      placeholder="Select title"
                      data={['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Prof']}
                      value={registrationData.title}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        title: value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4.5 }}>
                    <TextInput
                      label="First Name *"
                      placeholder="Enter first name"
                      value={registrationData.forename}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        forename: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4.5 }}>
                    <TextInput
                      label="Surname *"
                      placeholder="Enter surname"
                      value={registrationData.surname}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        surname: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <DateInput
                      label="Date of Birth *"
                      placeholder="Select date of birth"
                      value={registrationData.dateOfBirth ? new Date(registrationData.dateOfBirth) : null}
                      onChange={(date) => setRegistrationData({
                        ...registrationData,
                        dateOfBirth: date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : ''
                      })}
                      maxDate={new Date()}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Full Name (for signature)"
                      placeholder="Enter full name as it appears on documents"
                      value={registrationData.fullName}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        fullName: e.target.value
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Address Information</Title>
                <Grid gutter="md">
                  <Grid.Col span={12}>
                    <TextInput
                      label="Address Line 1 *"
                      placeholder="House number and street name"
                      value={registrationData.addressLine1}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        addressLine1: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <TextInput
                      label="Address Line 2"
                      placeholder="Additional address information"
                      value={registrationData.addressLine2}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        addressLine2: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <TextInput
                      label="City/Town *"
                      placeholder="Enter city or town"
                      value={registrationData.addressLine3}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        addressLine3: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <TextInput
                      label="County"
                      placeholder="Enter county"
                      value={registrationData.addressLine4}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        addressLine4: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <TextInput
                      label="Postcode *"
                      placeholder="e.g., M1 1AA"
                      value={registrationData.postcode}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        postcode: e.target.value.toUpperCase()
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Contact Information</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Email Address *"
                      placeholder="your.email@example.com"
                      type="email"
                      value={registrationData.emailAddress}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        emailAddress: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <TextInput
                      label="Mobile Phone *"
                      placeholder="07XXX XXXXXX"
                      value={registrationData.mobilePhoneNumber}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        mobilePhoneNumber: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <TextInput
                      label="Landline"
                      placeholder="01XXX XXXXXX"
                      value={registrationData.landLineNumber}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        landLineNumber: e.target.value
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Group justify="space-between" mt="xl">
                  <Button
                    variant="outline"
                    onClick={() => setRegistrationForm({
                      ...registrationForm,
                      currentStep: 'welcome'
                    })}
                    leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => navigateToRegistrationStep('professional-credentials')}
                    rightSection={<ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Next: Professional Details
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {/* Step 2: Professional Credentials */}
          {registrationForm.currentStep === 'professional-credentials' && (
            <Paper shadow="lg" p="xl" radius="md">
              <Stack gap="lg">
                <Group justify="space-between" align="center">
                  <div>
                    <Title order={2}>Professional Credentials</Title>
                    <Text c="dimmed">Step 2 of 8 - Professional credentials and certifications</Text>
                  </div>
                  <Text size="sm" c="dimmed">NI: {registrationForm.niNumber}</Text>
                </Group>

                <Title order={4}>NMC PIN / HPC Registration</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <TextInput
                      label="NMC PIN / HPC Number *"
                      placeholder="e.g., 12A3456E"
                      value={registrationData.nmcPinHpcNumber}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        nmcPinHpcNumber: e.target.value.toUpperCase()
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <DateInput
                      label="Registration Date"
                      placeholder="Select registration date"
                      value={registrationData.nmcPinHpcRegistrationDate ? new Date(registrationData.nmcPinHpcRegistrationDate) : null}
                      onChange={(date) => setRegistrationData({
                        ...registrationData,
                        nmcPinHpcRegistrationDate: date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : ''
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <DateInput
                      label="Expiry Date"
                      placeholder="Select expiry date"
                      value={registrationData.nmcPinHpcExpiryDate ? new Date(registrationData.nmcPinHpcExpiryDate) : null}
                      onChange={(date) => setRegistrationData({
                        ...registrationData,
                        nmcPinHpcExpiryDate: date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : ''
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">DBS Certificate Details</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <TextInput
                      label="DBS Number"
                      placeholder="DBS certificate number"
                      value={registrationData.dbsNumber}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        dbsNumber: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <TextInput
                      label="DBS Update Service Reference"
                      placeholder="Update service reference"
                      value={registrationData.dbsUpdateReference}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        dbsUpdateReference: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <DateInput
                      label="DBS Registration Date"
                      placeholder="Select registration date"
                      value={registrationData.dbsRegistrationDate ? new Date(registrationData.dbsRegistrationDate) : null}
                      onChange={(date) => setRegistrationData({
                        ...registrationData,
                        dbsRegistrationDate: date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : ''
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <DateInput
                      label="DBS Expiry Date"
                      placeholder="Select expiry date"
                      value={registrationData.dbsExpiryDate ? new Date(registrationData.dbsExpiryDate) : null}
                      onChange={(date) => setRegistrationData({
                        ...registrationData,
                        dbsExpiryDate: date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : ''
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Passport Information</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Passport Number"
                      placeholder="Enter passport number"
                      value={registrationData.passportNumber}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        passportNumber: e.target.value.toUpperCase()
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Document Uploads</Title>
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <Text size="sm" fw={600}>Required Documents</Text>
                    <Grid gutter="md">
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <FileUploadControl
                          fileType="photoId"
                          label="Photo ID (Passport Photo)"
                          description="Upload a passport-style photograph"
                          required={true}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <FileUploadControl
                          fileType="dbsCertificate"
                          label="DBS Certificate"
                          description="Upload your DBS certificate"
                          required={true}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <FileUploadControl
                          fileType="nmcCertificate"
                          label="NMC Certificate"
                          description="Upload your NMC/PIN/HPC certificate"
                          required={true}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <FileUploadControl
                          fileType="passportScans"
                          label="Passport Scans"
                          description="Upload passport front and back pages"
                          required={true}
                        />
                      </Grid.Col>
                    </Grid>
                  </Stack>
                </Paper>

                <Title order={4} mt="lg">Next of Kin Information</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 8 }}>
                    <TextInput
                      label="Phone Number *"
                      placeholder="Contact number"
                      value={registrationData.nextOfKinPhone}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        nextOfKinPhone: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <Select
                      label="Relationship *"
                      placeholder="Select relationship"
                      data={['Spouse', 'Partner', 'Parent', 'Child', 'Sibling', 'Friend', 'Other']}
                      value={registrationData.nextOfKinRelationship}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        nextOfKinRelationship: value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Textarea
                      label="Next of Kin, Name, Full Address & Country of Residence *"
                      placeholder="Full name, address and country of residence"
                      rows={4}
                      value={registrationData.nextOfKinAddress}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        nextOfKinAddress: e.target.value
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Group justify="space-between" mt="xl">
                  <Button
                    variant="outline"
                    onClick={() => setRegistrationForm({
                      ...registrationForm,
                      currentStep: 'personal-details'
                    })}
                    leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => navigateToRegistrationStep('clinical-specializations')}
                    rightSection={<ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Next: Clinical Specializations
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {/* Step 3: Clinical Specializations */}
          {registrationForm.currentStep === 'clinical-specializations' && (
            <Paper shadow="lg" p="xl" radius="md">
              <Stack gap="lg">
                <Group justify="space-between" align="center">
                  <div>
                    <Title order={2}>Clinical Specializations</Title>
                    <Text c="dimmed">Step 3 of 8 - Your clinical areas of expertise</Text>
                  </div>
                  <Text size="sm" c="dimmed">NI: {registrationForm.niNumber}</Text>
                </Group>

                <Text>
                  Please select all clinical specializations you are qualified and experienced in.
                  This helps us match you with appropriate positions.
                </Text>

                <Title order={4}>Clinical Areas</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="Anaesthetics (ODP)"
                        checked={registrationData.specializations.anaestheticsODP}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            anaestheticsODP: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.anaestheticsODP}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.anaestheticsODP?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            anaestheticsODP: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="HCA / Health Care Assistant"
                        checked={registrationData.specializations.hcaHealthCareAssistant}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            hcaHealthCareAssistant: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.hcaHealthCareAssistant}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.hcaHealthCareAssistant?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            hcaHealthCareAssistant: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="HDU / ITU"
                        checked={registrationData.specializations.hduItu}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            hduItu: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.hduItu}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.hduItu?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            hduItu: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="Paediatrics"
                        checked={registrationData.specializations.paediatrics}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            paediatrics: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.paediatrics}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.paediatrics?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            paediatrics: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="Recovery"
                        checked={registrationData.specializations.recovery}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            recovery: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.recovery}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.recovery?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            recovery: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="Sterilisation Technician (SSD)"
                        checked={registrationData.specializations.sterilisationTechnician}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            sterilisationTechnician: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.sterilisationTechnician}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.sterilisationTechnician?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            sterilisationTechnician: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="Ward (RN)"
                        checked={registrationData.specializations.wardRN}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            wardRN: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.wardRN}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.wardRN?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            wardRN: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="Emergency Room Nurse (RN)"
                        checked={registrationData.specializations.emergencyRoomNurse}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            emergencyRoomNurse: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.emergencyRoomNurse}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.emergencyRoomNurse?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            emergencyRoomNurse: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Scrub Specializations</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="Scrub (General)"
                        checked={registrationData.specializations.scrubGeneral}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            scrubGeneral: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.scrubGeneral}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.scrubGeneral?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            scrubGeneral: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="Scrub (Cardiac)"
                        checked={registrationData.specializations.scrubCardiac}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            scrubCardiac: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.scrubCardiac}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.scrubCardiac?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            scrubCardiac: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="Scrub (ENT)"
                        checked={registrationData.specializations.scrubENT}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            scrubENT: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.scrubENT}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.scrubENT?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            scrubENT: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="Scrub (Gynaecology)"
                        checked={registrationData.specializations.scrubGynaecology}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            scrubGynaecology: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.scrubGynaecology}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.scrubGynaecology?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            scrubGynaecology: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Stack gap="xs">
                      <Checkbox
                        label="Scrub (Laparoscopic)"
                        checked={registrationData.specializations.scrubLaparoscopic}
                        onChange={(e) => setRegistrationData({
                          ...registrationData,
                          specializations: {
                            ...registrationData.specializations,
                            scrubLaparoscopic: e.currentTarget.checked
                          }
                        })}
                      />
                      <Select
                        label="Experience Level"
                        placeholder="Select level"
                        size="sm"
                        disabled={!registrationData.specializations.scrubLaparoscopic}
                        data={[
                          { value: '0', label: 'Not Experienced' },
                          { value: '1', label: 'Moderately Experienced' },
                          { value: '2', label: 'Very Experienced' }
                        ]}
                        value={registrationData.competencyScores.scrubLaparoscopic?.toString() || '0'}
                        onChange={(value) => setRegistrationData({
                          ...registrationData,
                          competencyScores: {
                            ...registrationData.competencyScores,
                            scrubLaparoscopic: parseInt(value)
                          }
                        })}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Checkbox
                      label="Scrub (Microvascular)"
                      checked={registrationData.specializations.scrubMicrovascular}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        specializations: {
                          ...registrationData.specializations,
                          scrubMicrovascular: e.currentTarget.checked
                        }
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Checkbox
                      label="Scrub (Neuro)"
                      checked={registrationData.specializations.scrubNeuro}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        specializations: {
                          ...registrationData.specializations,
                          scrubNeuro: e.currentTarget.checked
                        }
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Checkbox
                      label="Scrub (Optha)"
                      checked={registrationData.specializations.scrubOptha}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        specializations: {
                          ...registrationData.specializations,
                          scrubOptha: e.currentTarget.checked
                        }
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Checkbox
                      label="Scrub (Ortho)"
                      checked={registrationData.specializations.scrubOrtho}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        specializations: {
                          ...registrationData.specializations,
                          scrubOrtho: e.currentTarget.checked
                        }
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Checkbox
                      label="Scrub (Plastics)"
                      checked={registrationData.specializations.scrubPlastics}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        specializations: {
                          ...registrationData.specializations,
                          scrubPlastics: e.currentTarget.checked
                        }
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Checkbox
                      label="Scrub (Spine)"
                      checked={registrationData.specializations.scrubSpine}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        specializations: {
                          ...registrationData.specializations,
                          scrubSpine: e.currentTarget.checked
                        }
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Checkbox
                      label="Scrub (Thoracic)"
                      checked={registrationData.specializations.scrubThoracic}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        specializations: {
                          ...registrationData.specializations,
                          scrubThoracic: e.currentTarget.checked
                        }
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Checkbox
                      label="Scrub (Urology)"
                      checked={registrationData.specializations.scrubUrology}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        specializations: {
                          ...registrationData.specializations,
                          scrubUrology: e.currentTarget.checked
                        }
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <Checkbox
                      label="Scrub (SFA / ASP)"
                      checked={registrationData.specializations.scrubSfaAsp}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        specializations: {
                          ...registrationData.specializations,
                          scrubSfaAsp: e.currentTarget.checked
                        }
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Group justify="space-between" mt="xl">
                  <Button
                    variant="outline"
                    onClick={() => setRegistrationForm({
                      ...registrationForm,
                      currentStep: 'professional-credentials'
                    })}
                    leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => navigateToRegistrationStep('employment-history')}
                    rightSection={<ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Next: Employment History
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {/* Step 4: Employment History */}
          {registrationForm.currentStep === 'employment-history' && (
            <Paper shadow="lg" p="xl" radius="md">
              <Stack gap="lg">
                <Group justify="space-between" align="center">
                  <div>
                    <Title order={2}>Employment History</Title>
                    <Text c="dimmed">Step 4 of 8 - Previous employment and qualifications</Text>
                  </div>
                  <Text size="sm" c="dimmed">NI: {registrationForm.niNumber}</Text>
                </Group>

                <Title order={4}>Previous Employers</Title>
                {registrationData.employers.map((employer, index) => (
                  <Paper key={index} p="md" withBorder radius="md">
                    <Title order={5} mb="md">Employer {index + 1}</Title>
                    <Grid gutter="md">
                      <Grid.Col span={12}>
                        <Textarea
                          label="Employer Name & Address"
                          placeholder="Full employer name and address"
                          rows={3}
                          value={employer.nameAddress}
                          onChange={(e) => {
                            const newEmployers = [...registrationData.employers];
                            newEmployers[index].nameAddress = e.target.value;
                            setRegistrationData({
                              ...registrationData,
                              employers: newEmployers
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <TextInput
                          label="Position Held"
                          placeholder="Job title/position"
                          value={employer.position}
                          onChange={(e) => {
                            const newEmployers = [...registrationData.employers];
                            newEmployers[index].position = e.target.value;
                            setRegistrationData({
                              ...registrationData,
                              employers: newEmployers
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <DateInput
                          label="Start Date"
                          placeholder="From"
                          value={employer.dateFrom ? new Date(employer.dateFrom) : null}
                          onChange={(date) => {
                            const newEmployers = [...registrationData.employers];
                            newEmployers[index].dateFrom = date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : '';
                            setRegistrationData({
                              ...registrationData,
                              employers: newEmployers
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <DateInput
                          label="End Date"
                          placeholder="To (or current)"
                          value={employer.dateTo ? new Date(employer.dateTo) : null}
                          onChange={(date) => {
                            const newEmployers = [...registrationData.employers];
                            newEmployers[index].dateTo = date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : '';
                            setRegistrationData({
                              ...registrationData,
                              employers: newEmployers
                            });
                          }}
                        />
                      </Grid.Col>
                    </Grid>
                  </Paper>
                ))}

                <Title order={4} mt="lg">Professional Qualifications</Title>
                {registrationData.qualifications.map((qualification, index) => (
                  <Paper key={index} p="md" withBorder radius="md">
                    <Title order={5} mb="md">Qualification {index + 1}</Title>
                    <Grid gutter="md">
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <TextInput
                          label="Qualification Name"
                          placeholder="Name of qualification/course"
                          value={qualification.name}
                          onChange={(e) => {
                            const newQualifications = [...registrationData.qualifications];
                            newQualifications[index].name = e.target.value;
                            setRegistrationData({
                              ...registrationData,
                              qualifications: newQualifications
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <TextInput
                          label="Institution"
                          placeholder="School/University/Training provider"
                          value={qualification.institution}
                          onChange={(e) => {
                            const newQualifications = [...registrationData.qualifications];
                            newQualifications[index].institution = e.target.value;
                            setRegistrationData({
                              ...registrationData,
                              qualifications: newQualifications
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <DateInput
                          label="Start Date"
                          placeholder="From"
                          value={qualification.dateFrom ? new Date(qualification.dateFrom) : null}
                          onChange={(date) => {
                            const newQualifications = [...registrationData.qualifications];
                            newQualifications[index].dateFrom = date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : '';
                            setRegistrationData({
                              ...registrationData,
                              qualifications: newQualifications
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <DateInput
                          label="Completion Date"
                          placeholder="To"
                          value={qualification.dateTo ? new Date(qualification.dateTo) : null}
                          onChange={(date) => {
                            const newQualifications = [...registrationData.qualifications];
                            newQualifications[index].dateTo = date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : '';
                            setRegistrationData({
                              ...registrationData,
                              qualifications: newQualifications
                            });
                          }}
                        />
                      </Grid.Col>
                    </Grid>
                  </Paper>
                ))}

                <Title order={4} mt="lg">Professional Organizations</Title>
                {registrationData.organizations.map((org, index) => (
                  <Paper key={index} p="md" withBorder radius="md">
                    <Title order={5} mb="md">Organization {index + 1}</Title>
                    <Grid gutter="md">
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <TextInput
                          label="Organization Name"
                          placeholder="Professional body/organization"
                          value={org.name}
                          onChange={(e) => {
                            const newOrganizations = [...registrationData.organizations];
                            newOrganizations[index].name = e.target.value;
                            setRegistrationData({
                              ...registrationData,
                              organizations: newOrganizations
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <TextInput
                          label="Membership Number"
                          placeholder="Member ID/number"
                          value={org.number}
                          onChange={(e) => {
                            const newOrganizations = [...registrationData.organizations];
                            newOrganizations[index].number = e.target.value;
                            setRegistrationData({
                              ...registrationData,
                              organizations: newOrganizations
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <DateInput
                          label="Join Date"
                          placeholder="Membership start date"
                          value={org.date ? new Date(org.date) : null}
                          onChange={(date) => {
                            const newOrganizations = [...registrationData.organizations];
                            newOrganizations[index].date = date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : '';
                            setRegistrationData({
                              ...registrationData,
                              organizations: newOrganizations
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <DateInput
                          label="Expiry Date"
                          placeholder="Membership expiry"
                          value={org.expiryDate ? new Date(org.expiryDate) : null}
                          onChange={(date) => {
                            const newOrganizations = [...registrationData.organizations];
                            newOrganizations[index].expiryDate = date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : '';
                            setRegistrationData({
                              ...registrationData,
                              organizations: newOrganizations
                            });
                          }}
                        />
                      </Grid.Col>
                    </Grid>
                  </Paper>
                ))}

                <Title order={4} mt="lg">Document Uploads</Title>
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <Text size="sm" fw={600}>Professional Documents</Text>
                    <Grid gutter="md">
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <FileUploadControl
                          fileType="cv"
                          label="CV (Curriculum Vitae)"
                          description="Upload your current CV"
                          required={true}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <FileUploadControl
                          fileType="professionalQualifications"
                          label="Professional Qualifications & Certificates"
                          description="Upload certificates and qualifications"
                          required={true}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <FileUploadControl
                          fileType="professionalIndemnityInsurance"
                          label="Professional Indemnity Insurance"
                          description="Upload insurance certificate"
                          required={false}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <FileUploadControl
                          fileType="companyFormationCertificate"
                          label="Company Formation Certificate"
                          description="Required if self-employed"
                          required={false}
                        />
                      </Grid.Col>
                    </Grid>
                  </Stack>
                </Paper>

                <Title order={4} mt="lg">Work Preferences</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Work Type"
                      placeholder="Select work type"
                      data={['Flexible Agency Work', 'Part-time Contract', 'Full-time Contract', 'Bank/Locum', 'Permanent']}
                      value={registrationData.workType}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        workType: value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Preferred Clinical Areas"
                      placeholder="e.g., Theatre, Ward, ICU"
                      value={registrationData.clinicalAreas}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        clinicalAreas: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <DateInput
                      label="Available Start Date"
                      placeholder="When can you start?"
                      value={registrationData.startDate ? new Date(registrationData.startDate) : null}
                      onChange={(date) => setRegistrationData({
                        ...registrationData,
                        startDate: date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : ''
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Can you drive?"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.canDrive !== null ? registrationData.canDrive.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        canDrive: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Group justify="space-between" mt="xl">
                  <Button
                    variant="outline"
                    onClick={() => setRegistrationForm({
                      ...registrationForm,
                      currentStep: 'clinical-specializations'
                    })}
                    leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => navigateToRegistrationStep('qualifications-references')}
                    rightSection={<ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Next: References
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {/* Step 5: Professional References */}
          {registrationForm.currentStep === 'qualifications-references' && (
            <Paper shadow="lg" p="xl" radius="md">
              <Stack gap="lg">
                <Group justify="space-between" align="center">
                  <div>
                    <Title order={2}>Professional References</Title>
                    <Text c="dimmed">Step 5 of 8 - Professional references and workplace contacts</Text>
                  </div>
                  <Text size="sm" c="dimmed">NI: {registrationForm.niNumber}</Text>
                </Group>

                <Text>
                  Please provide details for 2 professional references who can verify your employment history and professional competence.
                </Text>

                {registrationData.references.map((reference, index) => (
                  <Paper key={index} p="lg" withBorder radius="md">
                    <Title order={4} mb="md">Professional Reference {index + 1}</Title>

                    <Title order={5} mb="sm">Employment Details</Title>
                    <Grid gutter="md" mb="lg">
                      <Grid.Col span={12}>
                        <TextInput
                          label="Employer Name *"
                          placeholder="Name of employer/organization"
                          value={reference.employerName}
                          onChange={(e) => {
                            const newReferences = [...registrationData.references];
                            newReferences[index].employerName = e.target.value;
                            setRegistrationData({
                              ...registrationData,
                              references: newReferences
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={12}>
                        <Textarea
                          label="Workplace Address *"
                          placeholder="Full workplace address"
                          rows={3}
                          value={reference.workplaceAddress}
                          onChange={(e) => {
                            const newReferences = [...registrationData.references];
                            newReferences[index].workplaceAddress = e.target.value;
                            setRegistrationData({
                              ...registrationData,
                              references: newReferences
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <DateInput
                          label="Employment Start Date *"
                          placeholder="When did you start?"
                          value={reference.startDate ? new Date(reference.startDate) : null}
                          onChange={(date) => {
                            const newReferences = [...registrationData.references];
                            newReferences[index].startDate = date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : '';
                            setRegistrationData({
                              ...registrationData,
                              references: newReferences
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <DateInput
                          label="Employment End Date"
                          placeholder="When did you finish?"
                          value={reference.endDate ? new Date(reference.endDate) : null}
                          onChange={(date) => {
                            const newReferences = [...registrationData.references];
                            newReferences[index].endDate = date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : '';
                            setRegistrationData({
                              ...registrationData,
                              references: newReferences
                            });
                          }}
                        />
                      </Grid.Col>
                    </Grid>

                    <Title order={5} mb="sm">Referee Contact Details</Title>
                    <Grid gutter="md">
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <TextInput
                          label="Referee Name *"
                          placeholder="Full name of referee"
                          value={reference.refereeName}
                          onChange={(e) => {
                            const newReferences = [...registrationData.references];
                            newReferences[index].refereeName = e.target.value;
                            setRegistrationData({
                              ...registrationData,
                              references: newReferences
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <TextInput
                          label="Job Title *"
                          placeholder="Referee's position"
                          value={reference.refereeTitle}
                          onChange={(e) => {
                            const newReferences = [...registrationData.references];
                            newReferences[index].refereeTitle = e.target.value;
                            setRegistrationData({
                              ...registrationData,
                              references: newReferences
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <Select
                          label="Relationship *"
                          placeholder="Select relationship"
                          data={['Direct Manager', 'Senior Colleague', 'HR Manager', 'Clinical Supervisor', 'Ward Manager', 'Matron', 'Other']}
                          value={reference.refereeRelationship}
                          onChange={(value) => {
                            const newReferences = [...registrationData.references];
                            newReferences[index].refereeRelationship = value;
                            setRegistrationData({
                              ...registrationData,
                              references: newReferences
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <TextInput
                          label="Phone Number *"
                          placeholder="Contact phone"
                          value={reference.refereePhone}
                          onChange={(e) => {
                            const newReferences = [...registrationData.references];
                            newReferences[index].refereePhone = e.target.value;
                            setRegistrationData({
                              ...registrationData,
                              references: newReferences
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <TextInput
                          label="Email Address *"
                          placeholder="Contact email"
                          type="email"
                          value={reference.refereeEmail}
                          onChange={(e) => {
                            const newReferences = [...registrationData.references];
                            newReferences[index].refereeEmail = e.target.value;
                            setRegistrationData({
                              ...registrationData,
                              references: newReferences
                            });
                          }}
                        />
                      </Grid.Col>
                    </Grid>
                  </Paper>
                ))}

                <Title order={4} mt="lg">Reference Documents</Title>
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <Text size="sm" fw={600}>Reference Documentation</Text>
                    <FileUploadControl
                      fileType="references"
                      label="References"
                      description="Upload any reference letters or documentation"
                      required={false}
                    />
                  </Stack>
                </Paper>

                <Group justify="space-between" mt="xl">
                  <Button
                    variant="outline"
                    onClick={() => setRegistrationForm({
                      ...registrationForm,
                      currentStep: 'employment-history'
                    })}
                    leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => navigateToRegistrationStep('health-medical')}
                    rightSection={<ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Next: Health & Medical
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {/* Step 6: Health & Medical */}
          {registrationForm.currentStep === 'health-medical' && (
            <Paper shadow="lg" p="xl" radius="md">
              <Stack gap="lg">
                <Group justify="space-between" align="center">
                  <div>
                    <Title order={2}>Health & Medical Information</Title>
                    <Text c="dimmed">Step 6 of 8 - Health questionnaire and medical screening</Text>
                  </div>
                  <Text size="sm" c="dimmed">NI: {registrationForm.niNumber}</Text>
                </Group>

                <Title order={4}>General Health Questions</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Do you have any illness or disability that may affect your work? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.hasIllnessDisability !== null ? registrationData.hasIllnessDisability.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        hasIllnessDisability: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Have you had any work-related illness? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.workRelatedIllness !== null ? registrationData.workRelatedIllness.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        workRelatedIllness: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Are you currently receiving treatment? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.currentTreatment !== null ? registrationData.currentTreatment.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        currentTreatment: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Do you need any workplace adjustments? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.needsAdjustments !== null ? registrationData.needsAdjustments.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        needsAdjustments: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Infection Control</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Have you ever had MRSA? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.hasMRSA !== null ? registrationData.hasMRSA.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        hasMRSA: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Have you ever had C.Diff? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.hasCDiff !== null ? registrationData.hasCDiff.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        hasCDiff: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Have you had contact with blood-borne viruses? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.bloodBorneVirusContact !== null ? registrationData.bloodBorneVirusContact.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        bloodBorneVirusContact: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Have you had Chicken Pox or Shingles? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.chickenPoxShingles !== null ? registrationData.chickenPoxShingles.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        chickenPoxShingles: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Tuberculosis Screening</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Have you lived outside the UK? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.tuberculosisOutsideUK !== null ? registrationData.tuberculosisOutsideUK.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        tuberculosisOutsideUK: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Have you had BCG vaccination? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.tuberculosisBCGVaccination !== null ? registrationData.tuberculosisBCGVaccination.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        tuberculosisBCGVaccination: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <Select
                      label="Persistent cough? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.tuberculosisCough !== null ? registrationData.tuberculosisCough.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        tuberculosisCough: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <Select
                      label="Unexplained weight loss? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.tuberculosisWeightLoss !== null ? registrationData.tuberculosisWeightLoss.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        tuberculosisWeightLoss: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <Select
                      label="Night sweats/fever? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.tuberculosisFever !== null ? registrationData.tuberculosisFever.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        tuberculosisFever: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Vaccination Records</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Have you had triple vaccination (DPT)? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.tripleVaccination !== null ? registrationData.tripleVaccination.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        tripleVaccination: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="COVID-19 Vaccination Status"
                      placeholder="e.g., Fully vaccinated, Boosted, etc."
                      value={registrationData.covid19VaccinationStatus}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        covid19VaccinationStatus: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Textarea
                      label="Vaccination Summary"
                      placeholder="List all relevant vaccinations with dates"
                      rows={4}
                      value={registrationData.vaccinationSummary}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        vaccinationSummary: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Textarea
                      label="GP Name & Address"
                      placeholder="Your GP practice details"
                      rows={3}
                      value={registrationData.gpNameAddress}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        gpNameAddress: e.target.value
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Medical Documents</Title>
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <Text size="sm" fw={600}>Medical Documentation</Text>
                    <FileUploadControl
                      fileType="immunizationRecords"
                      label="Immunization Records"
                      description="Upload your immunization/vaccination records"
                      required={true}
                    />
                  </Stack>
                </Paper>

                <Group justify="space-between" mt="xl">
                  <Button
                    variant="outline"
                    onClick={() => setRegistrationForm({
                      ...registrationForm,
                      currentStep: 'qualifications-references'
                    })}
                    leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => navigateToRegistrationStep('immigration-financial')}
                    rightSection={<ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Next: Immigration & Financial
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {/* Step 7: Immigration & Financial */}
          {registrationForm.currentStep === 'immigration-financial' && (
            <Paper shadow="lg" p="xl" radius="md">
              <Stack gap="lg">
                <Group justify="space-between" align="center">
                  <div>
                    <Title order={2}>Immigration & Financial Details</Title>
                    <Text c="dimmed">Step 7 of 8 - Immigration status and financial information</Text>
                  </div>
                  <Text size="sm" c="dimmed">NI: {registrationForm.niNumber}</Text>
                </Group>

                <Title order={4}>Immigration Status</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Do you have a British or EU passport? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.hasBritishEUPassport !== null ? registrationData.hasBritishEUPassport.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        hasBritishEUPassport: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Are you an EU citizen? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.isEUCitizen !== null ? registrationData.isEUCitizen.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        isEUCitizen: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <DateInput
                      label="VISA Expiry Date"
                      placeholder="If applicable"
                      value={registrationData.visaExpiryDate ? new Date(registrationData.visaExpiryDate) : null}
                      onChange={(date) => setRegistrationData({
                        ...registrationData,
                        visaExpiryDate: date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : ''
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Other Work Permits"
                      placeholder="e.g., Work Permit, Settled Status"
                      value={registrationData.otherWorkPermits}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        otherWorkPermits: e.target.value
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Tax Information</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <Select
                      label="Tax Statement *"
                      placeholder="Select statement"
                      data={['Statement A', 'Statement B', 'Statement C']}
                      value={registrationData.taxStatement}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        taxStatement: value
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Student Loans</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Do you have a student loan? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.hasStudentLoan !== null ? registrationData.hasStudentLoan.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        hasStudentLoan: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Student loan type"
                      placeholder="If applicable"
                      data={['Plan 1', 'Plan 2', 'Both']}
                      value={registrationData.studentLoanType}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        studentLoanType: value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Do you have a postgraduate loan? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.hasPostgraduateLoan !== null ? registrationData.hasPostgraduateLoan.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        hasPostgraduateLoan: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Banking Details</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Preferred Payment Method *"
                      placeholder="Select method"
                      data={['Bank Transfer (BACS)', 'Cheque', 'Other']}
                      value={registrationData.preferredPaymentMethod}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        preferredPaymentMethod: value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Bank Account Name"
                      placeholder="Account holder name"
                      value={registrationData.bankAccountName}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        bankAccountName: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Bank Account Number"
                      placeholder="8-digit account number"
                      value={registrationData.bankAccountNumber}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        bankAccountNumber: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Bank Sort Code"
                      placeholder="XX-XX-XX"
                      value={registrationData.bankSortCode}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        bankSortCode: e.target.value
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Background Checks</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Do you have any criminal convictions? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.criminalConvictions !== null ? registrationData.criminalConvictions.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        criminalConvictions: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Have you been subject to fitness to practice investigation? *"
                      placeholder="Select"
                      data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' }
                      ]}
                      value={registrationData.fitnessToPracticeInvestigation !== null ? registrationData.fitnessToPracticeInvestigation.toString() : ''}
                      onChange={(value) => setRegistrationData({
                        ...registrationData,
                        fitnessToPracticeInvestigation: value !== '' ? value === 'true' : null
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Textarea
                      label="Background Check Details"
                      placeholder="Please provide details if you answered 'Yes' to any background check questions"
                      rows={4}
                      value={registrationData.backgroundCheckDetails}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        backgroundCheckDetails: e.target.value
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Group justify="space-between" mt="xl">
                  <Button
                    variant="outline"
                    onClick={() => setRegistrationForm({
                      ...registrationForm,
                      currentStep: 'health-medical'
                    })}
                    leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => navigateToRegistrationStep('review-submit')}
                    rightSection={<ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Next: Review & Submit
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {/* Step 8: Review & Submit */}
          {registrationForm.currentStep === 'review-submit' && (
            <Paper shadow="lg" p="xl" radius="md">
              <Stack gap="lg">
                <Group justify="space-between" align="center">
                  <div>
                    <Title order={2}>Review & Submit Application</Title>
                    <Text c="dimmed">Step 8 of 8 - Final review and submission</Text>
                  </div>
                  <Text size="sm" c="dimmed">NI: {registrationForm.niNumber}</Text>
                </Group>

                <Paper p="lg" withBorder radius="md">
                  <Stack gap="md">
                    <Title order={4}>Application Summary</Title>
                    <Text>
                      Please review your application details below. You can go back to any previous step to make changes.
                    </Text>
                  </Stack>
                </Paper>

                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Paper p="md" withBorder>
                      <Stack gap="xs">
                        <Title order={5}>Personal Details</Title>
                        <Text size="sm">Name: {registrationData.title} {registrationData.forename} {registrationData.surname}</Text>
                        <Text size="sm">Email: {registrationData.emailAddress}</Text>
                        <Text size="sm">Phone: {registrationData.mobilePhoneNumber}</Text>
                        <Text size="sm">Address: {registrationData.addressLine1}, {registrationData.postcode}</Text>
                      </Stack>
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Paper p="md" withBorder>
                      <Stack gap="xs">
                        <Title order={5}>Professional Credentials</Title>
                        <Text size="sm">NMC PIN: {registrationData.nmcPinHpcNumber || 'Not provided'}</Text>
                        <Text size="sm">DBS Number: {registrationData.dbsNumber || 'Not provided'}</Text>
                        <Text size="sm">Next of Kin: {registrationData.nextOfKinName}</Text>
                      </Stack>
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Paper p="md" withBorder>
                      <Stack gap="xs">
                        <Title order={5}>Clinical Specializations</Title>
                        <Text size="sm">
                          {Object.entries(registrationData.specializations)
                            .filter(([key, value]) => value)
                            .map(([key, value]) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()))
                            .join(', ') || 'None selected'}
                        </Text>
                      </Stack>
                    </Paper>
                  </Grid.Col>
                </Grid>

                <Title order={4} mt="lg">Terms & Conditions</Title>
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <Text size="sm">
                      By submitting this application, I confirm that:
                    </Text>
                    <Text size="sm">
                      • All information provided is true and accurate to the best of my knowledge
                      • I understand that providing false information may result in termination of employment
                      • I consent to background checks and reference verification
                      • I agree to IntaStream's terms and conditions of employment
                      • I understand that this application does not guarantee employment
                    </Text>
                    <Checkbox
                      label="I accept the terms and conditions *"
                      checked={registrationData.termsAccepted}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        termsAccepted: e.currentTarget.checked
                      })}
                    />
                  </Stack>
                </Paper>

                <Title order={4} mt="lg">Digital Signature</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Full Name (Digital Signature) *"
                      placeholder="Type your full name"
                      value={registrationData.fullName}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        fullName: e.target.value
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Email Address (Confirmation) *"
                      placeholder="Confirm your email"
                      type="email"
                      value={registrationData.emailAddressSignature}
                      onChange={(e) => setRegistrationData({
                        ...registrationData,
                        emailAddressSignature: e.target.value
                      })}
                    />
                  </Grid.Col>
                </Grid>

                <Group justify="space-between" mt="xl">
                  <Button
                    variant="outline"
                    onClick={() => setRegistrationForm({
                      ...registrationForm,
                      currentStep: 'immigration-financial'
                    })}
                    leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Back
                  </Button>
                  <Button
                    color="green"
                    size="lg"
                    disabled={!registrationData.termsAccepted || !registrationData.fullName || !registrationData.emailAddressSignature}
                    onClick={() => {
                      setRegistrationData({
                        ...registrationData,
                        dateSigned: new Date().toISOString().split('T')[0]
                      });
                      completeRegistration();
                    }}
                    rightSection={<DocumentCheckIcon style={{ width: '20px', height: '20px' }} />}
                  >
                    Save Profile
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}
        </Container>

        {/* General Confirmation Modal for Staff Registration */}
        <Modal
          opened={confirmationModal.opened}
          onClose={closeConfirmationModal}
          title={confirmationModal.title}
          size="sm"
          centered
          zIndex={10000}
          overlayProps={{ opacity: 0.5, blur: 3 }}
        >
          <Stack gap="md">
            <Text>
              {confirmationModal.message}
            </Text>

            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                onClick={handleConfirmationCancel}
                leftSection={<XMarkIcon style={{ width: '16px', height: '16px' }} />}
              >
                {confirmationModal.cancelText}
              </Button>
              <Button
                onClick={handleConfirmationConfirm}
                rightSection={<CheckCircleIcon style={{ width: '16px', height: '16px' }} />}
              >
                {confirmationModal.confirmText}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </MantineWrapper>
    );
  }

  // Nurse Authentication
  if (currentStep === 'nurse-auth') {
    return (
      <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
        <UnifiedHeader />
        <Container size="sm" style={{ minHeight: '100vh', paddingTop: '2rem' }}>
          <Paper shadow="lg" p="xl" radius="md">
            <Stack align="center" gap="lg">
              <img
                src="/logo.png"
                alt="IntaStream Hub Logo"
                style={{
                  height: '72px',

                }}
              />
              <div style={{ textAlign: 'center' }}>
                <Title order={1} mb="xs">IntaLearn Login</Title>
                <Text c="dimmed">Access your mandatory training and profile</Text>
              </div>
            </Stack>

            <Stack gap="lg" mt="xl">
              <Paper p="md" withBorder>
                <Text fw={600} c="blue" mb="xs">Login to Continue</Text>
                <Text size="sm" c="dimmed">
                  Sign in with your email or Google account to access your training portal
                  and complete your mandatory training requirements.
                </Text>
              </Paper>

              <form onSubmit={async (e) => {
                e.preventDefault();
                const username = (e.target as any).username.value;
                const password = (e.target as any).password.value;
                await handleNurseAuth(username, password);
              }}>
                <Stack gap="md">
                  <TextInput
                    name="username"
                    label="Username"
                    placeholder="Enter your IntaStream username"
                    size="md"
                    required
                  />
                  <TextInput
                    name="password"
                    type="password"
                    label="Password"
                    placeholder="Enter your password"
                    size="md"
                    required
                  />
                  <Button
                    type="submit"
                    size="md"
                    fullWidth
                    leftSection={<IdentificationIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Sign In to IntaLearn (Mandatory Training)
                  </Button>
                </Stack>
              </form>
            </Stack>
          </Paper>
        </Container>
      </MantineWrapper>
    );
  }

  // Nurse Profile Setup
  if (currentStep === 'nurse-profile') {
    return (
      <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
        <Container size="md" style={{ minHeight: '100vh', paddingTop: '2rem' }}>
          <Group justify="flex-end" mb="md">
            <ActionIcon
              onClick={toggleColorScheme}
              variant="light"
              size="lg"
              aria-label="Toggle color scheme"
            >
              {colorScheme === 'dark' ? (
                <SunIcon style={{ width: '20px', height: '20px' }} />
              ) : (
                <MoonIcon style={{ width: '20px', height: '20px' }} />
              )}
            </ActionIcon>
            <Button
              onClick={() => navigateToStep('nurse-auth')}
              variant="light"
              size="sm"
            >
              Back
            </Button>
            <Button
              onClick={handleNurseLogout}
              variant="light"
              color="red"
              size="sm"
              leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
            >
              Logout
            </Button>
          </Group>
          <Paper shadow="lg" p="xl" radius="md">
            <Stack align="center" gap="lg">
              <img
                src="/logo.png"
                alt="IntaStream Hub Logo"
                style={{
                  height: '72px',

                }}
              />
              <div style={{ textAlign: 'center' }}>
                <Title order={1} mb="xs">Complete Your Profile</Title>
                <Text c="dimmed">We need a few details to set up your training account</Text>
              </div>
            </Stack>

            <Stack gap="lg" mt="xl">
              <Grid>
                <Grid.Col span={6}>
                  <TextInput
                    label="First Name *"
                    value={nurseProfile.firstName}
                    onChange={(e) => setNurseProfile({ ...nurseProfile, firstName: e.target.value })}
                    placeholder="Enter your first name"
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="Last Name *"
                    value={nurseProfile.lastName}
                    onChange={(e) => setNurseProfile({ ...nurseProfile, lastName: e.target.value })}
                    placeholder="Enter your last name"
                  />
                </Grid.Col>
              </Grid>

              <TextInput
                label="Phone Number *"
                value={nurseProfile.phoneNumber}
                onChange={(e) => setNurseProfile({ ...nurseProfile, phoneNumber: e.target.value })}
                placeholder="+44 7xxx xxx xxx"
              />

              <Select
                label="Nursing Role *"
                value={nurseProfile.nursingRole}
                onChange={(value) => setNurseProfile({ ...nurseProfile, nursingRole: value })}
                data={medicalRoles.map(role => ({ value: role.Role, label: role.Role }))}
                placeholder="Select your primary nursing role"
                searchable
                disabled={isLoadingMedicalRoles}
              />


              <TextInput
                label="NMC Registration Number"
                value={nurseProfile.registrationNumber}
                onChange={(e) => setNurseProfile({ ...nurseProfile, registrationNumber: e.target.value })}
                placeholder="12A3456E (optional)"
                description="Your Nursing and Midwifery Council registration number"
              />

              <div>
                <Text size="sm" fw={500} mb="xs">Address *</Text>
                <Stack gap="sm">
                  <TextInput
                    placeholder="Street Address"
                    value={nurseProfile.address?.street || ''}
                    onChange={(e) => setNurseProfile({
                      ...nurseProfile,
                      address: { ...(nurseProfile.address || {}), street: e.target.value }
                    })}
                  />
                  <Grid>
                    <Grid.Col span={8}>
                      <TextInput
                        placeholder="City"
                        value={nurseProfile.address?.city || ''}
                        onChange={(e) => setNurseProfile({
                          ...nurseProfile,
                          address: { ...(nurseProfile.address || {}), city: e.target.value }
                        })}
                      />
                    </Grid.Col>
                    <Grid.Col span={4}>
                      <TextInput
                        placeholder="Postcode"
                        value={nurseProfile.address?.postcode || ''}
                        onChange={(e) => setNurseProfile({
                          ...nurseProfile,
                          address: { ...(nurseProfile.address || {}), postcode: e.target.value }
                        })}
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
              </div>

              <Button
                onClick={handleNurseProfileSubmit}
                size="lg"
                fullWidth
                rightSection={<UserPlusIcon style={{ width: '16px', height: '16px' }} />}
              >
                Complete Profile & Continue
              </Button>
            </Stack>
          </Paper>
        </Container>

        {/* General Confirmation Modal for Nurse Auth */}
        <Modal
          opened={confirmationModal.opened}
          onClose={closeConfirmationModal}
          title={confirmationModal.title}
          size="sm"
          centered
          zIndex={10000}
          overlayProps={{ opacity: 0.5, blur: 3 }}
        >
          <Stack gap="md">
            <Text>
              {confirmationModal.message}
            </Text>

            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                onClick={handleConfirmationCancel}
                leftSection={<XMarkIcon style={{ width: '16px', height: '16px' }} />}
              >
                {confirmationModal.cancelText}
              </Button>
              <Button
                onClick={handleConfirmationConfirm}
                rightSection={<CheckCircleIcon style={{ width: '16px', height: '16px' }} />}
              >
                {confirmationModal.confirmText}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </MantineWrapper>
    );
  }

  // Nurse Dashboard
  if (currentStep === 'nurse-dashboard') {
    // Use database courses - separate by priority instead of isMandatory
    const mandatoryCourses = dbCourses.filter(course => course.priority === 1); // Core courses
    const roleSpecificCourses = dbCourses.filter(course => course.priority === 2); // Role-specific courses

    const totalCourses = dbCourses.length;
    const completedCourses = dbCourses.filter(course =>
      trainingProgress[course.courseId]?.completed
    ).length;
    const compliancePercentage = totalCourses > 0 ? Math.round((completedCourses / totalCourses) * 100) : 0;

    return (
      <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
        <UnifiedHeader />
        <Container size="xl" style={{ minHeight: '100vh', paddingTop: '2rem' }}>

          <Paper shadow="lg" p="xl" radius="md">
            <Stack align="center" gap="lg">
              <img
                src="/logo.png"
                alt="IntaStream Hub Logo"
                style={{
                  height: '72px',

                }}
              />
              <div style={{ textAlign: 'center' }}>
                <Title order={1} mb="xs">Welcome, {nurseProfile.firstName}!</Title>
                <Text c="dimmed">Your mandatory training dashboard</Text>
              </div>
            </Stack>

            {/* Compliance Overview */}
            <Paper p="lg" withBorder radius="md" mt="xl">
              <Group justify="space-between" align="center">
                <div>
                  <Text size="xl" fw={700} c="blue">Training Compliance Overview</Text>
                  <Text c="dimmed">Complete all mandatory training to maintain professional compliance</Text>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Text size="3rem" fw={700} c="blue">{compliancePercentage}%</Text>
                  <Text size="sm" c="dimmed">{completedCourses} of {totalCourses} completed</Text>
                  <Progress value={compliancePercentage} size="md" radius="md" style={{ width: '200px', marginTop: '8px' }} />
                </div>
              </Group>
            </Paper>

            <Grid mt="xl" gutter="lg">
              {/* Profile Information */}
              <Grid.Col span={{ base: 12, lg: 4 }}>
                <Paper p="lg" withBorder radius="md" h="100%">
                  <Group justify="space-between" mb="md">
                    <div>
                      <Text size="lg" fw={600}>Profile Information</Text>
                      <Text size="sm" c="dimmed">Your registered details</Text>
                    </div>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => navigateToStep('nurse-profile')}
                    >
                      Edit
                    </Button>
                  </Group>
                  <Stack gap="xs">
                    <Text><strong>Name:</strong> {nurseProfile.firstName} {nurseProfile.lastName}</Text>
                    <Text><strong>Email:</strong> {nurseAuth.email}</Text>
                    <Text><strong>Medical Role:</strong> {nurseProfile.nursingRole}</Text>
                    <Text><strong>Phone:</strong> {nurseProfile.phoneNumber}</Text>
                    {nurseProfile.registrationNumber && (
                      <Text><strong>NMC Number:</strong> {nurseProfile.registrationNumber}</Text>
                    )}
                  </Stack>
                </Paper>
              </Grid.Col>


              {/* Certificate Gallery - Database-driven */}
              <CertificateGallery />

              {/* Core Mandatory Training */}
              <Grid.Col span={{ base: 12, lg: 8 }}>
                <Paper p="lg" withBorder radius="md">
                  <Text size="lg" fw={600} mb="xs">Mandatory Training Courses</Text>
                  <Text c="dimmed" mb="lg">Required courses from database</Text>

                  {coursesLoading ? (
                    <Stack align="center" gap="sm">
                      <Text>Loading training courses...</Text>
                    </Stack>
                  ) : coursesError ? (
                    <Stack align="center" gap="sm">
                      <Text c="red">{coursesError}</Text>
                      <Button onClick={() => loadDatabaseCourses(nurseAuth.professionalId)}>
                        Retry
                      </Button>
                    </Stack>
                  ) : (
                    <Stack gap="sm">
                      {mandatoryCourses.map((course) => {
                        const isCompleted = trainingProgress[course.courseId]?.completed;
                        const completionDate = trainingProgress[course.courseId]?.completionDate;
                        const expiryDate = trainingProgress[course.courseId]?.expiryDate;

                        return (
                          <Paper
                            key={course.courseId}
                            p="md"
                            withBorder
                            radius="sm"
                            style={{
                              position: 'relative',
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <Group justify="space-between" align="flex-start">
                              <div style={{ flex: 1 }}>
                                <Group gap="sm" mb="xs">
                                  <Text fw={600} size="md">{course.courseName}</Text>
                                  <Badge variant="light" size="sm">
                                    {course.renewalPeriod}
                                  </Badge>
                                  {isCompleted && (
                                    <CheckCircleIcon style={{ width: '16px', height: '16px', color: 'var(--mantine-color-green-filled)' }} />
                                  )}
                                </Group>
                                <Text size="sm" c="dimmed" mb="xs">{course.description}</Text>
                                <Group gap="xs" align="center">
                                  <Text size="xs" c="blue" fw={500}>📚 {course.category}</Text>
                                  <Text size="xs" c="dimmed">• {course.duration}</Text>
                                </Group>
                                {isCompleted && (
                                  <Group gap="xs" mt="xs">
                                    <Text size="xs" c="green" fw={500}>
                                      ✅ Completed: {new Date(completionDate).toLocaleDateString('en-GB')}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      • Expires: {new Date(expiryDate).toLocaleDateString('en-GB')}
                                    </Text>
                                  </Group>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant={isCompleted ? "filled" : "light"}
                                color={isCompleted ? "green" : undefined}
                                onClick={() => handleStartDatabaseTraining(course.courseId)}
                              >
                                {isCompleted ? "Retake Course" : "Start Training"}
                              </Button>
                            </Group>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Paper>
              </Grid.Col>

              {/* Role-Specific/Optional Training Courses */}
              {roleSpecificCourses.length > 0 && (
                <Grid.Col span={12}>
                  <Paper p="lg" withBorder radius="md">
                    <Text size="lg" fw={600} mb="xs">Role-Specific Training</Text>
                    <Text c="dimmed" mb="lg">Additional courses for {nurseProfile.nursingRole || 'your role'}</Text>

                    {coursesLoading ? (
                      <Stack align="center" gap="sm">
                        <Text>Loading role-specific courses...</Text>
                      </Stack>
                    ) : (
                      <Grid>
                        {roleSpecificCourses.map((course, index) => {
                          const isCompleted = trainingProgress[course.courseId]?.completed;
                          const completionDate = trainingProgress[course.courseId]?.completionDate;
                          const expiryDate = trainingProgress[course.courseId]?.expiryDate;

                          return (
                            <Grid.Col key={course.courseId} span={{ base: 12, md: 6, lg: 4 }}>
                              <Paper
                                p="md"
                                withBorder
                                radius="sm"
                                h="100%"
                                style={{
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                                onClick={() => handleStartDatabaseTraining(course.courseId)}
                              >
                                <Stack gap="xs" h="100%">
                                  <Group justify="space-between" align="flex-start">
                                    <Text fw={600} size="sm" style={{ flex: 1 }}>{course.courseName}</Text>
                                    <Text size="xs" c="dimmed">{course.duration}</Text>
                                  </Group>
                                  <Text size="xs" c="dimmed" mb="xs">{course.description}</Text>

                                  <Stack gap="xs" mt="auto">
                                    {isCompleted && (
                                      <Group gap="xs">
                                        <CheckCircleIcon style={{ width: '14px', height: '14px', color: 'var(--mantine-color-green-6)' }} />
                                        <Text size="xs" c="green">Completed</Text>
                                        {completionDate && (
                                          <Text size="xs" c="dimmed">
                                            {new Date(completionDate).toLocaleDateString('en-GB')}
                                          </Text>
                                        )}
                                      </Group>
                                    )}
                                    <Button
                                      size="xs"
                                      variant="light"
                                      color={isCompleted ? "green" : undefined}
                                      onClick={() => handleStartDatabaseTraining(course.courseId)}
                                    >
                                      {isCompleted ? "Retake Course" : "Start Training"}
                                    </Button>
                                  </Stack>
                                </Stack>
                              </Paper>
                            </Grid.Col>
                          );
                        })}
                      </Grid>
                    )}
                  </Paper>
                </Grid.Col>
              )}

              {/* NMC Revalidation Notice */}
              <Grid.Col span={12}>
                <Paper p="md" withBorder radius="md">
                  <Group gap="sm">
                    <CheckCircleIcon style={{ width: '20px', height: '20px', color: 'var(--mantine-color-green-filled)' }} />
                    <div>
                      <Text fw={600} c="green">NMC Revalidation Requirement</Text>
                      <Text size="sm" c="dimmed">
                        Complete 35 hours of CPD every three years for NMC revalidation.
                        Mandatory training hours contribute to this requirement.
                      </Text>
                    </div>
                  </Group>
                </Paper>
              </Grid.Col>
            </Grid>
          </Paper>
        </Container>

        {/* General Confirmation Modal for Nurse Dashboard */}
        <Modal
          opened={confirmationModal.opened}
          onClose={closeConfirmationModal}
          title={confirmationModal.title}
          size="sm"
          centered
          zIndex={10000}
          overlayProps={{ opacity: 0.5, blur: 3 }}
        >
          <Stack gap="md">
            <Text>
              {confirmationModal.message}
            </Text>

            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                onClick={handleConfirmationCancel}
                leftSection={<XMarkIcon style={{ width: '16px', height: '16px' }} />}
              >
                {confirmationModal.cancelText}
              </Button>
              <Button
                onClick={handleConfirmationConfirm}
                rightSection={<CheckCircleIcon style={{ width: '16px', height: '16px' }} />}
              >
                {confirmationModal.confirmText}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </MantineWrapper>
    );
  }

  // Training Module - Handle case where no specific course is selected
  if (currentStep === 'training-module' && !currentTraining) {
    // Check if there's a persisted training session to restore
    const persistedTraining = loadFromStorage('intastream_current_training', null, true);
    if (persistedTraining && nurseAuth.authenticated) {
      // Restore the persisted training session
      setCurrentTraining(persistedTraining);
      return null; // Let it re-render with the restored training
    }

    // If no specific training is selected, redirect to nurse dashboard or show course selection
    if (nurseAuth.authenticated) {
      // Redirect to nurse dashboard to select a course
      navigateToStep('nurse-dashboard');
      return null;
    } else {
      // Not authenticated, redirect to nurse login
      navigateToStep('nurse-auth');
      return null;
    }
  }

  // Training Module - Database-driven training course UI
  if (currentStep === 'training-module' && currentTraining) {
    // Show loading if courses are still being loaded from database
    if (coursesLoading || dbCourses.length === 0) {
      return (
        <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
          <UnifiedHeader />
          <Container size="lg" style={{ minHeight: '100vh', paddingTop: '2rem' }}>
            <Paper p="xl" radius="md">
              <Stack align="center" gap="md">
                <Loader size="lg" />
                <Text>Loading course information...</Text>
              </Stack>
            </Paper>
          </Container>
        </MantineWrapper>
      );
    }

    // Find course in database courses
    const course = dbCourses.find(c => c.courseId === currentTraining);

    if (!course) {
      return (
        <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
          <UnifiedHeader />
          <Container size="lg" style={{ minHeight: '100vh', paddingTop: '2rem' }}>
            <Paper p="xl" radius="md">
              <Stack align="center" gap="md">
                <Text c="red">Course not found</Text>
              </Stack>
            </Paper>
          </Container>
        </MantineWrapper>
      );
    }

    // Show loading if course content is not loaded yet
    if (!currentCourseContent) {
      return (
        <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
          <UnifiedHeader />
          <Container size="lg" style={{ minHeight: '100vh', paddingTop: '2rem' }}>
            <Paper p="xl" radius="md">
              <Stack align="center" gap="md">
                <Text>Loading course content...</Text>
              </Stack>
            </Paper>
          </Container>
        </MantineWrapper>
      );
    }

    // Parse course content from database
    let courseData;
    try {
      // Parse sections and assessment from JSON strings
      const sections = typeof currentCourseContent.sections === 'string'
        ? JSON.parse(currentCourseContent.sections)
        : currentCourseContent.sections;

      const assessment = typeof currentCourseContent.assessment === 'string'
        ? JSON.parse(currentCourseContent.assessment)
        : currentCourseContent.assessment;

      // Convert assessment format to match UI expectations
      const processedAssessment = {
        passingScore: currentCourseContent.passingScore || 70,
        questions: assessment?.map((q, index) => ({
          id: `q${index + 1}`,
          question: q.question,
          options: q.options,
          correctAnswer: q.correct, // Database uses 'correct' field with index
          explanation: q.explanation
        })) || []
      };


      courseData = {
        courseName: currentCourseContent.courseName,
        sections: sections || [],
        assessment: processedAssessment
      };
    } catch (error) {
      console.error('Failed to parse course content:', error);
      return (
        <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
          <UnifiedHeader />
          <Container size="lg" style={{ minHeight: '100vh', paddingTop: '2rem' }}>
            <Paper p="xl" radius="md">
              <Stack align="center" gap="md">
                <Text c="red">Invalid course content format</Text>
              </Stack>
            </Paper>
          </Container>
        </MantineWrapper>
      );
    }

    // Use database course content structure
    const totalSections = courseData?.sections?.length || 0;
    const isAssessmentSection = currentSection === totalSections;
    const currentSectionData = courseData?.sections?.[currentSection];


    // Assessment results view
    if (assessmentComplete) {
      const passed = assessmentScore >= (courseData?.assessment?.passingScore || 70);

      return (
        <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
          <UnifiedHeader />
          <Container size="md" style={{ minHeight: '100vh', paddingTop: '2rem' }}>

            <Paper p="xl" radius="md">
              <Stack align="center" gap="xl">
                <div style={{ textAlign: 'center' }}>
                  {passed ? (
                    <CheckCircleIcon style={{ width: '64px', height: '64px', color: 'var(--mantine-color-green-6)' }} />
                  ) : (
                    <XMarkIcon style={{ width: '64px', height: '64px', color: 'var(--mantine-color-red-6)' }} />
                  )}
                </div>

                <div style={{ textAlign: 'center' }}>
                  <Title order={2} mb="sm">
                    {passed ? 'Assessment Passed!' : 'Assessment Failed'}
                  </Title>
                  <Text size="lg" mb="md">
                    You scored {assessmentScore}%
                  </Text>
                  <Text c="dimmed">
                    Passing Score: {courseData?.assessment?.passingScore || 70}%
                  </Text>
                  {passed && (
                    <Text c="green" fw={500} mt="md">
                      ✅ Training completed successfully
                    </Text>
                  )}
                </div>

                <Group>
                  <Button
                    onClick={() => handleCompleteTraining(currentTraining)}
                    variant={passed ? "filled" : "light"}
                    color={passed ? "green" : "blue"}
                  >
                    {passed ? 'Complete Training' : 'Return to Dashboard'}
                  </Button>
                  {!passed && (
                    <Button
                      onClick={() => {
                        navigateToSection(0);
                        setAssessmentComplete(false);
                        setAssessmentAnswers({});
                        setAssessmentScore(0);
                      }}
                      variant="light"
                    >
                      Retake Training
                    </Button>
                  )}
                </Group>
              </Stack>
            </Paper>
          </Container>
        </MantineWrapper>
      );
    }

    // Assessment section
    if (isAssessmentSection) {
      return (
        <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
          <UnifiedHeader />
          <Container size="md" style={{ minHeight: '100vh', paddingTop: '2rem' }}>

            <Paper p="xl" radius="md">
              <Group justify="space-between" mb="xl">
                <div>
                  <Title order={2}>{course.courseName}</Title>
                  <Text c="dimmed">Assessment</Text>
                </div>
                <Button
                  variant="light"
                  onClick={() => navigateToStep('nurse-dashboard')}
                  leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
                >
                  Back
                </Button>
              </Group>

              <Stack gap="lg">
                <Paper p="lg" withBorder radius="md">
                  <Text fw={600} mb="md">Assessment Instructions</Text>
                  <Text mb="sm">Answer all questions to complete the assessment.</Text>
                  <Text size="sm" c="dimmed">
                    Passing Score: {courseData?.assessment?.passingScore || 70}%
                  </Text>
                </Paper>

                <Stack gap="lg">
                  {courseData?.assessment?.questions?.map((question, index) => (
                    <Paper key={question.id} p="lg" withBorder radius="md">
                      <Text fw={600} mb="md">
                        Question {index + 1}: {question.question}
                      </Text>
                      <Stack gap="sm">
                        {question.options?.map((option, optionIndex) => (
                          <Checkbox
                            key={optionIndex}
                            label={option}
                            checked={assessmentAnswers[question.id] === optionIndex}
                            onChange={() => handleAssessmentAnswer(question.id, optionIndex)}
                          />
                        ))}
                      </Stack>
                    </Paper>
                  )) || (
                      <Paper p="lg" withBorder>
                        <Text c="dimmed">No assessment questions available</Text>
                      </Paper>
                    )}
                </Stack>

                <Group justify="space-between" mt="xl">
                  <Button
                    variant="light"
                    onClick={() => navigateToSection(Math.max(0, currentSection - 1))}
                  >
                    Previous Section
                  </Button>
                  <Group gap="sm">
                    <Button
                      variant="outline"
                      color="orange"
                      onClick={() => {
                        if (!courseData?.assessment?.questions) return;

                        const correctAnswers = {};
                        courseData.assessment.questions.forEach(question => {
                          correctAnswers[question.id] = question.correctAnswer;
                        });

                        setAssessmentAnswers(correctAnswers);
                        showNotification('All correct answers have been selected!', 'success', 'Testing Mode');
                      }}
                    >
                      🧪 Auto-Fill Correct Answers
                    </Button>
                    <Button
                      onClick={handleSubmitAssessment}
                      disabled={!courseData?.assessment?.questions || Object.keys(assessmentAnswers).length !== courseData.assessment.questions.length}
                    >
                      Submit Assessment
                    </Button>
                  </Group>
                </Group>
              </Stack>
            </Paper>
          </Container>
        </MantineWrapper>
      );
    }

    // Course content sections
    return (
      <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
        <UnifiedHeader />
        <Container size={isMobile ? "sm" : "md"} style={{ minHeight: '100vh', paddingTop: isMobile ? '1rem' : '2rem' }}>

          <Paper p={isMobile ? "md" : "xl"} radius="md">
            {/* Mobile-optimized header */}
            {isMobile ? (
              <Stack gap="sm" mb="lg">
                <Group justify="space-between">
                  <Button
                    variant="light"
                    size="sm"
                    onClick={() => navigateToStep('nurse-dashboard')}
                    leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '14px', height: '14px' }} />}
                  >
                    Back
                  </Button>
                  <Text size="sm" c="dimmed" fw={500}>
                    {currentSection + 1}/{totalSections}
                  </Text>
                </Group>
                <Title order={3} size="h4">{course.courseName}</Title>
                {currentSectionData?.title && (
                  <Text size="sm" c="dimmed" lineClamp={2}>
                    {currentSectionData.title}
                  </Text>
                )}
              </Stack>
            ) : (
              <Group justify="space-between" mb="xl">
                <div>
                  <Title order={2}>{course.courseName}</Title>
                  <Text c="dimmed">
                    Section {currentSection + 1} of {totalSections}
                    {currentSectionData?.title && ` - ${currentSectionData.title}`}
                  </Text>
                </div>
                <Button
                  variant="light"
                  onClick={() => navigateToStep('nurse-dashboard')}
                  leftSection={<ArrowLeftEndOnRectangleIcon style={{ width: '16px', height: '16px' }} />}
                >
                  Back
                </Button>
              </Group>
            )}

            {/* Compact progress bar for mobile */}
            <Paper p={isMobile ? "sm" : "md"} withBorder radius="md" mb={isMobile ? "md" : "xl"}>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>Progress</Text>
                <Text size="sm" c="dimmed">
                  {Math.round(((currentSection + 1) / (totalSections + 1)) * 100)}%
                </Text>
              </Group>
              <Progress value={((currentSection + 1) / (totalSections + 1)) * 100} size="sm" radius="md" />
            </Paper>

            {/* Mobile-optimized course content */}
            {currentSectionData ? (
              <Stack gap={isMobile ? "md" : "lg"}>
                {!isMobile && <Title order={3}>{currentSectionData.title}</Title>}

                {/* Database course content with mobile styling */}
                <Paper
                  p={isMobile ? "md" : "lg"}
                  withBorder
                  radius="md"
                  style={{
                    maxHeight: isMobile ? '60vh' : 'none',
                    overflow: isMobile ? 'auto' : 'visible'
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{ __html: currentSectionData.content }}
                    className="training-content"
                    style={{
                      fontSize: isMobile ? '14px' : '16px',
                      lineHeight: isMobile ? '1.4' : '1.6'
                    }}
                  />
                </Paper>
              </Stack>
            ) : (
              <Paper p={isMobile ? "md" : "lg"} withBorder>
                <Text c="dimmed">Course content not available</Text>
              </Paper>
            )}

            {/* Mobile-optimized navigation */}
            <Group justify="space-between" mt={isMobile ? "md" : "xl"} gap={isMobile ? "xs" : "md"}>
              <Button
                variant="light"
                size={isMobile ? "sm" : "md"}
                onClick={() => navigateToSection(Math.max(0, currentSection - 1))}
                disabled={currentSection === 0}
              >
                {isMobile ? 'Previous' : 'Previous'}
              </Button>
              <Button
                size={isMobile ? "sm" : "md"}
                onClick={() => {
                  if (currentSection < totalSections - 1) {
                    navigateToSection(currentSection + 1);
                  } else {
                    navigateToSection(totalSections); // Go to assessment
                  }
                }}
              >
                {currentSection < totalSections - 1 ? (isMobile ? 'Next' : 'Next Section') : (isMobile ? 'Assessment' : 'Take Assessment')}
              </Button>
            </Group>
          </Paper>
        </Container>

        {/* Confirmation Modal for Training Module */}
        <Modal
          opened={confirmationModal.opened}
          onClose={closeConfirmationModal}
          title={confirmationModal.title}
          size="sm"
          centered
          zIndex={10000}
          overlayProps={{ opacity: 0.5, blur: 3 }}
        >
          <Stack gap="md">
            <Text>
              {confirmationModal.message}
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="subtle"
                onClick={() => {
                  if (confirmationModal.onCancel) {
                    confirmationModal.onCancel();
                  }
                  closeConfirmationModal();
                }}
              >
                {confirmationModal.cancelText}
              </Button>
              <Button
                onClick={() => {
                  if (confirmationModal.onConfirm) {
                    confirmationModal.onConfirm();
                  }
                  closeConfirmationModal();
                }}
                rightSection={<CheckCircleIcon style={{ width: '16px', height: '16px' }} />}
              >
                {confirmationModal.confirmText}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </MantineWrapper>
    );
  }

  // Legacy training module removed for database integration

  // End of training sections
  // Note: "New Training System" page removed - functionality integrated into main nurse dashboard

  // Initial Authorization Form (First Time)
  if (currentStep === 'initial-auth' && isFirstTime) {
    return (
      <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
        <UnifiedHeader />
        <Container size="sm" style={{ minHeight: '100vh', paddingTop: '2rem' }}>
          <Paper shadow="lg" p="xl" radius="md">
            <Stack align="center" gap="lg">
              <img
                src="/logo.png"
                alt="IntaStream Hub Logo"
                style={{
                  height: '72px',

                }}
              />
              <Title order={1}>IntaSchedule Login</Title>
            </Stack>

            <Stack gap="lg" mt="xl">
              <Paper p="md" withBorder>
                <Text fw={600} c="blue" mb="xs">IntaSchedule Authorization Form</Text>
                <Text size="sm" c="dimmed">
                  Please sign in with your IntaSchedule credentials to access the staff booking system.
                </Text>
              </Paper>

              <TextInput
                label="Username *"
                value={authForm.username}
                onChange={(e) => setAuthForm({ ...authForm, username: e.target.value })}
                placeholder="Enter your username"
                description="Use your IntaSchedule username"
                disabled={authForm.isAuthenticating}
              />

              <TextInput
                label="Password *"
                type="password"
                value={authForm.password}
                onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                placeholder="Enter your password"
                description="Use your IntaSchedule password"
                disabled={authForm.isAuthenticating}
              />

              <Paper p="md" withBorder>
                <Text fw={600} size="sm" mb="xs">Legal Agreement</Text>
                <Text size="xs" c="dimmed" mb="sm">
                  By logging in and using this system to book staff, you automatically agree to our:
                </Text>
                <Group gap="lg" mb="sm">
                  <Anchor
                    href="#"
                    size="xs"
                    onClick={(e) => { e.preventDefault(); showNotification('Terms and Conditions document will be displayed here', 'default', 'Coming Soon'); }}
                  >
                    📄 Terms and Conditions
                  </Anchor>
                  <Anchor
                    href="#"
                    size="xs"
                    onClick={(e) => { e.preventDefault(); showNotification('Contract document will be displayed here', 'default', 'Coming Soon'); }}
                  >
                    📋 Service Contract
                  </Anchor>
                </Group>
                <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                  This includes but is not limited to: authorized system use, confidentiality requirements,
                  healthcare compliance, booking responsibilities, and security obligations.
                </Text>
              </Paper>

              <Checkbox
                checked={authForm.termsAccepted}
                onChange={(e) => setAuthForm({ ...authForm, termsAccepted: e.currentTarget.checked })}
                disabled={authForm.isAuthenticating}
                label={
                  <Text size="sm">
                    I acknowledge that by logging in and booking staff, I accept the{' '}
                    <Anchor
                      href="#"
                      onClick={(e) => { e.preventDefault(); showNotification('Terms and Conditions document will be displayed here', 'default', 'Coming Soon'); }}
                    >
                      Terms and Conditions
                    </Anchor>
                    {' '}and{' '}
                    <Anchor
                      href="#"
                      onClick={(e) => { e.preventDefault(); showNotification('Contract document will be displayed here', 'default', 'Coming Soon'); }}
                    >
                      Service Contract
                    </Anchor>
                  </Text>
                }
              />

              <Button
                onClick={handleAuthSubmit}
                disabled={!authForm.username || !authForm.password || !authForm.termsAccepted || authForm.isAuthenticating}
                fullWidth
                size="md"
                loading={authForm.isAuthenticating}
                rightSection={authForm.isAuthenticating ? null : <ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
              >
                {authForm.isAuthenticating ? 'Signing In...' : 'Sign In to Booking System'}
              </Button>
            </Stack>
          </Paper>
        </Container>
      </MantineWrapper>
    );
  }

  // Simplified Authorization Form (Subsequent Users)
  if (currentStep === 'initial-auth' && !isFirstTime) {
    return (
      <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
        <UnifiedHeader />
        <Container size="sm" style={{ minHeight: '100vh', paddingTop: '2rem' }}>
          <Paper shadow="lg" p="xl" radius="md">
            <Stack align="center" gap="lg">
              <img
                src="/logo.png"
                alt="IntaStream Hub Logo"
                style={{
                  height: '72px',

                }}
              />
              <Title order={1}>IntaSchedule Login</Title>
            </Stack>

            <Stack gap="lg" mt="xl">
              <Paper p="md" withBorder>
                <Group gap="sm" mb="xs">
                  <CheckCircleIcon style={{ width: '20px', height: '20px', color: 'var(--mantine-color-green-filled)' }} />
                  <Text fw={600} size="sm">Legal Agreement Status</Text>
                </Group>
                <Text size="xs" c="dimmed" mb="sm">
                  Your organization has already agreed to our legal framework. By logging in, you confirm acceptance of:
                </Text>
                <Group gap="lg" mb="sm">
                  <Anchor
                    href="#"
                    size="xs"
                    onClick={(e) => { e.preventDefault(); showNotification('Terms and Conditions document will be displayed here', 'default', 'Coming Soon'); }}
                  >
                    📄 Terms and Conditions
                  </Anchor>
                  <Anchor
                    href="#"
                    size="xs"
                    onClick={(e) => { e.preventDefault(); showNotification('Contract document will be displayed here', 'default', 'Coming Soon'); }}
                  >
                    📋 Service Contract
                  </Anchor>
                </Group>
              </Paper>

              <TextInput
                label="Username *"
                value={authForm.username}
                onChange={(e) => setAuthForm({ ...authForm, username: e.target.value })}
                placeholder="Enter your username"
                description="Use your IntaSchedule username"
                disabled={authForm.isAuthenticating}
              />

              <TextInput
                label="Password *"
                type="password"
                value={authForm.password}
                onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                placeholder="Enter your password"
                description="Use your IntaSchedule password"
                disabled={authForm.isAuthenticating}
              />

              <Checkbox
                checked={authForm.termsAccepted}
                onChange={(e) => setAuthForm({ ...authForm, termsAccepted: e.currentTarget.checked })}
                disabled={authForm.isAuthenticating}
                label={
                  <Text size="sm">
                    I confirm I'm authorized to book staff on behalf of <Text span fw={600}>{hospitalName}</Text>
                  </Text>
                }
              />

              <Button
                onClick={handleAuthSubmit}
                disabled={!authForm.username || !authForm.password || !authForm.termsAccepted || authForm.isAuthenticating}
                fullWidth
                size="md"
                loading={authForm.isAuthenticating}
                rightSection={authForm.isAuthenticating ? null : <ArrowRightIcon style={{ width: '16px', height: '16px' }} />}
              >
                {authForm.isAuthenticating ? 'Signing In...' : 'Sign In to Booking System'}
              </Button>
            </Stack>
          </Paper>
        </Container>
      </MantineWrapper>
    );
  }


  // Main Scheduler Interface
  if (currentStep === 'scheduler') {
    return (
      <MantineWrapper colorScheme={colorScheme} currentUser={getCurrentUser()} selectedProviderId={selectedProviderId} currentStep={currentStep}>
        <Box style={{ minHeight: '100vh' }}>
          <UnifiedHeader />
          <SubHeader />

          {/* Admin Provider Selection */}
          {authForm.provider && authForm.provider.userType === 'ADMIN' && (
            <Paper
              p="lg"
              radius={0}
              style={{
                borderBottom: `1px solid ${colorScheme === 'dark' ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                backgroundColor: colorScheme === 'dark' ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-blue-0)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Container size="xl">
                <Group justify="center">
                  <Select
                    placeholder="Choose a hospital to view appointments"
                    data={availableProviders.map(provider => ({
                      value: provider.ProviderId.toString(),
                      label: provider.Description
                    }))}
                    value={selectedProviderId}
                    onChange={(value) => {
                      setSelectedProviderId(value);
                      if (value) {
                        loadAppointments(parseInt(value), { currentWeek });
                        console.log('Admin provider selected:', value);
                      } else {
                        setLiveAppointments([]);
                      }
                    }}
                    searchable
                    clearable
                    size="md"
                    style={{ minWidth: '400px', maxWidth: '600px', width: '100%' }}
                  />
                </Group>
              </Container>
            </Paper>
          )}

          {/* Week Navigation - Hidden on Mobile (integrated into day navigation) */}
          {!isMobile && (
            <Paper shadow="sm" p="md" radius={0}>
              <Group justify="center" gap="md">
                <Button
                  onClick={() => navigateWeek(-1)}
                  variant="light"
                  size="sm"
                  leftSection={<ChevronLeftIcon style={{ width: '16px', height: '16px' }} />}
                >
                  Previous Week
                </Button>

                <Text c="dimmed" size="lg">|</Text>

                <Button
                  onClick={() => {
                    const today = new Date();
                    today.setHours(12, 0, 0, 0);
                    setDatePickerValue(today);
                    openDatePicker();
                  }}
                  variant="light"
                  size="sm"
                  leftSection={<CalendarIcon style={{ width: '16px', height: '16px' }} />}
                >
                  Go to Date
                </Button>

                <Group gap="xs">
                  <CalendarIcon style={{ width: '20px', height: '20px', color: 'var(--mantine-color-blue-filled)' }} />
                  <Text size="lg" fw={600}>
                    {(() => {
                      const firstDate = weekDates[0];
                      const lastDate = weekDates[6];

                      if (firstDate.getFullYear() !== lastDate.getFullYear()) {
                        const firstMonth = firstDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
                        const lastMonth = lastDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
                        return `${firstMonth} to ${lastMonth}`;
                      } else {
                        return firstDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
                      }
                    })()}
                  </Text>
                </Group>

                <Button
                  onClick={() => {
                    const today = new Date();
                    today.setHours(12, 0, 0, 0);
                    setCurrentWeek(today);
                    setSelectedDate(today);
                  }}
                  variant="light"
                  size="sm"
                  leftSection={<HomeIcon style={{ width: '16px', height: '16px' }} />}
                >
                  Go to Today
                </Button>

                <Text c="dimmed" size="lg">|</Text>

                <Button
                  onClick={() => navigateWeek(1)}
                  variant="light"
                  size="sm"
                  rightSection={<ChevronRightIcon style={{ width: '16px', height: '16px' }} />}
                >
                  Next Week
                </Button>
              </Group>
            </Paper>
          )}

          {/* Mobile Week Navigation - Integrated into day navigation */}
          {isMobile && (
            <Paper shadow="sm" p="sm" radius={0}>
              <Group justify="center" gap="sm">
                <Button
                  onClick={() => {
                    const today = new Date();
                    today.setHours(12, 0, 0, 0);
                    setCurrentWeek(today);
                    setSelectedDate(today);
                  }}
                  variant="light"
                  size="xs"
                  leftSection={<HomeIcon style={{ width: '14px', height: '14px' }} />}
                  style={{ flex: 1, maxWidth: '100px' }}
                >
                  Today
                </Button>

                <Button
                  onClick={() => {
                    const today = new Date();
                    today.setHours(12, 0, 0, 0);
                    setDatePickerValue(today);
                    openDatePicker();
                  }}
                  variant="light"
                  size="xs"
                  leftSection={<CalendarIcon style={{ width: '14px', height: '14px' }} />}
                  style={{ flex: 1, maxWidth: '120px' }}
                >
                  Pick Date
                </Button>
              </Group>
            </Paper>
          )}


          {/* Week View - Desktop */}
          {!isMobile && (
            <Container size="xl" p="md">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0px' }}>
                {weekDates.map((date, index) => {
                  const dateStr = date.toISOString().split('T')[0];
                  const selectedDateStr = selectedDate && selectedDate instanceof Date ? selectedDate.toISOString().split('T')[0] : null;
                  const isSelected = selectedDateStr === dateStr;

                  return (
                    <Paper
                      key={index}
                      onClick={() => handleDateSelect(date)}
                      p="md"
                      radius={0}
                      withBorder={false}
                      bg={isSelected ? 'blue.1' : undefined}
                      style={{
                        minHeight: '200px',
                        cursor: 'pointer',
                        border: isSelected ? '2px solid var(--mantine-color-blue-6)' : 'none'
                      }}
                    >
                      <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                        <Text
                          size="sm"
                          fw={500}
                          c={isSelected ? 'blue' : 'dimmed'}
                        >
                          {formatDate(date)}
                        </Text>
                      </div>
                      <Stack gap="xs">
                        {getBookingsForDate(date).map((booking) => (
                          <Paper
                            key={booking.id}
                            onClick={() => handleEditBooking(booking)}
                            p="xs"
                            radius="sm"
                            withBorder
                            style={{
                              cursor: 'pointer'
                            }}
                          >
                            <Text size="xs" fw={500} truncate>
                              {booking.specialization}
                            </Text>
                            {booking.isLive && booking.title && (
                              <Group gap="xs">
                                <UserIcon style={{ width: '12px', height: '12px', color: 'var(--mantine-color-blue-6)' }} />
                                <Text size="xs" c="blue" fw={500} truncate>
                                  {booking.title}
                                </Text>
                              </Group>
                            )}
                            <Group gap="xs">
                              <ClockIcon style={{ width: '12px', height: '12px' }} />
                              <Text size="xs" c="dimmed">{formatTime(booking.startTime)} - {formatTime(booking.endTime)}</Text>
                              {getCrossoverIcon(booking, date)}
                            </Group>
                            <Text size="xs" c="green" fw={500}>
                              {getShiftType(booking.startTime, booking.endTime, booking.startDate, booking.endDate)}
                            </Text>
                            {booking.notes && (
                              <Text size="xs" c="blue" truncate>
                                {booking.notes}
                              </Text>
                            )}
                            {(booking.invoiceId && booking.invoiceId > 0) || (booking.timesheetId && booking.timesheetId > 0) ? (
                              <Group gap="xs" mt="xs">
                                {booking.invoiceId && booking.invoiceId > 0 && (
                                  <div
                                    style={{
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s ease, opacity 0.2s ease',
                                      display: 'inline-block'
                                    }}
                                    title="Invoice attached - Click to view"
                                    onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'scale(1.1)'}
                                    onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'scale(1)'}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      console.log('View invoice ID:', booking.invoiceId);
                                    }}
                                  >
                                    <DocumentArrowDownIcon style={{ width: '21px', height: '21px', color: 'var(--mantine-color-blue-6)' }} />
                                  </div>
                                )}
                                {booking.timesheetId && booking.timesheetId > 0 && (
                                  <div
                                    style={{
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s ease, opacity 0.2s ease',
                                      display: 'inline-block'
                                    }}
                                    title="Timesheet attached - Click to view"
                                    onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'scale(1.1)'}
                                    onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'scale(1)'}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      console.log('View timesheet ID:', booking.timesheetId);
                                    }}
                                  >
                                    <ClockIcon style={{ width: '21px', height: '21px', color: 'var(--mantine-color-green-6)' }} />
                                  </div>
                                )}
                              </Group>
                            ) : null}
                            <Group justify="space-between" mt="xs">
                              <span className={`badge ${booking.backgroundId === APPOINTMENT_STATUS.SEARCHING ? 'badge-warning' :
                                booking.backgroundId === APPOINTMENT_STATUS.CONFIRMED ? 'badge-success' :
                                  booking.backgroundId === APPOINTMENT_STATUS.HOLIDAY ? 'badge-info' :
                                    'badge-secondary'
                                }`}>
                                {booking.status || 'Unknown'}
                              </span>
                              {canDeleteShift(booking) && (
                                <ActionIcon
                                  size="xs"
                                  variant="light"
                                  color="red"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteBooking(booking);
                                  }}
                                  aria-label="Delete shift"
                                >
                                  <TrashIcon style={{ width: '12px', height: '12px' }} />
                                </ActionIcon>
                              )}
                            </Group>
                          </Paper>
                        ))}
                      </Stack>
                    </Paper>
                  );
                })}
              </div>
            </Container>
          )}

          {/* Single Day View - Mobile */}
          {(isMobile && (
            <Container size="sm" p="md">
              <Paper
                shadow="lg"
                radius="md"
                p="md"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <Group justify="space-between" align="center" mb="lg" wrap="nowrap">
                  <ActionIcon
                    onClick={() => {
                      const newDate = new Date(selectedDate);
                      newDate.setDate(selectedDate.getDate() - 1);
                      setSelectedDate(newDate);
                      setCurrentWeek(newDate);
                    }}
                    variant="light"
                    size="lg"
                    style={{ flexShrink: 0 }}
                  >
                    <ChevronLeftIcon style={{ width: '20px', height: '20px' }} />
                  </ActionIcon>

                  <Box style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                    {/* Week navigation indicators */}
                    <Group justify="center" gap="xs" mb="xs">
                      <ActionIcon
                        onClick={() => navigateWeek(-1)}
                        variant="subtle"
                        size="sm"
                        aria-label="Previous Week"
                      >
                        <ChevronLeftIcon style={{ width: '14px', height: '14px' }} />
                      </ActionIcon>
                      <Text size="xs" c="dimmed">
                        Week of {weekDates[0].toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </Text>
                      <ActionIcon
                        onClick={() => navigateWeek(1)}
                        variant="subtle"
                        size="sm"
                        aria-label="Next Week"
                      >
                        <ChevronRightIcon style={{ width: '14px', height: '14px' }} />
                      </ActionIcon>
                    </Group>

                    {/* Day display */}
                    <Text size="xl" fw={600} style={{ lineHeight: 1.2 }}>
                      {selectedDate.toLocaleDateString('en-GB', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </Text>
                    <Text size="sm" c="dimmed" mt="xs">
                      Swipe or use arrows to navigate
                    </Text>
                  </Box>

                  <ActionIcon
                    onClick={() => {
                      const newDate = new Date(selectedDate);
                      newDate.setDate(selectedDate.getDate() + 1);
                      setSelectedDate(newDate);
                      setCurrentWeek(newDate);
                    }}
                    variant="light"
                    size="lg"
                    style={{ flexShrink: 0 }}
                  >
                    <ChevronRightIcon style={{ width: '20px', height: '20px' }} />
                  </ActionIcon>
                </Group>

                <Stack gap="md" style={{ minHeight: '300px' }}>
                  {getBookingsForDate(selectedDate).length === 0 ? (
                    <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center' }}>
                      <Text c="dimmed" size="lg">No shifts scheduled for this day</Text>
                      <Button
                        onClick={handleNewBooking}
                        variant="light"
                        size="md"
                        mt="md"
                        leftSection={<PlusIcon style={{ width: '16px', height: '16px' }} />}
                      >
                        Request Booking
                      </Button>
                    </Paper>
                  ) : (
                    getBookingsForDate(selectedDate).map((booking) => (
                      <Paper
                        key={booking.id}
                        onClick={() => handleEditBooking(booking)}
                        p="lg"
                        radius="md"
                        withBorder
                        style={{ cursor: 'pointer' }}
                      >
                        <Group justify="space-between" align="flex-start">
                          <Stack gap="xs" style={{ flex: 1 }}>
                            <Text size="lg" fw={600}>
                              {booking.specialization}
                            </Text>
                            {booking.isLive && booking.title && (
                              <Group gap="xs">
                                <UserIcon style={{ width: '16px', height: '16px', color: 'var(--mantine-color-blue-6)' }} />
                                <Text size="sm" c="blue" fw={500}>
                                  {booking.title}
                                </Text>
                              </Group>
                            )}
                            <Group gap="sm">
                              <ClockIcon style={{ width: '16px', height: '16px' }} />
                              <Text size="md">{formatTime(booking.startTime)} - {formatTime(booking.endTime)}</Text>
                              {getCrossoverIcon(booking, selectedDate)}
                            </Group>
                            <Text size="sm" c="green" fw={500}>
                              {getShiftType(booking.startTime, booking.endTime, booking.startDate, booking.endDate)}
                            </Text>
                            {booking.notes && (
                              <Text size="sm" c="blue">
                                {booking.notes}
                              </Text>
                            )}
                            {(booking.invoiceId && booking.invoiceId > 0) || (booking.timesheetId && booking.timesheetId > 0) ? (
                              <Group gap="sm">
                                {booking.invoiceId && booking.invoiceId > 0 && (
                                  <div
                                    style={{
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s ease, opacity 0.2s ease',
                                      display: 'inline-block'
                                    }}
                                    title="Invoice attached - Click to view"
                                    onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'scale(1.1)'}
                                    onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'scale(1)'}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      console.log('View invoice ID:', booking.invoiceId);
                                    }}
                                  >
                                    <DocumentArrowDownIcon style={{ width: '24px', height: '24px', color: 'var(--mantine-color-blue-6)' }} />
                                  </div>
                                )}
                                {booking.timesheetId && booking.timesheetId > 0 && (
                                  <div
                                    style={{
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s ease, opacity 0.2s ease',
                                      display: 'inline-block'
                                    }}
                                    title="Timesheet attached - Click to view"
                                    onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'scale(1.1)'}
                                    onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'scale(1)'}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      console.log('View timesheet ID:', booking.timesheetId);
                                    }}
                                  >
                                    <ClockIcon style={{ width: '24px', height: '24px', color: 'var(--mantine-color-green-6)' }} />
                                  </div>
                                )}
                              </Group>
                            ) : null}
                            <Group gap="sm">
                              <span className={`badge ${booking.backgroundId === APPOINTMENT_STATUS.SEARCHING ? 'badge-warning' :
                                booking.backgroundId === APPOINTMENT_STATUS.CONFIRMED ? 'badge-success' :
                                  booking.backgroundId === APPOINTMENT_STATUS.HOLIDAY ? 'badge-info' :
                                    'badge-secondary'
                                }`}>
                                {booking.status || 'Unknown'}
                              </span>
                            </Group>
                          </Stack>
                          {canDeleteShift(booking) && (
                            <ActionIcon
                              size="lg"
                              variant="light"
                              color="red"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteBooking(booking);
                              }}
                              aria-label="Delete shift"
                            >
                              <TrashIcon style={{ width: '20px', height: '20px' }} />
                            </ActionIcon>
                          )}
                        </Group>
                      </Paper>
                    ))
                  )}
                </Stack>
              </Paper>
            </Container>
          )) as any}

          {(showTutorial && (
            <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 40, pointerEvents: 'none' }}>
              <Paper
                p="md"
                radius="md"
                shadow="lg"
                bg="blue"
                c="white"
                style={{ maxWidth: '480px', pointerEvents: 'auto' }}
              >
                <Text fw={600} mb="sm">Help & Quick Guide</Text>
                <Stack gap="xs">
                  <Text size="xs">• Click the "Request Booking" button to create new shifts.</Text>
                  <Text size="xs">• Select a date first to set it as the default for new bookings.</Text>
                  <Text size="xs">• Double-click existing shifts to edit them.</Text>
                  <Text size="xs">• Use the week navigation to view different time periods.</Text>
                  <Text size="xs">• Use "Go to Date" button for quick navigation to specific dates.</Text>
                  <Text size="xs">• Toggle between light and dark themes with the sun/moon icon.</Text>
                </Stack>
                <Button
                  onClick={() => setShowTutorial(false)}
                  variant="white"
                  color="blue"
                  size="xs"
                  mt="md"
                >
                  Got it!
                </Button>
              </Paper>
            </div>
          )) as any}

          {/* Booking Dialog */}
          <Modal
            opened={bookingOpened}
            onClose={() => {
              closeBooking();
              setEditingBooking(null);
              setHighlightSpecialization(false);
            }}
            title={editingBooking ? 'Edit IntaSchedule Booking' : 'Request IntaSchedule Booking'}
            size="lg"
            centered
          >
            <Stack gap="md" pt="lg">
              {/* Check if controls should be disabled */}
              {(() => {
                const isDisabled = editingBooking && !canEditShift(editingBooking);

                return (
                  <>
                    {isDisabled && (
                      <Text size="sm" c="orange" fw={500}>
                        This shift cannot be edited: {(() => {
                          // Check if staff is assigned (not searching status)
                          if (editingBooking.backgroundId !== APPOINTMENT_STATUS.SEARCHING) {
                            // For assigned bookings, check additional restrictions
                            const endDateTime = new Date(`${editingBooking.endDate}T${editingBooking.endTime}`);
                            const now = new Date();
                            if (endDateTime < now) {
                              return "Assigned shift has already ended";
                            }

                            if (isWithin4Hours(editingBooking.startDate, editingBooking.startTime)) {
                              return "Assigned shift starts within 4 hours";
                            }

                            return "Staff member is assigned";
                          }

                          // For searching status bookings
                          if (isWithin4Hours(editingBooking.startDate, editingBooking.startTime)) {
                            return "Start time is within 4 hours";
                          }

                          return "Unknown reason";
                        })()}
                      </Text>
                    )}
                  </>
                );
              })()}
              <div>
                <Text size="sm" fw={500} mb="xs">Role *</Text>
                <div style={{
                  transition: 'box-shadow 0.6s ease-in-out',
                  boxShadow: highlightSpecialization ? '0 0 15px var(--mantine-color-blue-light-color)' : 'none',
                  borderRadius: 'var(--mantine-radius-sm)'
                }}>
                  <Select
                    value={bookingForm.specialization}
                    onChange={(value) => setBookingForm({ ...bookingForm, specialization: value })}
                    data={medicalRoles.map(role => ({ value: role.Role, label: role.Role }))}
                    placeholder="Select role..."
                    searchable
                    maxDropdownHeight={700}
                    disabled={editingBooking && !canEditShift(editingBooking) || isLoadingMedicalRoles}
                  />
                </div>
              </div>

              {/* Staff Assignment - Dropdown for Admin, Read-only for Hospital */}
              {editingBooking && editingBooking.isLive && (
                <div>
                  <Text size="sm" fw={500} mb="xs">Assigned Staff</Text>
                  {authForm.provider && (authForm.provider.userType === 'ADMIN' || authForm.provider.id === -1) ? (
                    <Select
                      value={bookingForm.assignedStaffId}
                      onChange={(value) => setBookingForm({ ...bookingForm, assignedStaffId: value || '' })}
                      data={[
                        { value: '', label: 'No staff assigned' },
                        ...professionals.map(prof => ({
                          value: prof.professionalId.toString(),
                          label: prof.fullName
                        }))
                      ]}
                      placeholder="Select a professional..."
                      searchable
                      clearable
                      disabled={editingBooking && !canEditShift(editingBooking)}
                    />
                  ) : (
                    <TextInput
                      value={editingBooking.title || "Unknown"}
                      readOnly
                      style={{
                        input: {
                          cursor: 'not-allowed'
                        }
                      }}
                    />
                  )}
                </div>
              )}

              <Group gap="md">
                <Button
                  onClick={setDayShift}
                  variant="light"
                  size="sm"
                  flex={1}
                  leftSection={<SunIcon style={{ width: '16px', height: '16px' }} />}
                  disabled={editingBooking && !canEditShift(editingBooking)}
                >
                  Day Shift (8AM - 8PM)
                </Button>
                <Button
                  onClick={setNightShift}
                  variant="light"
                  size="sm"
                  flex={1}
                  leftSection={<MoonIcon style={{ width: '16px', height: '16px' }} />}
                  disabled={editingBooking && !canEditShift(editingBooking)}
                >
                  Night Shift (8PM - 8AM)
                </Button>
              </Group>

              <Grid>
                <Grid.Col span={6}>
                  <DateInput
                    label="Start Date *"
                    value={bookingForm.startDate ? new Date(bookingForm.startDate) : null}
                    onChange={(date) => {
                      const dateStr = date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : '';
                      setBookingForm({ ...bookingForm, startDate: dateStr });
                    }}
                    valueFormat="DD/MM/YYYY"
                    placeholder="DD/MM/YYYY"
                    disabled={editingBooking && !canEditShift(editingBooking)}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="Start Time *"
                    type="time"
                    value={bookingForm.startTime}
                    onChange={(e) => setBookingForm({ ...bookingForm, startTime: e.target.value })}
                    disabled={editingBooking && !canEditShift(editingBooking)}
                  />
                </Grid.Col>
              </Grid>

              <Grid>
                <Grid.Col span={6}>
                  <DateInput
                    label="End Date *"
                    value={bookingForm.endDate ? new Date(bookingForm.endDate) : null}
                    onChange={(date) => {
                      const dateStr = date ? (typeof date === 'string' ? date : (date as Date).toISOString().split('T')[0]) : '';
                      setBookingForm({ ...bookingForm, endDate: dateStr });
                    }}
                    valueFormat="DD/MM/YYYY"
                    placeholder="DD/MM/YYYY"
                    disabled={editingBooking && !canEditShift(editingBooking)}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="End Time *"
                    type="time"
                    value={bookingForm.endTime}
                    onChange={(e) => setBookingForm({ ...bookingForm, endTime: e.target.value })}
                    disabled={editingBooking && !canEditShift(editingBooking)}
                  />
                </Grid.Col>
              </Grid>

              <Textarea
                label="Notes"
                value={bookingForm.notes}
                onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                placeholder="Enter any specific requirements or notes..."
                rows={3}
              />

              <Group justify="flex-end" mt="md">
                <Button
                  variant="light"
                  onClick={() => {
                    closeBooking();
                    setEditingBooking(null);
                    setHighlightSpecialization(false);
                  }}
                  leftSection={<XMarkIcon style={{ width: '16px', height: '16px' }} />}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBookingSubmit}
                  rightSection={<BookmarkIcon style={{ width: '16px', height: '16px' }} />}
                  disabled={editingBooking && !canEditShift(editingBooking)}
                >
                  {editingBooking ? 'Update Booking' : 'Save Booking'}
                </Button>
              </Group>
            </Stack>
          </Modal>

          {/* Date Picker Modal */}
          <Modal
            opened={datePickerOpened}
            onClose={closeDatePicker}
            title="Go to Date"
            size="auto"
            centered
          >
            <Stack align="center" gap="md">
              <DatePicker
                value={datePickerValue}
                onChange={setDatePickerValue}
              />
              <Group gap="md" mt="md">
                <Button
                  onClick={() => {
                    if (datePickerValue) {
                      // Normalize the selected date to avoid timezone issues
                      const normalizedDate = new Date(datePickerValue);
                      normalizedDate.setHours(12, 0, 0, 0);

                      setCurrentWeek(normalizedDate);
                      setSelectedDate(normalizedDate);
                      closeDatePicker();
                    }
                  }}
                  disabled={!datePickerValue}
                >
                  Go to Selected Date
                </Button>
                <Button
                  variant="light"
                  onClick={closeDatePicker}
                >
                  Cancel
                </Button>
              </Group>
            </Stack>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal
            opened={deleteConfirmOpened}
            onClose={closeDeleteConfirm}
            title="Confirm Delete"
            size="sm"
            centered
          >
            <Stack gap="md">
              <Text>
                Are you sure you want to delete this{' '}
                <Text span fw={600} c="blue">
                  {bookingToDelete?.specialization}
                </Text>{' '}
                shift on{' '}
                <Text span fw={600}>
                  {bookingToDelete?.startDate && formatDateUK(bookingToDelete.startDate)}
                </Text>?
              </Text>
              <Text size="sm" c="red">
                This action cannot be undone.
              </Text>

              <Group justify="flex-end" gap="sm">
                <Button
                  variant="light"
                  onClick={closeDeleteConfirm}
                  leftSection={<XMarkIcon style={{ width: '16px', height: '16px' }} />}
                >
                  Cancel
                </Button>
                <Button
                  color="red"
                  onClick={confirmDelete}
                  rightSection={<TrashIcon style={{ width: '16px', height: '16px' }} />}
                >
                  Delete Shift
                </Button>
              </Group>
            </Stack>
          </Modal>

          {/* General Confirmation Modal */}
          {console.log('🔍 About to render confirmation modal section')}
          {console.log('🔍 Rendering confirmation modal, opened:', confirmationModal.opened, 'title:', confirmationModal.title)}
          <Modal
            opened={confirmationModal.opened}
            onClose={closeConfirmationModal}
            title={confirmationModal.title}
            size="sm"
            centered
            zIndex={10000}
            overlayProps={{ opacity: 0.5, blur: 3 }}
          >
            <Stack gap="md">
              <Text>
                {confirmationModal.message}
              </Text>

              <Group justify="flex-end" gap="sm">
                <Button
                  variant="light"
                  onClick={handleConfirmationCancel}
                  leftSection={<XMarkIcon style={{ width: '16px', height: '16px' }} />}
                >
                  {confirmationModal.cancelText}
                </Button>
                <Button
                  onClick={handleConfirmationConfirm}
                  rightSection={<CheckCircleIcon style={{ width: '16px', height: '16px' }} />}
                >
                  {confirmationModal.confirmText}
                </Button>
              </Group>
            </Stack>
          </Modal>
        </Box>
      </MantineWrapper>
    );
  }

  // SSE cleanup effect for React development mode stability
  useEffect(() => {
    // Cleanup function for component unmount or hot reload
    return () => {
      console.log('IntaStreamApp cleanup: disconnecting SSE');
      sseService.disconnect();
    };
  }, []);

  // SSE reconnection monitoring for development mode
  useEffect(() => {
    const user = getCurrentUser();
    if (user && user.id) {
      console.log('SSE monitoring setup for user:', user.id);

      // Check connection health periodically
      const healthCheck = setInterval(() => {
        const status = sseService.getStatus();
        console.log('SSE health check:', status);

        // Use the new forced connection check method
        const reconnectionTriggered = sseService.forceConnectionCheck();
        if (reconnectionTriggered) {
          console.log('SSE health check triggered reconnection');
        }
      }, 15000); // Check every 15 seconds to avoid overwhelming the system

      return () => {
        clearInterval(healthCheck);
      };
    }
  }, [authForm.provider, nurseAuth.authenticated]); // Depend on auth state changes

  // Fallback - should not reach here
  return null;
};

// Temporary: Direct export without ErrorBoundary to debug
export default IntaStreamApp;
